import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  Card, 
  Button, 
  Input, 
  Space, 
  Typography, 
  Row, 
  Col,
  Select,
  Form,
  Radio,
  Upload,
  Divider,
  Tag,
  Alert,
  Modal,
  message,
  Tooltip,
  Switch,
  InputNumber,
  Collapse,
  Badge,
  Checkbox,
  Dropdown,
  Menu,
  Skeleton
} from 'antd';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { 
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  LinkOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  CopyOutlined,
  EditOutlined,
  SmileOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  ThunderboltOutlined,
  FormOutlined,
  CodeOutlined,
  CloudUploadOutlined,
  DownOutlined
} from '@ant-design/icons';
import EmojiPicker from 'emoji-picker-react';
// import './TemplateBuilder.css';
import './AdvancedTemplateBuilder.css';
import './AdvancedBroadcastBuilder.css';
import { Link } from 'react-router-dom';
import axiosConfig from '../../../../config/axiosConfig';
import AnimatedList from './AnimatedList';
import TemplateWizardStepIndicator from '../../../email/templates/TemplateWizardStepIndicator';
import '../../../email/shared/emailDesign.css';

const { TextArea } = Input;
const { Option } = Select;
const { Text, Title } = Typography;
const { Panel } = Collapse;

const AI_DRAFT_TARGET_VARIANTS = 3;
const AI_DRAFT_FALLBACK_DIRECTIONS = [
  'Use a concise and direct style while preserving the user intent.',
  'Use a warmer and more conversational style while preserving the user intent.',
  'Use a polished and action-focused style while preserving the user intent.'
];

const FLOW_FIELD_TYPES = [
  'TextInput',
  'TextArea',
  'Dropdown',
  'RadioButtonsGroup',
  'CheckboxGroup',
  'DatePicker',
  'OptIn'
];

const FLOW_INPUT_TYPES = ['text', 'number', 'email', 'phone', 'password', 'passcode'];
const FLOW_CHOICE_FIELD_TYPES = ['Dropdown', 'RadioButtonsGroup', 'CheckboxGroup'];
const FLOW_TEXT_CONTENT_TYPES = ['LargeHeading', 'SmallHeading', 'Body', 'Caption'];
const FLOW_MEDIA_CONTENT_TYPES = ['Image'];
const FLOW_ANSWER_CONTENT_TYPES = [...FLOW_FIELD_TYPES];
const FLOW_CONTENT_TYPES = [...FLOW_TEXT_CONTENT_TYPES, ...FLOW_MEDIA_CONTENT_TYPES, ...FLOW_ANSWER_CONTENT_TYPES];
const FLOW_FIELD_TYPE_LABELS = {
  TextInput: 'Short answer',
  TextArea: 'Paragraph',
  Dropdown: 'Dropdown',
  RadioButtonsGroup: 'Single choice',
  CheckboxGroup: 'Multiple choice',
  DatePicker: 'Date picker',
  OptIn: 'Opt-in'
};
const FLOW_TEXT_TYPE_LABELS = {
  LargeHeading: 'Large heading',
  SmallHeading: 'Small heading',
  Body: 'Body',
  Caption: 'Caption',
};
const FLOW_MEDIA_TYPE_LABELS = {
  Image: 'Image',
};
const FLOW_INPUT_TYPE_LABELS = {
  text: 'Text',
  number: 'Number',
  email: 'Email',
  phone: 'Phone',
  password: 'Password',
  passcode: 'Passcode'
};
const FLOW_CATEGORIES = [
  'SIGN_UP',
  'SIGN_IN',
  'APPOINTMENT_BOOKING',
  'LEAD_GENERATION',
  'CONTACT_US',
  'CUSTOMER_SUPPORT',
  'SURVEY',
  'OTHER'
];

const FLOW_CONTENT_VALIDATION = {
  LargeHeading: { textMax: 80 },
  SmallHeading: { textMax: 80 },
  Body: { textMax: 4096 },
  Caption: { textMax: 300 },
  Image: { urlMax: 2048 },
  TextInput: { labelMax: 80 },
  TextArea: { labelMax: 80 },
  DatePicker: { labelMax: 80 },
  RadioButtonsGroup: { labelMax: 80, optionMax: 30, maxOptions: 20 },
  CheckboxGroup: { labelMax: 80, optionMax: 30, maxOptions: 20 },
  Dropdown: { labelMax: 80, optionMax: 30, maxOptions: 20 },
  OptIn: { labelMax: 120 },
};

const FLOW_ADD_CONTENT_GROUPS = [
  {
    key: 'text',
    label: 'Text',
    items: [
      { type: 'LargeHeading', label: 'Large heading' },
      { type: 'SmallHeading', label: 'Small heading' },
      { type: 'Body', label: 'Body' },
      { type: 'Caption', label: 'Caption' },
    ],
  },
  {
    key: 'media',
    label: 'Media',
    items: [{ type: 'Image', label: 'Image' }],
  },
  {
    key: 'text-answer',
    label: 'Text Answer',
    items: [
      { type: 'TextInput', label: 'Short answer' },
      { type: 'TextArea', label: 'Paragraph' },
      { type: 'DatePicker', label: 'Date picker' },
    ],
  },
  {
    key: 'selection',
    label: 'Selection',
    items: [
      { type: 'RadioButtonsGroup', label: 'Single choice' },
      { type: 'CheckboxGroup', label: 'Multiple choice' },
      { type: 'Dropdown', label: 'Dropdown' },
      { type: 'OptIn', label: 'Opt-in' },
    ],
  },
];

const FLOW_ADD_CONTENT_MENU_ITEMS = FLOW_ADD_CONTENT_GROUPS.map(group => ({
  key: group.key,
  label: group.label,
  children: group.items.map(item => ({ key: item.type, label: item.label })),
}));

const makeFlowContentId = () => `flow_content_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const FALLBACK_FLOW_PRESETS = [
  {
    id: 'send_survey',
    title: 'Send a survey',
    description: 'Ask questions and collect preferences.',
    category: 'SURVEY',
    flowName: 'customer_survey_flow',
    screenId: 'SURVEY',
    screenTitle: 'Quick survey',
    bodyText: 'Please answer a few quick questions so we can serve you better.',
    buttonText: 'Start survey',
    submitLabel: 'Submit',
    fields: [
      {
        type: 'RadioButtonsGroup',
        name: 'satisfaction',
        label: 'How satisfied are you?',
        required: true,
        options: ['Very satisfied', 'Satisfied', 'Neutral', 'Not satisfied']
      },
      {
        type: 'TextArea',
        name: 'feedback',
        label: 'Tell us more',
        required: false
      }
    ]
  },
  {
    id: 'register_event',
    title: 'Register for an event',
    description: 'Collect attendee details and preferences.',
    category: 'LEAD_GENERATION',
    flowName: 'event_registration_flow',
    screenId: 'EVENT_REGISTRATION',
    screenTitle: 'Event registration',
    buttonText: 'Register',
    submitLabel: 'Register',
    fields: [
      { type: 'TextInput', inputType: 'text', name: 'full_name', label: 'Full name', required: true },
      { type: 'TextInput', inputType: 'email', name: 'email', label: 'Email address', required: true },
      { type: 'Dropdown', name: 'attendees', label: 'Number of attendees', required: true, options: ['1', '2', '3', '4+'] }
    ]
  },
  {
    id: 'complete_signup',
    title: 'Complete sign up',
    description: 'Finish onboarding with a compact form.',
    category: 'SIGN_UP',
    flowName: 'signup_completion_flow',
    screenId: 'SIGN_UP',
    screenTitle: 'Complete sign up',
    buttonText: 'Sign up',
    submitLabel: 'Submit',
    fields: [
      { type: 'TextInput', inputType: 'text', name: 'full_name', label: 'Full name', required: true },
      { type: 'TextInput', inputType: 'phone', name: 'phone', label: 'Phone number', required: true },
      { type: 'OptIn', name: 'consent', label: 'I agree to receive updates', required: true }
    ]
  },
  {
    id: 'custom_form',
    title: 'Custom form',
    description: 'Build a Flow from your own fields.',
    category: 'OTHER',
    flowName: 'custom_whatsapp_flow',
    screenId: 'CUSTOM_FORM',
    screenTitle: 'Custom form',
    buttonText: 'Open form',
    submitLabel: 'Submit',
    fields: [
      { type: 'TextInput', inputType: 'text', name: 'full_name', label: 'Full name', required: true }
    ]
  }
];


const AdvancedTemplateBuilder = ({ onBack, onSave }) => {
  const [form] = Form.useForm();
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [buttonUrlErrors, setButtonUrlErrors] = useState({});
  const touchedFieldsRef = useRef({});
  const bodyTextareaRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const bodyContentInEditor = useRef('');
  
  // New step-by-step flow states
  const [currentScreen, setCurrentScreen] = useState('category'); // 'category', 'variableType', 'header', 'body', 'footer'
  const [selectedVariableType, setSelectedVariableType] = useState('');
  
  // Step-based flow for authentication templates
  const [currentStep, setCurrentStep] = useState(0); // 0: Template Info, 1: Build Template, 2: Review & Submit
  const [useStepFlow, setUseStepFlow] = useState(false); // Flag to determine which flow to use

  // Address Book related states
  const [addressBooks, setAddressBooks] = useState([]);
  const [addressBookLoading, setAddressBookLoading] = useState(false);
  const [selectedAddressBookUsers, setSelectedAddressBookUsers] = useState([]);
  const [addressBookColumns, setAddressBookColumns] = useState([]);
  const [currentVariableSection, setCurrentVariableSection] = useState('body');
  
  // Emoji picker states
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerTarget, setEmojiPickerTarget] = useState(null); // 'header', 'body', 'footer'
  const [emojiPickerAnchorEl, setEmojiPickerAnchorEl] = useState(null);
  const [emojiPickerPosition, setEmojiPickerPosition] = useState({ top: 0, left: 0 });
  const emojiPickerRef = useRef(null);
  const [bodyToolbarState, setBodyToolbarState] = useState({
    bold: false,
    italic: false,
    strike: false,
    code: false,
    bullet: false,
    ordered: false,
  });
  const [aiCopyModalOpen, setAiCopyModalOpen] = useState(false);
  const [aiCopyTarget, setAiCopyTarget] = useState('body'); // header, body, both
  const [aiCopyPrompt, setAiCopyPrompt] = useState('');
  const [aiCopyTone, setAiCopyTone] = useState('friendly');
  const [aiCopyAudience, setAiCopyAudience] = useState('');
  const [aiCopyVariantsCount, setAiCopyVariantsCount] = useState(2);
  const [aiCopyVariableType, setAiCopyVariableType] = useState('number');
  const [aiCopyVariableCount, setAiCopyVariableCount] = useState(2);
  const [aiCopyNamedVariables, setAiCopyNamedVariables] = useState([]);
  const [aiCopyLoading, setAiCopyLoading] = useState(false);
  const [aiCopyResult, setAiCopyResult] = useState(null);
  const [aiDraftPrompt, setAiDraftPrompt] = useState('');
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiDraftResult, setAiDraftResult] = useState(null);
  const [aiDraftError, setAiDraftError] = useState('');
  const [aiDraftSelectedVariantIndex, setAiDraftSelectedVariantIndex] = useState(0);
  const [flowPresets, setFlowPresets] = useState([]);
  const [acceptedFlowFieldTypes, setAcceptedFlowFieldTypes] = useState(FLOW_FIELD_TYPES);
  const [flowPresetLoading, setFlowPresetLoading] = useState(false);
  const [flowDraft, setFlowDraft] = useState(null);
  const [flowPreviewResult, setFlowPreviewResult] = useState(null);
  const [flowCreateResult, setFlowCreateResult] = useState(null);
  const [flowErrors, setFlowErrors] = useState([]);
  const [flowApiFieldErrors, setFlowApiFieldErrors] = useState({});
  const [flowPreviewLoading, setFlowPreviewLoading] = useState(false);
  const [flowCreateLoading, setFlowCreateLoading] = useState(false);
  const [flowPresetsLoaded, setFlowPresetsLoaded] = useState(false);
  const [isFlowPreviewOpen, setIsFlowPreviewOpen] = useState(false);
  const [aiFlowModalOpen, setAiFlowModalOpen] = useState(false);
  const [aiFlowPrompt, setAiFlowPrompt] = useState('');
  const [aiFlowLoading, setAiFlowLoading] = useState(false);
  const [aiFlowError, setAiFlowError] = useState('');

  // Category button animation state
  const [activeButton, setActiveButton] = useState(null); // Track which button is being pressed

  const getFlowContentLabel = (type) => FLOW_TEXT_TYPE_LABELS[type] || FLOW_MEDIA_TYPE_LABELS[type] || FLOW_FIELD_TYPE_LABELS[type] || type;

  const normalizeFlowField = (field = {}) => {
    const type = FLOW_FIELD_TYPES.includes(field.type) ? field.type : 'TextInput';
    const normalizedField = {
      type,
      name: field.name || '',
      label: field.label || '',
      required: !!field.required
    };

    if (type === 'TextInput') {
      normalizedField.inputType = FLOW_INPUT_TYPES.includes(field.inputType) ? field.inputType : 'text';
    }

    if (FLOW_CHOICE_FIELD_TYPES.includes(type)) {
      const options = Array.isArray(field.options) && field.options.length > 0 ? field.options : ['Option 1'];
      normalizedField.options = options.map(option => {
        if (typeof option === 'string') return option;
        return option?.title || option?.label || option?.value || option?.id || '';
      });
    }

    return normalizedField;
  };

  const normalizeFlowContentItem = (item = {}, index = 0) => {
    const type = FLOW_CONTENT_TYPES.includes(item.type) ? item.type : 'TextInput';
    const base = {
      ...item,
      id: item.id || makeFlowContentId(),
      type,
    };

    if (FLOW_TEXT_CONTENT_TYPES.includes(type)) {
      return {
        ...base,
        text: item.text ?? item.content ?? (type === 'Body' ? item.bodyText : '') ?? '',
      };
    }

    if (type === 'Image') {
      return {
        ...base,
        imageUrl: item.imageUrl || item.url || item.mediaUrl || '',
        altText: item.altText || '',
      };
    }

    return {
      ...base,
      ...normalizeFlowField({
        ...item,
        name: item.name || buildFlowFieldNameFromLabel(item.label || getFlowContentLabel(type), index),
      }),
    };
  };

  const getFlowContentItems = (draft = flowDraft) => {
    if (Array.isArray(draft?.contentItems) && draft.contentItems.length > 0) {
      return draft.contentItems.map(normalizeFlowContentItem);
    }

    const items = [];
    if (draft?.bodyText) {
      items.push(normalizeFlowContentItem({ type: 'Body', text: draft.bodyText }));
    }
    (Array.isArray(draft?.fields) ? draft.fields : []).forEach((field, index) => {
      items.push(normalizeFlowContentItem(field, index));
    });
    return items.length > 0 ? items : [normalizeFlowContentItem({ type: 'TextInput', label: 'Full name', required: true, name: 'full_name' })];
  };

  const getFlowAnswerFields = (draft = flowDraft) => getFlowContentItems(draft)
    .filter(item => FLOW_ANSWER_CONTENT_TYPES.includes(item.type))
    .map(({ id, imageUrl, altText, text, content, mediaFile, ...field }) => normalizeFlowField(field));

  const prepareFlowDraftPayload = (draft = flowDraft) => {
    const baseDraft = draft || buildFlowDraftFromPreset(FALLBACK_FLOW_PRESETS[0]);
    const contentItems = getFlowContentItems(baseDraft).map(({ mediaFile, ...item }) => item);
    const fields = getFlowAnswerFields({ ...baseDraft, contentItems });
    const firstBody = contentItems.find(item => item.type === 'Body' && item.text);

    return {
      ...baseDraft,
      bodyText: firstBody?.text || baseDraft.bodyText || '',
      contentItems,
      fields,
    };
  };

  const makeUniqueFlowName = (name = 'custom_whatsapp_flow') => {
    const baseName = String(name || 'custom_whatsapp_flow')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_\s-]/g, '')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '') || 'custom_whatsapp_flow';
    const suffix = Date.now().toString(36).slice(-6);
    const trimmedBase = baseName.slice(0, Math.max(1, 199 - suffix.length));
    return `${trimmedBase}_${suffix}`;
  };

  const buildFlowDraftFromPreset = (preset = {}) => ({
    preset: preset.id || 'custom_form',
    flowName: makeUniqueFlowName(preset.flowName || 'custom_whatsapp_flow'),
    category: preset.category || 'OTHER',
    screenId: preset.screenId || 'CUSTOM_FORM',
    screenTitle: preset.screenTitle || preset.title || 'Custom form',
    bodyText: preset.bodyText || '',
    buttonText: preset.buttonText || 'Open form',
    submitLabel: preset.submitLabel || 'Submit',
    publish: preset.publish !== false,
    endpointUri: preset.endpointUri || '',
    contentItems: [
      ...(preset.bodyText ? [normalizeFlowContentItem({ type: 'Body', text: preset.bodyText })] : []),
      ...(Array.isArray(preset.fields) && preset.fields.length > 0
        ? preset.fields.map(normalizeFlowContentItem)
        : [normalizeFlowContentItem({ type: 'TextInput', name: 'full_name', label: 'Full name', required: true })])
    ],
    fields: Array.isArray(preset.fields) && preset.fields.length > 0
      ? preset.fields.map(normalizeFlowField)
      : [normalizeFlowField({ name: 'full_name', label: 'Full name', required: true })]
  });

  const getFlowEntityFromResponse = (response) => response?.data?.entity || response?.data?.data || response?.data || {};

  const getFlowErrorMessages = (error) => {
    const data = error?.response?.data || error;
    const candidates = [
      data?.error?.errors,
      data?.error?.error?.error_user_msg,
      data?.error?.error?.error_user_title,
      data?.error?.error?.message,
      data?.error?.error_user_msg,
      data?.error?.error_user_title,
      data?.error?.message,
      data?.message,
      error?.message
    ];
    const messages = candidates.flatMap(candidate => {
      if (!candidate) return [];
      if (Array.isArray(candidate)) return candidate.map(item => String(item));
      if (typeof candidate === 'object') return [formatFlowObjectMessage(candidate)];
      return [String(candidate)];
    }).filter(Boolean);

    return messages.length > 0 ? messages : ['WhatsApp Flow request failed'];
  };

  const formatFlowObjectMessage = (value) => {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  };

  const getFlowFieldLabel = (type) => FLOW_FIELD_TYPE_LABELS[type] || type;

  const getFlowInputLabel = (type) => FLOW_INPUT_TYPE_LABELS[type] || type;

  function buildFlowFieldNameFromLabel(label, fieldIndex) {
    const fallback = `field_${fieldIndex + 1}`;
    const normalized = String(label || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_\s-]/g, '')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    const baseName = normalized || fallback;
    const uniqueName = `${baseName}_${fieldIndex + 1}`;
    return uniqueName.slice(0, 50).replace(/_+$/g, '') || fallback;
  }

  const setNestedFlowFieldError = (errors, fieldIndex, key, messageText) => {
    errors.fields = errors.fields || {};
    errors.fields[fieldIndex] = { ...(errors.fields[fieldIndex] || {}), [key]: messageText };
  };

  const setNestedFlowOptionError = (errors, fieldIndex, optionIndex, messageText) => {
    errors.fields = errors.fields || {};
    errors.fields[fieldIndex] = {
      ...(errors.fields[fieldIndex] || {}),
      options: {
        ...((errors.fields[fieldIndex] || {}).options || {}),
        [optionIndex]: messageText
      }
    };
  };

  const getFlowLocalFieldErrors = (draft = flowDraft) => {
    const errors = { fields: {} };
    const normalizedName = String(draft?.flowName || '').trim();
    const screenId = String(draft?.screenId || '').trim();
    const buttonText = String(draft?.buttonText || '').trim();
    const submitLabel = String(draft?.submitLabel || '').trim();
    const contentItems = getFlowContentItems(draft);

    if (!['send_survey', 'register_event', 'complete_signup', 'custom_form'].includes(draft?.preset)) {
      errors.preset = 'Select a valid Flow preset.';
    }
    if (draft?.category && !FLOW_CATEGORIES.includes(draft.category)) {
      errors.category = 'Select a valid Flow category.';
    }
    if (!normalizedName) errors.flowName = 'Flow name is required.';
    else if (normalizedName.length > 200) errors.flowName = 'Flow name must be 200 characters or fewer.';
    if (!/^[A-Z][A-Z0-9_]{0,49}$/.test(screenId)) {
      errors.screenId = 'Screen ID must start with a letter and use uppercase letters, numbers, or underscores only.';
    }
    if (!buttonText) errors.buttonText = 'Flow button text is required.';
    else if (buttonText.length > 25) errors.buttonText = 'Flow button text must be 25 characters or fewer.';
    if (!submitLabel) errors.submitLabel = 'Submit label is required.';
    else if (submitLabel.length > 35) errors.submitLabel = 'Submit label must be 35 characters or fewer.';
    if (contentItems.length === 0) errors.fieldsRoot = 'Add at least one Flow content block.';

    contentItems.forEach((item, index) => {
      const config = FLOW_CONTENT_VALIDATION[item.type] || {};

      if (!FLOW_CONTENT_TYPES.includes(item?.type)) {
        setNestedFlowFieldError(errors, index, 'type', 'Choose a valid content type.');
        return;
      }

      if (FLOW_TEXT_CONTENT_TYPES.includes(item.type)) {
        const text = String(item.text || '').trim();
        if (!text) {
          setNestedFlowFieldError(errors, index, 'text', `${getFlowContentLabel(item.type)} text is required.`);
        } else if (text.length > config.textMax) {
          setNestedFlowFieldError(errors, index, 'text', `${getFlowContentLabel(item.type)} must be ${config.textMax} characters or fewer.`);
        }
        return;
      }

      if (item.type === 'Image') {
        const imageUrl = String(item.imageUrl || '').trim();
        if (!imageUrl) {
          setNestedFlowFieldError(errors, index, 'imageUrl', 'Image URL is required.');
        } else if (imageUrl.length > config.urlMax) {
          setNestedFlowFieldError(errors, index, 'imageUrl', `Image URL must be ${config.urlMax} characters or fewer.`);
        }
        if (imageUrl && !/^https?:\/\//i.test(imageUrl)) {
          setNestedFlowFieldError(errors, index, 'imageUrl', 'Image URL must start with http:// or https://.');
        }
        return;
      }

      const name = String(item?.name || '').trim();
      const label = String(item?.label || '').trim();

      if (!FLOW_FIELD_TYPES.includes(item?.type)) {
        setNestedFlowFieldError(errors, index, 'type', 'Choose a valid answer type.');
      }
      if (!/^[A-Za-z_][A-Za-z0-9_]{0,49}$/.test(name)) {
        setNestedFlowFieldError(errors, index, 'name', 'Field name must start with a letter or underscore and use only letters, numbers, and underscores.');
      }
      if (!label) {
        setNestedFlowFieldError(errors, index, 'label', 'Question text is required.');
      } else if (label.length > config.labelMax) {
        setNestedFlowFieldError(errors, index, 'label', `Question text must be ${config.labelMax} characters or fewer.`);
      }
      if (item?.type === 'TextInput' && item.inputType && !FLOW_INPUT_TYPES.includes(item.inputType)) {
        setNestedFlowFieldError(errors, index, 'inputType', 'Choose a valid text format.');
      }
      if (FLOW_CHOICE_FIELD_TYPES.includes(item?.type)) {
        const options = Array.isArray(item.options) ? item.options : [];
        if (options.length === 0) {
          setNestedFlowFieldError(errors, index, 'optionsRoot', 'Add at least one answer choice.');
        }
        if (options.length > config.maxOptions) {
          setNestedFlowFieldError(errors, index, 'optionsRoot', `Answer choices are limited to ${config.maxOptions}.`);
        }
        options.forEach((option, optionIndex) => {
          const optionTitle = typeof option === 'string'
            ? option.trim()
            : String(option?.title || option?.label || option?.value || option?.id || '').trim();
          if (!optionTitle) {
            setNestedFlowOptionError(errors, index, optionIndex, 'Option text is required.');
          } else if (optionTitle.length > config.optionMax) {
            setNestedFlowOptionError(errors, index, optionIndex, `Option text must be ${config.optionMax} characters or fewer.`);
          }
        });
      }
    });

    return errors;
  };

  const getFlowFieldErrorMessages = (fieldErrors = {}) => {
    const messages = [];
    ['preset', 'category', 'flowName', 'screenId', 'buttonText', 'submitLabel', 'fieldsRoot'].forEach(key => {
      if (fieldErrors[key]) messages.push(fieldErrors[key]);
    });
    Object.entries(fieldErrors.fields || {}).forEach(([fieldIndex, errors]) => {
      Object.entries(errors || {}).forEach(([key, value]) => {
        if (key === 'options') {
          Object.entries(value || {}).forEach(([optionIndex, optionError]) => {
            if (optionError) messages.push(`Content ${Number(fieldIndex) + 1}, option ${Number(optionIndex) + 1}: ${optionError}`);
          });
          return;
        }
        if (value) messages.push(`Content ${Number(fieldIndex) + 1}: ${value}`);
      });
    });
    return [...new Set(messages)];
  };

  const mergeFlowFieldErrors = (localErrors = {}, apiErrors = {}) => {
    const mergedFields = { ...(localErrors.fields || {}) };
    Object.entries(apiErrors.fields || {}).forEach(([fieldIndex, fieldErrors]) => {
      mergedFields[fieldIndex] = {
        ...(mergedFields[fieldIndex] || {}),
        ...fieldErrors,
        options: {
          ...((mergedFields[fieldIndex] || {}).options || {}),
          ...((fieldErrors || {}).options || {})
        }
      };
    });
    return { ...localErrors, ...apiErrors, fields: mergedFields };
  };

  const getFlowApiFieldErrors = (error) => {
    const data = error?.response?.data || error || {};
    const metaError = data?.error?.error || data?.error || {};
    const messageText = metaError?.error_user_msg || metaError?.message || data?.message || error?.message || 'WhatsApp Flow request failed';
    const title = metaError?.error_user_title || '';
    const combined = `${title} ${messageText}`.toLowerCase();

    if (metaError?.error_subcode === 4016019 || combined.includes('flow name') || combined.includes('name is not unique')) {
      return { flowName: messageText };
    }
    if (combined.includes('screen')) return { screenId: messageText };
    if (combined.includes('button')) return { buttonText: messageText };
    if (combined.includes('category')) return { category: messageText };
    return {};
  };

  const isFlowNameUniqueError = (messageText) => String(messageText || '').toLowerCase().includes('unique');

  const validateFlowDraft = (draft = flowDraft) => {
    return getFlowFieldErrorMessages(getFlowLocalFieldErrors(draft));
  };

  const setFlowButtonText = (buttonText) => {
    setTemplateData(prev => ({
      ...prev,
      components: {
        ...prev.components,
        buttons: prev.components.buttons.map(button => (
          button.type === 'flow' ? { ...button, text: buttonText } : button
        ))
      }
    }));
  };

  const applyFlowPreset = (preset) => {
    const nextDraft = buildFlowDraftFromPreset(preset);
    setFlowDraft(nextDraft);
    setFlowPreviewResult(null);
    setFlowCreateResult(null);
    setFlowErrors([]);
    setFlowApiFieldErrors({});
    setFlowButtonText(nextDraft.buttonText);
  };

  const fetchFlowPresets = async () => {
    if (flowPresetLoading) return;
    try {
      setFlowPresetLoading(true);
      const response = await axiosConfig.get('/template/flow-presets');
      const entity = getFlowEntityFromResponse(response);
      const presets = Array.isArray(entity?.presets) && entity.presets.length > 0
        ? entity.presets
        : FALLBACK_FLOW_PRESETS;

      setFlowPresets(presets);
      setAcceptedFlowFieldTypes(
        Array.isArray(entity?.acceptedFieldTypes) && entity.acceptedFieldTypes.length > 0
          ? entity.acceptedFieldTypes
          : FLOW_FIELD_TYPES
      );
      setFlowPresetsLoaded(true);

      if (!flowDraft) {
        applyFlowPreset(presets[0]);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp Flow presets:', error);
      setFlowPresets(FALLBACK_FLOW_PRESETS);
      setAcceptedFlowFieldTypes(FLOW_FIELD_TYPES);
      setFlowPresetsLoaded(true);
      if (!flowDraft) {
        applyFlowPreset(FALLBACK_FLOW_PRESETS[0]);
      }
      message.warning('Flow presets could not be loaded from the server. Default presets are available for editing.');
    } finally {
      setFlowPresetLoading(false);
    }
  };

  const updateFlowDraft = (patch) => {
    if (Object.prototype.hasOwnProperty.call(patch, 'buttonText')) {
      setFlowButtonText(patch.buttonText);
    }
    setFlowDraft(prev => {
      const nextDraft = { ...(prev || buildFlowDraftFromPreset(FALLBACK_FLOW_PRESETS[0])), ...patch };
      if (patch.contentItems) {
        nextDraft.contentItems = patch.contentItems.map(normalizeFlowContentItem);
        nextDraft.fields = getFlowAnswerFields(nextDraft);
      }
      setFlowPreviewResult(null);
      setFlowCreateResult(null);
      setFlowErrors([]);
      setFlowApiFieldErrors({});
      return nextDraft;
    });
  };

  const updateFlowField = (fieldIndex, patch) => {
    setFlowDraft(prev => {
      const baseDraft = prev || buildFlowDraftFromPreset(FALLBACK_FLOW_PRESETS[0]);
      const contentItems = getFlowContentItems(baseDraft).map((field, index) => {
        if (index !== fieldIndex) return field;
        const nextField = { ...field, ...patch };
        if (patch.type) {
          if (patch.type === 'TextInput') {
            nextField.inputType = nextField.inputType || 'text';
          } else {
            delete nextField.inputType;
          }
          if (FLOW_CHOICE_FIELD_TYPES.includes(patch.type)) {
            nextField.options = Array.isArray(nextField.options) && nextField.options.length > 0 ? nextField.options : ['Option 1'];
          } else {
            delete nextField.options;
          }
        }
        return normalizeFlowContentItem(nextField, index);
      });
      setFlowPreviewResult(null);
      setFlowCreateResult(null);
      setFlowErrors([]);
      setFlowApiFieldErrors({});
      return { ...baseDraft, contentItems, fields: getFlowAnswerFields({ ...baseDraft, contentItems }) };
    });
  };

  const addFlowField = (type = 'TextInput') => {
    setFlowDraft(prev => {
      const baseDraft = prev || buildFlowDraftFromPreset(FALLBACK_FLOW_PRESETS[0]);
      const contentItems = getFlowContentItems(baseDraft);
      const nextIndex = contentItems.length + 1;
      const newItem = normalizeFlowContentItem({
        type,
        inputType: type === 'TextInput' ? 'text' : undefined,
        name: FLOW_ANSWER_CONTENT_TYPES.includes(type) ? `field_${nextIndex}` : undefined,
        label: FLOW_ANSWER_CONTENT_TYPES.includes(type) ? (type === 'OptIn' ? 'I agree to the terms' : 'Label') : undefined,
        text: FLOW_TEXT_CONTENT_TYPES.includes(type) ? getFlowContentLabel(type) : undefined,
        required: FLOW_ANSWER_CONTENT_TYPES.includes(type),
        options: FLOW_CHOICE_FIELD_TYPES.includes(type) ? ['Option 1', 'Option 2'] : undefined,
      }, nextIndex);
      const nextItems = [...contentItems, newItem];
      return {
        ...baseDraft,
        contentItems: nextItems,
        fields: getFlowAnswerFields({ ...baseDraft, contentItems: nextItems }),
      };
    });
    setFlowPreviewResult(null);
    setFlowCreateResult(null);
    setFlowErrors([]);
    setFlowApiFieldErrors({});
  };

  const removeFlowField = (fieldIndex) => {
    setFlowDraft(prev => {
      const baseDraft = prev || buildFlowDraftFromPreset(FALLBACK_FLOW_PRESETS[0]);
      const contentItems = getFlowContentItems(baseDraft).filter((_, index) => index !== fieldIndex);
      return {
        ...baseDraft,
        contentItems,
        fields: getFlowAnswerFields({ ...baseDraft, contentItems }),
      };
    });
    setFlowPreviewResult(null);
    setFlowCreateResult(null);
    setFlowErrors([]);
    setFlowApiFieldErrors({});
  };

  const updateFlowOption = (fieldIndex, optionIndex, value) => {
    setFlowDraft(prev => {
      const baseDraft = prev || buildFlowDraftFromPreset(FALLBACK_FLOW_PRESETS[0]);
      const contentItems = getFlowContentItems(baseDraft).map((field, index) => {
        if (index !== fieldIndex) return field;
        const options = [...(field.options || [])];
        options[optionIndex] = value;
        return { ...field, options };
      });
      return { ...baseDraft, contentItems, fields: getFlowAnswerFields({ ...baseDraft, contentItems }) };
    });
    setFlowPreviewResult(null);
    setFlowCreateResult(null);
    setFlowErrors([]);
    setFlowApiFieldErrors({});
  };

  const addFlowOption = (fieldIndex) => {
    setFlowDraft(prev => {
      const baseDraft = prev || buildFlowDraftFromPreset(FALLBACK_FLOW_PRESETS[0]);
      const contentItems = getFlowContentItems(baseDraft).map((field, index) => {
        if (index !== fieldIndex) return field;
        const options = [...(field.options || []), `Option ${(field.options || []).length + 1}`];
        return { ...field, options };
      });
      return { ...baseDraft, contentItems, fields: getFlowAnswerFields({ ...baseDraft, contentItems }) };
    });
    setFlowPreviewResult(null);
    setFlowCreateResult(null);
    setFlowErrors([]);
    setFlowApiFieldErrors({});
  };

  const removeFlowOption = (fieldIndex, optionIndex) => {
    setFlowDraft(prev => {
      const baseDraft = prev || buildFlowDraftFromPreset(FALLBACK_FLOW_PRESETS[0]);
      const contentItems = getFlowContentItems(baseDraft).map((field, index) => {
        if (index !== fieldIndex) return field;
        return { ...field, options: (field.options || []).filter((_, optionIdx) => optionIdx !== optionIndex) };
      });
      return { ...baseDraft, contentItems, fields: getFlowAnswerFields({ ...baseDraft, contentItems }) };
    });
    setFlowPreviewResult(null);
    setFlowCreateResult(null);
    setFlowErrors([]);
  };

  const previewWhatsAppFlow = async () => {
    const payload = prepareFlowDraftPayload(flowDraft);
    const localFieldErrors = getFlowLocalFieldErrors(payload);
    const errors = getFlowFieldErrorMessages(localFieldErrors);
    if (errors.length > 0) {
      setFlowApiFieldErrors(localFieldErrors);
      setFlowErrors(errors);
      return;
    }

    try {
      setFlowPreviewLoading(true);
      setFlowErrors([]);
      setFlowApiFieldErrors({});
      const response = await axiosConfig.post('/template/flow-preview', payload);
      const entity = getFlowEntityFromResponse(response);

      if (response.data?.status === false || response.data?.statuscode >= 400) {
        throw response.data;
      }

      setFlowPreviewResult(entity);
      setFlowCreateResult(null);
      message.success('Flow preview generated. Review it before creating the Meta Flow.');
    } catch (error) {
      console.error('Error previewing WhatsApp Flow:', error);
      setFlowApiFieldErrors(getFlowApiFieldErrors(error));
      setFlowErrors(getFlowErrorMessages(error));
    } finally {
      setFlowPreviewLoading(false);
    }
  };

  const createWhatsAppFlow = async () => {
    const payload = prepareFlowDraftPayload(flowDraft);
    const localFieldErrors = getFlowLocalFieldErrors(payload);
    const errors = getFlowFieldErrorMessages(localFieldErrors);
    if (errors.length > 0) {
      setFlowApiFieldErrors(localFieldErrors);
      setFlowErrors(errors);
      return;
    }

    try {
      setFlowCreateLoading(true);
      setFlowErrors([]);
      setFlowApiFieldErrors({});

      if (!flowPreviewResult) {
        const previewResponse = await axiosConfig.post('/template/flow-preview', payload);
        const previewEntity = getFlowEntityFromResponse(previewResponse);

        if (previewResponse.data?.status === false || previewResponse.data?.statuscode >= 400) {
          throw previewResponse.data;
        }

        setFlowPreviewResult(previewEntity);
      }

      const response = await axiosConfig.post('/template/flows/create', payload);
      const entity = getFlowEntityFromResponse(response);

      if (response.data?.status === false || response.data?.statuscode >= 400) {
        throw response.data;
      }

      const validationErrors = entity?.flow?.validation_errors || [];
      if (validationErrors.length > 0 || entity?.flow?.ready_for_template === false) {
        setFlowCreateResult(entity);
        setFlowErrors(validationErrors.length > 0 ? validationErrors : ['Meta did not mark this Flow as ready for template creation.']);
        return;
      }

      setFlowCreateResult(entity);
      message.success('Flow created and attached to this template.');
    } catch (error) {
      console.error('Error creating WhatsApp Flow:', error);
      setFlowApiFieldErrors(getFlowApiFieldErrors(error));
      setFlowErrors(getFlowErrorMessages(error));
    } finally {
      setFlowCreateLoading(false);
    }
  };

  // Apply an AI-generated flow draft into the flow editor state.
  // Unlike applyFlowPreset, this preserves the exact flowName from the AI response.
  const applyAiFlowDraft = (draft = {}) => {
    const bodyText = draft.bodyText || '';
    const fields = Array.isArray(draft.fields) ? draft.fields : [];
    const contentItems = [
      ...(bodyText ? [normalizeFlowContentItem({ type: 'Body', text: bodyText })] : []),
      ...(fields.length > 0
        ? fields.map(normalizeFlowContentItem)
        : [normalizeFlowContentItem({ type: 'TextInput', name: 'full_name', label: 'Full name', required: true })]),
    ];

    const normalizedDraft = {
      preset: draft.preset || 'custom_form',
      flowName: draft.flowName || 'ai_generated_flow',
      category: draft.category || 'OTHER',
      screenId: draft.screenId || 'FORM',
      screenTitle: draft.screenTitle || 'Form',
      bodyText,
      buttonText: draft.buttonText || 'Open form',
      submitLabel: draft.submitLabel || 'Submit',
      publish: draft.publish !== false,
      endpointUri: draft.endpointUri || '',
      contentItems,
      fields,
    };

    setFlowDraft(normalizedDraft);
    setFlowPreviewResult(null);
    setFlowCreateResult(null);
    setFlowErrors([]);
    setFlowApiFieldErrors({});
    setFlowButtonText(normalizedDraft.buttonText);
  };

  const generateAiFlow = async () => {
    const prompt = aiFlowPrompt.trim();
    if (!prompt) {
      message.warning('Describe the flow you want to create.');
      return;
    }

    try {
      setAiFlowLoading(true);
      setAiFlowError('');

      const response = await axiosConfig.post('/template/generate-ai-flow', {
        prompt,
        language: templateData.language || 'en_US',
        templatecategory: (templateData.category || 'utility').toLowerCase(),
      });

      const entity = getFlowEntityFromResponse(response);

      if (response.data?.status === false || (response.data?.statuscode && response.data.statuscode >= 400)) {
        throw response.data;
      }

      if (!entity?.readyForPreview) {
        const validationErrors = entity?.validation?.errors || [];
        setAiFlowError(
          validationErrors.length > 0
            ? validationErrors.join(' ')
            : 'AI could not generate a valid Flow. Try a more detailed prompt.'
        );
        return;
      }

      if (entity.draft) {
        applyAiFlowDraft(entity.draft);
      }

      // Apply suggested template content only if the body is currently empty
      if (entity.suggestedTemplate && !templateData.components.body.content) {
        const suggested = entity.suggestedTemplate;
        setTemplateData(prev => ({
          ...prev,
          components: {
            ...prev.components,
            body: {
              ...prev.components.body,
              content: suggested.templatecontent || prev.components.body.content,
            },
          },
        }));
      }

      setAiFlowModalOpen(false);
      setAiFlowPrompt('');
      message.success('AI-generated Flow loaded. Review the fields and create it in Meta when ready.');
    } catch (error) {
      console.error('Error generating AI Flow:', error);
      const errorMessages = getFlowErrorMessages(error);
      const errorText = errorMessages.join(' ') || error?.message || 'Failed to generate Flow with AI.';
      setAiFlowError(errorText);
    } finally {
      setAiFlowLoading(false);
    }
  };

  const getFlowButtonComponentForSubmit = () => {
    if (!hasFlowButton) return null;
    const validationErrors = flowCreateResult?.flow?.validation_errors || [];
    if (flowCreateResult?.flow?.ready_for_template !== true || validationErrors.length > 0) {
      return null;
    }
    return flowCreateResult?.button_component || null;
  };

  const getTemplateDataForSave = () => {
    const flowButtonComponent = getFlowButtonComponentForSubmit();
    if (!flowButtonComponent) return templateData;
    const preparedFlowDraft = prepareFlowDraftPayload(flowDraft);
    return {
      ...templateData,
      flowDraft: preparedFlowDraft,
      flowButtonComponent,
      flowTemplateButton: flowCreateResult?.template_button || flowButtonComponent?.buttons?.[0] || null
    };
  };

  // Validate individual field and update field errors
  const validateField = (fieldName, value, markAsTouched = true) => {
    let error = null;
    
    // Mark field as touched if specified
    if (markAsTouched) {
      touchedFieldsRef.current[fieldName] = true;
      setTouchedFields(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
    
    switch (fieldName) {
      case 'templateName':
        if (!value || !value.trim()) {
          error = 'Template name is required';
        } else {
          const templateNameRegex = /^[a-z0-9_]+$/;
          if (!templateNameRegex.test(value)) {
            error = 'Template name must contain only lowercase letters, numbers, and underscores';
          } else if (value.length < 3) {
            error = 'Template name must be at least 3 characters long';
          } else if (value.length > 50) {
            error = 'Template name must be less than 50 characters';
          }
        }
        break;
      case 'category':
        if (!value) {
          error = 'Category is required';
        }
        break;
      case 'language':
        if (!value) {
          error = 'Language is required';
        }
        break;
      case 'body':
        if (!value || !value.trim()) {
          error = 'Body content is required';
        } else if (value.trim().length < 1) {
          error = 'Body content cannot be empty';
        }
        break;
      case 'variableSamples':
        // This will be handled by a separate function for variable samples validation
        break;
      default:
        break;
    }
    
    // Only set error if field has been touched or if we're forcing validation
    const shouldShowError = touchedFieldsRef.current[fieldName] || !markAsTouched;
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: shouldShowError ? error : null
    }));
    
    return error;
  };

  // Validation function for template name (for Ant Design Form)
  const validateTemplateName = (_, value) => {
    const error = validateField('templateName', value);
    if (error) {
      return Promise.reject(new Error(error));
    }
    return Promise.resolve();
  };

  // Function to validate variable samples
  const validateVariableSamples = () => {
    const bodyVariables = templateData.components.body.variables || [];
    const validBodyVariables = getValidVariables(bodyVariables, templateData.variableType);
    const missingSamples = validBodyVariables.filter(variable => 
      !templateData.components.body.variableSamples?.[variable]?.trim()
    );
    
    if (missingSamples.length > 0) {
      const error = `Please provide sample values for all variables: ${missingSamples.map(v => `{{${v}}}`).join(', ')}`;
      setFieldErrors(prev => ({
        ...prev,
        variableSamples: error
      }));
      setTouchedFields(prev => ({
        ...prev,
        variableSamples: true
      }));
      return error;
    }
    
    // Clear error if validation passes
    setFieldErrors(prev => ({
      ...prev,
      variableSamples: null
    }));
    return null;
  };

  // Update templateData when variable type changes
  useEffect(() => {
    if (selectedVariableType) {
      setTemplateData(prev => ({
        ...prev,
        variableType: selectedVariableType
      }));
    }
  }, [selectedVariableType]);
  
  // Template State
  const [templateData, setTemplateData] = useState({
    name: '',
    category: '',
    language: 'en_US',
    addressBook: null, // Optional address book selection
    variableType: 'number', // 'number' or 'name' - controls variable format validation
    validityPeriod: {
      enabled: false,
      value: 10,
      unit: 'minutes'
    },
    components: {
      header: {
        type: 'none', // none, text, image, video, document
        content: '',
        variables: [],
        variableSamples: {}, // Store sample values for header variables
        mediaUrl: null, // For storing uploaded file URL
        mediaFile: null, // For storing uploaded file object
        validationErrors: [],
        validationWarnings: [],
        duplicateVariables: []
      },
      body: {
        content: '',
        variables: [],
        variableSamples: {}, // Store sample values for each variable
        validationErrors: [],
        validationWarnings: [],
        duplicateVariables: []
      },
      footer: {
        type: 'none', // none, text
        content: '',
        variables: [],
        validationErrors: [],
        validationWarnings: [],
        duplicateVariables: []
      },
      buttons: []
    },
    variables: [],
    // Authentication specific fields
    authentication: {
      codeDelivery: 'zero-tap', // zero-tap, one-tap, copy-code
      zeroTapAgreement: false, // Checkbox for zero-tap terms agreement
      apps: [{
        id: 'app-1',
        packageName: '',
        signatureHash: ''
      }],
      securityRecommendation: false,
      codeExpiration: false,
      expirationTime: 5,
      autofillButtonText: 'Autofill',
      copyButtonText: 'Copy code'
    }
  });

  const hasFlowButton = templateData.components.buttons.some(button => button.type === 'flow');

  // Initial validation on component load
  useEffect(() => {
    // Validate initial field values without marking as touched
    validateField('templateName', templateData.templateName, false);
    validateField('category', templateData.category, false);
    validateField('language', templateData.language, false);
    validateField('body', templateData.components.body.content, false);
  }, []); // Empty dependency array means this runs once on mount

  // Package Name Validation
  const validatePackageName = (packageName) => {
    if (!packageName || !packageName.trim()) {
      return { isValid: false, error: 'Package name is required' };
    }

    // Check for minimum segments (at least 2 parts separated by dots)
    const segments = packageName.split('.');
    if (segments.length < 2) {
      return { 
        isValid: false, 
        error: 'Per Android guidelines, an application ID must have at least two segments, separated by dots' 
      };
    }

    // Validate each segment
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      
      // Check if segment is empty
      if (!segment) {
        return { 
          isValid: false, 
          error: 'Package name segments cannot be empty' 
        };
      }

      // Check if segment starts with a letter
      if (!/^[a-zA-Z]/.test(segment)) {
        return { 
          isValid: false, 
          error: 'Each segment should begin with a letter' 
        };
      }

      // Check if all characters in segment are alphanumeric or underscore
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(segment)) {
        return { 
          isValid: false, 
          error: 'All characters in a segment should be alphanumeric or underscore' 
        };
      }
    }

    return { isValid: true, error: null };
  };

  // App Signature Hash Validation
  const validateSignatureHash = (signatureHash) => {
    if (!signatureHash || !signatureHash.trim()) {
      return { isValid: false, error: 'App signature hash is required' };
    }

    if (signatureHash.length !== 11) {
      return { 
        isValid: false, 
        error: 'Your signature hash must be 11 characters long' 
      };
    }

    return { isValid: true, error: null };
  };

  // Address Book API Functions
  const fetchAddressBooks = async () => {
    try {
      setAddressBookLoading(true);
      const response = await axiosConfig.get('/addressbook/details');
      if (response.data.status && response.data.entity) {
        setAddressBooks(response.data.entity);
      }
    } catch (error) {
      console.error('Error fetching address books:', error);
      // Address book loading is optional, so no popup error needed
    } finally {
      setAddressBookLoading(false);
    }
  };

  const fetchAddressBookUsers = async (bookId) => {
    try {
      const response = await axiosConfig.post('/addressbook/users', {
        bookid: bookId,
        limit: 10,
        offset: 0,
        searchby: "",
        labelfilter: ""
      });
      
      if (response.data.status && response.data.entity && response.data.entity.users) {
        const users = response.data.entity.users;
        setSelectedAddressBookUsers(users);
        
        // Extract column names from the first user
        if (users.length > 0) {
          const firstUser = users[0];
          const columns = [];
          
          // Add standard fields
          const standardFields = ['username', 'mobileno', 'emailid', 'address'];
          standardFields.forEach(field => {
            if (firstUser.hasOwnProperty(field)) {
              columns.push({
                key: field,
                label: field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                value: field,
                source: 'standard' // Keep track that this comes from standard fields
              });
            }
          });
          
          // Add userdata fields (flattened for template variables)
          if (firstUser.userdata && typeof firstUser.userdata === 'object') {
            Object.keys(firstUser.userdata).forEach(key => {
              columns.push({
                key: key, // Use the direct key name for template variables like {{city}}
                label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                value: key,
                source: 'userdata' // Keep track that this comes from userdata
              });
            });
          }
          
          setAddressBookColumns(columns);
        }
      }
    } catch (error) {
      console.error('Error fetching address book users:', error);
      // Address book user loading is optional, so no popup error needed
    }
  };

  // Handle address book selection
  const handleAddressBookChange = (bookId) => {
    setTemplateData(prev => ({
      ...prev,
      addressBook: bookId,
      variableType: bookId ? 'name' : 'number' // Auto-set to name format when address book is selected
    }));
    
    if (bookId) {
      fetchAddressBookUsers(bookId);
    } else {
      setSelectedAddressBookUsers([]);
      setAddressBookColumns([]);
    }
  };

  // Handle variable insertion into body content
  const handleVariableInsert = (variableKey) => {
    const variable = `{{${variableKey}}}`;
    const currentContent = templateData.components.body.content || '';
    
    // Insert variable at the end of current content with a space if content exists
    const newContent = currentContent ? `${currentContent} ${variable}` : variable;
    
    // Update the body content
    updateVariables('body', newContent);
    validateField('body', newContent);
    
    // Show success message
    // message.success(`Variable ${variable} added to template`);
  };

  // Apply WhatsApp formatting to the contentEditable body editor
  const applyBodyFormat = (marker) => {
    const editor = bodyEditorRef.current;
    if (editor) {
      editor.focus();
      if (marker === '*') {
        document.execCommand('bold', false, null);
      } else if (marker === '_') {
        document.execCommand('italic', false, null);
      } else if (marker === '~') {
        document.execCommand('strikeThrough', false, null);
      } else if (marker === 'bullet') {
        document.execCommand('insertUnorderedList', false, null);
      } else if (marker === 'number') {
        document.execCommand('insertOrderedList', false, null);
      }
      const markdown = displayHtmlToMarkdown(editor.innerHTML);
      bodyContentInEditor.current = markdown;
      updateVariables('body', markdown);
      validateField('body', markdown);
      syncBodyToolbarState();
      return;
    }
    // Fallback: textarea-based (if editor not mounted)
    const textarea = bodyTextareaRef.current?.resizableTextArea?.textArea;
    const currentValue = templateData.components.body.content || '';
    const start = textarea ? textarea.selectionStart : currentValue.length;
    const end = textarea ? textarea.selectionEnd : currentValue.length;
    const selected = currentValue.substring(start, end);
    let newValue, cursorStart, cursorEnd;
    if (marker === 'bullet' || marker === 'number') {
      const listPrefix = marker === 'bullet' ? '• ' : '1. ';
      if (selected) {
        newValue = currentValue.substring(0, start) + listPrefix + selected + currentValue.substring(end);
        cursorStart = start + listPrefix.length;
        cursorEnd = cursorStart + selected.length;
      } else {
        newValue = currentValue.substring(0, start) + listPrefix + currentValue.substring(start);
        cursorStart = start + listPrefix.length;
        cursorEnd = cursorStart;
      }
    } else if (selected) {
      newValue = currentValue.substring(0, start) + marker + selected + marker + currentValue.substring(end);
      cursorStart = start + marker.length;
      cursorEnd = end + marker.length;
    } else {
      newValue = currentValue.substring(0, start) + marker + marker + currentValue.substring(start);
      cursorStart = start + marker.length;
      cursorEnd = cursorStart;
    }
    updateVariables('body', newValue);
    validateField('body', newValue);
    requestAnimationFrame(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(cursorStart, cursorEnd);
      }
    });
  };

  const isSelectionInsideBodyEditor = () => {
    const editor = bodyEditorRef.current;
    const selection = window.getSelection();
    return !!editor && !!selection && selection.rangeCount > 0 && (
      editor.contains(selection.anchorNode) || editor.contains(selection.focusNode)
    );
  };

  const isSelectionInCodeTag = () => {
    const selection = window.getSelection();
    let node = selection?.anchorNode || selection?.focusNode || null;
    while (node) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'CODE') {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };

  const syncBodyToolbarState = () => {
    if (!isSelectionInsideBodyEditor()) {
      setBodyToolbarState({
        bold: false,
        italic: false,
        strike: false,
        code: false,
        bullet: false,
        ordered: false,
      });
      return;
    }

    let bold = false;
    let italic = false;
    let strike = false;
    let bullet = false;
    let ordered = false;

    try {
      bold = document.queryCommandState('bold');
      italic = document.queryCommandState('italic');
      strike = document.queryCommandState('strikeThrough');
      bullet = document.queryCommandState('insertUnorderedList');
      ordered = document.queryCommandState('insertOrderedList');
    } catch (error) {
      // Some browsers can throw here; keep the default false state.
    }

    setBodyToolbarState({
      bold,
      italic,
      strike,
      code: isSelectionInCodeTag(),
      bullet,
      ordered,
    });
  };

  // Handle direct variable insertion based on variable type
  const handleDirectVariableInsert = () => {
    const currentContent = templateData.components.body.content || '';
    let variable;
    
    if (templateData.variableType === 'number') {
      // For number type, find the next sequential number
      const existingVariables = extractVariables(currentContent);
      const existingNumbers = existingVariables
        .filter(v => /^\d+$/.test(v))
        .map(v => parseInt(v))
        .sort((a, b) => a - b);
      
      // Find the next available number
      let nextNumber = 1;
      for (let i = 0; i < existingNumbers.length; i++) {
        if (existingNumbers[i] === nextNumber) {
          nextNumber++;
        } else {
          break;
        }
      }
      
      variable = `{{${nextNumber}}}`;
    } else {
      // For name type, insert empty brackets for user to fill
      variable = `{{}}`;
    }
    
    // Insert variable at the end of current content with a space if content exists
    const newContent = currentContent ? `${currentContent} ${variable}` : variable;
    
    // Update the body content
    updateVariables('body', newContent);
    validateField('body', newContent);
    
    // Show success message
    if (templateData.variableType === 'number') {
      // message.success(`Variable ${variable} added to template`);
    } else {
      // message.success(`Variable placeholder {{}} added. Please replace with your variable name.`);
    }
  };

  // Handle variable insertion into header content (max 1 variable allowed)
  const handleHeaderVariableInsert = (variableKey) => {
    const variable = `{{${variableKey}}}`;
    const currentContent = templateData.components.header.content || '';
    const existingVariables = extractVariables(currentContent);
    
    // Check if header already has a variable
    if (existingVariables.length >= 1) {
      message.error('Header can only contain one variable. Please remove the existing variable first.');
      return;
    }
    
    // Insert variable at the end of current content with a space if content exists
    const newContent = currentContent ? `${currentContent} ${variable}` : variable;
    
    // Update the header content
    updateVariables('header', newContent);
    validateField('header', newContent);
    
    // Show success message
    // message.success(`Variable ${variable} added to header`);
  };

  // Handle direct variable insertion for header based on variable type (max 1 variable allowed)
  const handleHeaderDirectVariableInsert = () => {
    const currentContent = templateData.components.header.content || '';
    const existingVariables = extractVariables(currentContent);
    
    // Check if header already has a variable
    if (existingVariables.length >= 1) {
      message.error('Header can only contain one variable. Please remove the existing variable first.');
      return;
    }
    
    let variable;
    
    if (templateData.variableType === 'number') {
      // For number type, start with 1 since header can only have one variable
      variable = `{{1}}`;
    } else {
      // For name type, insert empty brackets for user to fill
      variable = `{{}}`;
    }
    
    // Insert variable at the end of current content with a space if content exists
    const newContent = currentContent ? `${currentContent} ${variable}` : variable;
    
    // Update the header content
    updateVariables('header', newContent);
    validateField('header', newContent);
    
    // Show success message
    if (templateData.variableType === 'number') {
      // message.success(`Variable ${variable} added to header`);
    } else {
      // message.success(`Variable placeholder {{}} added to header. Please replace with your variable name.`);
    }
  };

  // Load address books on component mount
  useEffect(() => {
    fetchAddressBooks();
  }, []);

  // Add new app
  const addApp = () => {
    if (templateData.authentication.apps.length >= 5) return;
    
    const newApp = {
      id: `app-${Date.now()}`,
      packageName: '',
      signatureHash: ''
    };

    setTemplateData(prev => ({
      ...prev,
      authentication: {
        ...prev.authentication,
        apps: [...prev.authentication.apps, newApp]
      }
    }));
  };

  // Remove app
  const removeApp = (appId) => {
    if (templateData.authentication.apps.length <= 1) return; // Keep at least one app

    setTemplateData(prev => ({
      ...prev,
      authentication: {
        ...prev.authentication,
        apps: prev.authentication.apps.filter((app, index) => 
          (app.id || index) !== appId
        )
      }
    }));
  };

  // Update app field
  const updateApp = (appId, field, value) => {
    setTemplateData(prev => ({
      ...prev,
      authentication: {
        ...prev.authentication,
        apps: prev.authentication.apps.map((app, index) => 
          (app.id || index) === appId ? { ...app, [field]: value } : app
        )
      }
    }));
  };

  // Close emoji picker when clicking outside, pressing Escape, or scrolling
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside emoji picker
      const emojiPicker = emojiPickerRef.current || document.querySelector('.EmojiPickerReact');
      const emojiButton = event.target.closest('button[aria-label*="Emoji"], button:has(.anticon-smile)');
      
      if (showEmojiPicker && emojiPicker && !emojiPicker.contains(event.target) && !emojiButton) {
        setShowEmojiPicker(false);
      }
    };
    
    const handleEscape = (event) => {
      if (event.key === 'Escape' && showEmojiPicker) {
        setShowEmojiPicker(false);
      }
    };

    const handleScroll = () => {
      if (emojiPickerAnchorEl) {
        const { width, height } = getEmojiPickerSize();
        setEmojiPickerPosition(getEmojiPickerPosition(emojiPickerAnchorEl, width, height));
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('scroll', handleScroll, true);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showEmojiPicker, emojiPickerAnchorEl]);

  // Handle emoji selection
  const handleEmojiSelect = (emoji, target) => {
    if (target === 'header') {
      const currentContent = templateData.components.header.content || '';
      const newContent = currentContent + emoji;
      updateVariables('header', newContent);
    } else if (target === 'body') {
      const currentContent = templateData.components.body.content || '';
      const newContent = currentContent + emoji;
      updateVariables('body', newContent);
      validateField('body', newContent);
    } else if (target === 'footer') {
      const currentContent = templateData.components.footer.content || '';
      const newContent = currentContent + emoji;
      updateVariables('footer', newContent);
    }
    // Keep picker open for multiple selections
  };

  const getEmojiPickerSize = () => {
    if (typeof window === 'undefined') {
      return { width: 350, height: 400 };
    }

    const isMobile = window.innerWidth < 480;
    // Keep height small enough to open above the button in most viewport positions
    return {
      width: isMobile ? Math.min(window.innerWidth - 20, 280) : 300,
      height: isMobile ? 260 : 280
    };
  };

  const getEmojiPickerPosition = (anchorEl, width, height) => {
    if (typeof window === 'undefined') {
      return { top: 0, left: 0 };
    }

    const gap = 8;
    const minLeft = 8;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);

    if (!anchorEl) {
      return {
        top: Math.max(8, window.innerHeight - height - 8),
        left: maxLeft
      };
    }

    const rect = anchorEl.getBoundingClientRect();
    const left = Math.min(Math.max(rect.right - width, minLeft), maxLeft);
    const topAbove = rect.top - height - gap;
    const topBelow = rect.bottom + gap;

    // Always open above the button; clamp to top of viewport if needed, never flip below
    const top = Math.max(8, topAbove);

    return { top, left };
  };

  const renderEmojiPicker = (target, onEmojiClick) => {
    if (!showEmojiPicker || emojiPickerTarget !== target) return null;
    if (typeof document === 'undefined') return null;

    const { width, height } = getEmojiPickerSize();

    return createPortal(
      <div
        ref={emojiPickerRef}
        style={{
          position: 'fixed',
          top: emojiPickerPosition.top,
          left: emojiPickerPosition.left,
          zIndex: 2000,
          boxShadow: '0 5px 30px rgba(0,0,0,0.3)',
          borderRadius: 12,
          overflow: 'hidden',
          maxWidth: '95vw',
          maxHeight: `${height}px`
        }}
      >
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => setShowEmojiPicker(false)}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2001,
            background: 'rgba(255,255,255,0.9)',
            borderRadius: '50%',
            width: 28,
            height: 28,
            minWidth: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666'
          }}
          size="small"
        />
        <EmojiPicker
          onEmojiClick={onEmojiClick}
          theme="light"
          width={width}
          height={height}
          searchPlaceholder="Search emoji..."
          previewConfig={{ showPreview: false }}
          skinTonesDisabled
          lazyLoadEmojis
        />
      </div>,
      document.body
    );
  };

  // Toggle emoji picker for specific target
  const toggleEmojiPicker = (target, event) => {
    if (emojiPickerTarget === target && showEmojiPicker) {
      setShowEmojiPicker(false);
      setEmojiPickerTarget(null);
      setEmojiPickerAnchorEl(null);
    } else {
      const anchorEl = event?.currentTarget || null;
      setEmojiPickerTarget(target);
      setEmojiPickerAnchorEl(anchorEl);
      if (anchorEl) {
        const { width, height } = getEmojiPickerSize();
        setEmojiPickerPosition(getEmojiPickerPosition(anchorEl, width, height));
      }
      setShowEmojiPicker(true);
    }
  };

  // Handle file upload
  const handleFileUpload = async (info) => {
    const { file, fileList } = info;
    
    // If file is removed
    if (fileList.length === 0) {
      setTemplateData(prev => ({
        ...prev,
        components: {
          ...prev.components,
          header: {
            ...prev.components.header,
            mediaUrl: null,
            mediaFile: null,
            uploadSessionId: null,
            fileHandle: null
          }
        }
      }));
      return;
    }
    
    if (file && file.status !== 'removed') {
      // Create a preview URL for the uploaded file
      const reader = new FileReader();
      reader.onload = async (e) => {
        // First update the UI with the preview
        setTemplateData(prev => ({
          ...prev,
          components: {
            ...prev.components,
            header: {
              ...prev.components.header,
              mediaUrl: e.target.result,
              mediaFile: file,
              uploadSessionId: null,
              fileHandle: null
            }
          }
        }));
        
        // Then immediately start the upload process
        try {
          message.loading({ content: 'Starting file upload...', key: 'file-upload', duration: 0 });
          
          // Start upload session
          const uploadSessionId = await startUploadSession(
            file.name,
            file.type,
            file.size
          );
          
          // Upload file data
          message.loading({ content: 'Uploading file data...', key: 'file-upload', duration: 0 });
          const { fileHandle, mediaId, s3Key } = await uploadFileData(uploadSessionId, file, file.type);
          
          // Update template data with file handle and mediaId
          setTemplateData(prev => ({
            ...prev,
            components: {
              ...prev.components,
              header: {
                ...prev.components.header,
                fileHandle: fileHandle,
                mediaId: mediaId,
                s3Key: s3Key,
                uploadSessionId: uploadSessionId
              }
            }
          }));
          
          message.success({ content: 'File uploaded successfully!', key: 'file-upload', duration: 3 });
          
        } catch (error) {
          console.error('Error uploading file:', error);
          message.error({ content: error.message || 'Failed to upload file', key: 'file-upload', duration: 5 });
          
          // Reset file state on error
          setTemplateData(prev => ({
            ...prev,
            components: {
              ...prev.components,
              header: {
                ...prev.components.header,
                mediaUrl: null,
                mediaFile: null,
                uploadSessionId: null,
                fileHandle: null
              }
            }
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Start file upload session
  const startUploadSession = async (fileName, fileType, fileLength) => {
    try {
      const response = await axiosConfig.post('/template/start-upload-session', {
        fileName,
        fileType,
        fileLength
      });
      
      if (response.data.status && response.data.statuscode === 200) {
        return response.data.entity.uploadSessionId;
      } else {
        throw new Error(response.data.message || 'Failed to start upload session');
      }
    } catch (error) {
      console.error('Error starting upload session:', error);
      throw error;
    }
  };

  // Upload file data and get file handle and mediaId
  const uploadFileData = async (uploadSessionId, file, fileType) => {
    try {
      const formData = new FormData();
      formData.append('uploadSessionId', uploadSessionId); // Add uploadSessionId as form data field
      formData.append('file', file); // Add the file itself
      formData.append('type', fileType); // Add fileType as 'type' field
      
      // Cookies are sent automatically via withCredentials: true
      const response = await axiosConfig.post('/template/upload-file-data', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.status && response.data.statuscode === 200) {
        return {
          fileHandle: response.data.entity.fileHandle,
          mediaId: response.data.entity.mediaId,
          s3Key: response.data.entity.s3Key
        };
      } else {
        throw new Error(response.data.message || 'Failed to upload file data');
      }
    } catch (error) {
      console.error('Error uploading file data:', error);
      throw error;
    }
  };

  // Create template with file handle and mediaId
  const createTemplateWithFileHandle = async (templateData, fileHandle, mediaId, headerFormat) => {
    try {
      // Prepare components array in the format expected by the API
      const components = [
        {
          type: 'BODY',
          text: templateData.components.body.content,
          example: {
            body_text: [templateData.components.body.variables?.map(variable => [variable]) || []]
          }
        }
      ];
      
      // Add footer if it exists
      if (templateData.components.footer.content) {
        components.push({
          type: 'FOOTER',
          text: templateData.components.footer.content
        });
      }
      
      // Add buttons if they exist
      if (templateData.components.buttons.length > 0) {
        const buttons = templateData.components.buttons.map(button => {
          const btnType = button.type.toUpperCase();
          if (btnType === 'QUICK_REPLY') {
            return { type: 'QUICK_REPLY', text: button.text };
          } else if (btnType === 'URL') {
            return { type: 'URL', text: button.text, url: button.url };
          } else if (btnType === 'PHONE' || btnType === 'PHONE_NUMBER') {
            return { type: 'PHONE_NUMBER', text: button.text, phone_number: button.phone || button.phoneNumber || '' };
          }
          return { type: btnType, text: button.text };
        });
        components.push({
          type: 'BUTTONS',
          buttons,
        });
      }
      
      const requestBody = {
        templatename: templateData.name,
        templatelanguage: templateData.language,
        templatecontent: templateData.components.body.content,
        templatecategory: templateData.category.toUpperCase(),
        companyid: templateData.companyId || '',
        fileHandle,
        mediaId: mediaId || '', // Add mediaId to the request body
        s3Key: templateData.s3Key || '',
        headerFormat: headerFormat.toUpperCase(),
        component: components,
        variableType: templateData.variableType || 'name'
      };
      
      const response = await axiosConfig.post('/template/create-with-file-handle', requestBody);
      
      if (response.data.status && response.data.statuscode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to create template with file');
      }
    } catch (error) {
      console.error('Error creating template with file handle:', error);
      throw error;
    }
  };

  // WhatsApp Template Categories (based on API requirements)
  const templateCategories = [
    {
      value: 'Utility',
      label: 'Utility',
      description: 'Account updates, order notifications, alerts',
      examples: ['Order confirmations', 'Account notifications', 'Delivery updates']
    },
    {
      value: 'Marketing',
      label: 'Marketing', 
      description: 'Promotional messages, offers, announcements',
      examples: ['Product promotions', 'Event invitations', 'Special offers']
    },
    {
      value: 'Authentication',
      label: 'Authentication',
      description: 'OTP codes, verification messages',
      examples: ['Login verification', 'Password reset', 'Account verification'],
      isSpecial: true
    }
  ];

  // Language options
  // Full Meta-supported language list (matches WhatsApp Business Platform)
  const languages = [
    { value: 'af',    label: 'Afrikaans' },
    { value: 'sq',    label: 'Albanian' },
    { value: 'ar',    label: 'Arabic' },
    { value: 'az',    label: 'Azerbaijani' },
    { value: 'bn',    label: 'Bengali' },
    { value: 'bg',    label: 'Bulgarian' },
    { value: 'ca',    label: 'Catalan' },
    { value: 'zh_CN', label: 'Chinese (Simplified)' },
    { value: 'zh_TW', label: 'Chinese (Traditional)' },
    { value: 'hr',    label: 'Croatian' },
    { value: 'cs',    label: 'Czech' },
    { value: 'da',    label: 'Danish' },
    { value: 'nl',    label: 'Dutch' },
    { value: 'en',    label: 'English' },
    { value: 'en_GB', label: 'English (UK)' },
    { value: 'en_US', label: 'English (US)' },
    { value: 'et',    label: 'Estonian' },
    { value: 'fil',   label: 'Filipino' },
    { value: 'fi',    label: 'Finnish' },
    { value: 'fr',    label: 'French' },
    { value: 'ka',    label: 'Georgian' },
    { value: 'de',    label: 'German' },
    { value: 'el',    label: 'Greek' },
    { value: 'gu',    label: 'Gujarati' },
    { value: 'ha',    label: 'Hausa' },
    { value: 'he',    label: 'Hebrew' },
    { value: 'hi',    label: 'Hindi' },
    { value: 'hu',    label: 'Hungarian' },
    { value: 'id',    label: 'Indonesian' },
    { value: 'ga',    label: 'Irish' },
    { value: 'it',    label: 'Italian' },
    { value: 'ja',    label: 'Japanese' },
    { value: 'kn',    label: 'Kannada' },
    { value: 'kk',    label: 'Kazakh' },
    { value: 'rw_RW', label: 'Kinyarwanda' },
    { value: 'ko',    label: 'Korean' },
    { value: 'ky_KG', label: 'Kyrgyz (Kyrgyzstan)' },
    { value: 'lo',    label: 'Lao' },
    { value: 'lv',    label: 'Latvian' },
    { value: 'lt',    label: 'Lithuanian' },
    { value: 'mk',    label: 'Macedonian' },
    { value: 'ms',    label: 'Malay' },
    { value: 'ml',    label: 'Malayalam' },
    { value: 'mr',    label: 'Marathi' },
    { value: 'nb',    label: 'Norwegian' },
    { value: 'fa',    label: 'Persian' },
    { value: 'pl',    label: 'Polish' },
    { value: 'pt_BR', label: 'Portuguese (Brazil)' },
    { value: 'pt_PT', label: 'Portuguese (Portugal)' },
    { value: 'pa',    label: 'Punjabi' },
    { value: 'ro',    label: 'Romanian' },
    { value: 'ru',    label: 'Russian' },
    { value: 'sr',    label: 'Serbian' },
    { value: 'sk',    label: 'Slovak' },
    { value: 'sl',    label: 'Slovenian' },
    { value: 'es',    label: 'Spanish' },
    { value: 'es_AR', label: 'Spanish (Argentina)' },
    { value: 'es_ES', label: 'Spanish (Spain)' },
    { value: 'es_MX', label: 'Spanish (Mexico)' },
    { value: 'sw',    label: 'Swahili' },
    { value: 'sv',    label: 'Swedish' },
    { value: 'ta',    label: 'Tamil' },
    { value: 'te',    label: 'Telugu' },
    { value: 'th',    label: 'Thai' },
    { value: 'tr',    label: 'Turkish' },
    { value: 'uk',    label: 'Ukrainian' },
    { value: 'ur',    label: 'Urdu' },
    { value: 'uz',    label: 'Uzbek' },
    { value: 'vi',    label: 'Vietnamese' },
    { value: 'zu',    label: 'Zulu' },
  ];

  // Button types
  const buttonTypes = [
    { value: 'url', label: 'Visit Website', icon: <LinkOutlined /> },
    { value: 'phone', label: 'Call Phone Number', icon: <PhoneOutlined /> },
    { value: 'quick_reply', label: 'Quick Reply', icon: <SendOutlined /> },
    { value: 'flow', label: 'Flow', icon: <FormOutlined /> }
  ];

  // Extract variables from text
  const extractVariables = (text) => {
    if (!text) return [];
    const regex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }
    return variables;
  };

  // Validate variables based on selected type and section
  const validateVariables = (content, variableType, section = 'body') => {
    if (!content) return { isValid: true, errors: [], warnings: [] };
    
    const variables = extractVariables(content);
    const errors = [];
    const warnings = [];
    const sectionLabelMap = {
      header: 'Header',
      body: 'Body',
      footer: 'Footer'
    };
    const sectionLabel = sectionLabelMap[section] || 'Template text';
    const componentCharacterLimit = {
      header: 60,
      body: 1024,
      footer: 60
    }[section];
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variableMatches = [];
    let match;

    while ((match = variableRegex.exec(content)) !== null) {
      variableMatches.push({
        fullMatch: match[0],
        variable: match[1],
        index: match.index,
        end: match.index + match[0].length
      });
    }

    if (componentCharacterLimit && content.length > componentCharacterLimit) {
      errors.push(`${sectionLabel} text must be ${componentCharacterLimit} characters or fewer.`);
    }
    
    // Header-specific validation: maximum 1 variable allowed
    if (section === 'header' && variables.length > 1) {
      errors.push(`Header can't include more than one variable. Found ${variables.length} variables: ${variables.map(v => `{{${v}}}`).join(', ')}`);
    }

    // Footer-specific validation: Meta footer components are static text only
    if (section === 'footer' && variables.length > 0) {
      errors.push(`Footer can't include variables. Meta footer components support static text only.`);
    }

    // Meta template text hygiene: variables must be surrounded by fixed text and not adjacent
    if ((section === 'body' || section === 'header') && variableMatches.length > 0) {
      const contentWithoutVariables = content.replace(variableRegex, '').trim();

      if (!contentWithoutVariables) {
        errors.push(`${sectionLabel} can't be only variable placeholders. Add meaningful fixed text around the variable.`);
      } else {
        const trimmedContent = content.trim();
        // Removed strictly enforcing fixed text before first or after last variable
        // as some specific use cases (like single header properties with context) might require
        // trailing variables, and users requested bypassing this strictness.
      }

      const hasAdjacentVariables = variableMatches.some((currentMatch, index) => {
        const nextMatch = variableMatches[index + 1];
        if (!nextMatch) return false;
        const textBetweenVariables = content.slice(currentMatch.end, nextMatch.index);
        return textBetweenVariables.trim() === '';
      });

      if (hasAdjacentVariables) {
        errors.push(`${sectionLabel} can't contain adjacent variables. Add fixed words between placeholders.`);
      }
    }
    
    // Check for duplicate variables
    const variableCounts = {};
    const allVariablesInContent = [];

    variableMatches.forEach(({ variable }) => {
      allVariablesInContent.push(variable);
      variableCounts[variable] = (variableCounts[variable] || 0) + 1;
    });
    
    // Check if there are duplicate variables
    const duplicateVariables = Object.keys(variableCounts).filter(variable => variableCounts[variable] > 1);
    if (duplicateVariables.length > 0) {
      errors.push(`This template contains duplicate variable parameters.`);
    }
    
    if (variableType === 'number') {
      // For number type, only {{1}}, {{2}}, {{3}} etc. are allowed
      const numberRegex = /^\d+$/;
      variables.forEach(variable => {
        if (!numberRegex.test(variable)) {
          errors.push(`Variable {{${variable}}} is not valid for number format. Use {{1}}, {{2}}, {{3}} etc.`);
        }
      });

      const numericVariablesInOrder = variableMatches
        .map(item => item.variable)
        .filter(variable => numberRegex.test(variable))
        .map(variable => Number(variable));

      if (numericVariablesInOrder.length > 0 && numericVariablesInOrder.length === variableMatches.length) {
        const uniqueNumericVariablesInOrder = numericVariablesInOrder.filter((value, index, values) => values.indexOf(value) === index);
        const hasSequentialNumbering = uniqueNumericVariablesInOrder.every((value, index) => value === index + 1);

        if (!hasSequentialNumbering) {
          errors.push(`${sectionLabel} numbered variables must start at {{1}} and continue in order without gaps, for example {{1}}, {{2}}, {{3}}.`);
        }
      }
    } else if (variableType === 'name') {
      // For name type, only lowercase letters, numbers, and underscores are allowed
      // But must contain at least one letter (not just numbers)
      const nameRegex = /^[a-z0-9_]*[a-z][a-z0-9_]*$/;
      variables.forEach(variable => {
        if (!nameRegex.test(variable)) {
          errors.push(`This template contains variable parameters with incorrect formatting. Variable parameters must be lowercase characters, underscores and numbers with two sets of curly brackets (for example, {{customer_name}}, {{order_id}}).`);
        }
      });
    }
    
    // Check variable to content ratio (Meta's official validation logic) - only for body section
    if (section === 'body') {
      const contentWithoutVariables = content.replace(/\{\{[^}]+\}\}/g, '').trim();
      const variableCount = allVariablesInContent.length; // Use total count including duplicates
      
      // Meta's validation: Check if message contains too many variables relative to message length
      if (variableCount > 0) {
        const contentToVariableRatio = contentWithoutVariables.length / variableCount;
        const totalLength = content.length;
        const variableLength = allVariablesInContent.reduce((sum, variable) => sum + `{{${variable}}}`.length, 0);
        const variablePercentage = (variableLength / totalLength) * 100;
        
        // Meta's validation triggers warning when:
        // 1. Less than 12 characters of actual content per variable, OR
        // 2. Variables make up more than 50% of total message, OR  
        // 3. Very short content with multiple variables
        
        let shouldShowWarning = false;
        
        // Test case: "Hello {{1}}, your order {{2}} has" 
        // Content: "Hello , your order  has" = 25 chars / 2 vars = 12.5 ratio (borderline)
        // Variables: "{{1}}{{2}}" = 8 chars / 33 total = 24% (acceptable)
        // Result: Should trigger warning due to ratio being close to threshold
        
        if (contentToVariableRatio < 13) {
          shouldShowWarning = true;
        } else if (variablePercentage > 50) {
          shouldShowWarning = true;
        } else if (contentWithoutVariables.length <= 5 && variableCount > 1) {
          shouldShowWarning = true;
        }
        
        if (shouldShowWarning) {
          warnings.push(`Template contains too many variable parameters relative to the message length. You need to decrease the number of variable parameters or increase the message length.`);
        }
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      duplicateVariables // Include this for highlighting in preview
    };
  };

  // Get valid variables based on current variable type
  const getValidVariables = (variables, variableType) => {
    if (!variables || variables.length === 0) return [];
    
    return variables.filter(variable => {
      if (variableType === 'number') {
        const numberRegex = /^\d+$/;
        return numberRegex.test(variable);
      } else if (variableType === 'name') {
        const nameRegex = /^[a-z0-9_]*[a-z][a-z0-9_]*$/;
        return nameRegex.test(variable);
      }
      return true;
    });
  };

  // Update variables when content changes
  const updateVariables = (section, content) => {
    let processedContent = content;
    
    // For header section, automatically remove extra variables (keep only the first one)
    if (section === 'header' && content) {
      const variables = extractVariables(content);
      if (variables.length > 1) {
        // Find the first valid variable and remove all others
        const validVariables = getValidVariables(variables, templateData.variableType);
        if (validVariables.length > 0) {
          const firstValidVariable = validVariables[0];
          const firstVariablePattern = new RegExp(`\\{\\{${firstValidVariable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}\\}`);
          
          // Find the position of the first valid variable
          const firstMatch = content.match(firstVariablePattern);
          if (firstMatch) {
            const beforeFirstVariable = content.substring(0, firstMatch.index);
            const afterFirstVariable = content.substring(firstMatch.index + firstMatch[0].length);
            
            // Remove all other variables from the remaining content
            const cleanedAfterContent = afterFirstVariable.replace(/\{\{[^}]+\}\}/g, '');
            
            processedContent = beforeFirstVariable + firstMatch[0] + cleanedAfterContent;
          }
        } else {
          // If no valid variables, remove all variables except incomplete ones (just {{}})
          processedContent = content.replace(/\{\{[^}]+\}\}/g, '');
        }
      }
    }
    
    const newVariables = extractVariables(processedContent);
    const validation = validateVariables(processedContent || '', templateData.variableType, section);
    
    // For sections that can have variable samples (body and header), maintain samples and handle edge cases
    if (section === 'body' || section === 'header') {
      setTemplateData(prev => {
        const currentSamples = prev.components[section].variableSamples || {};
        const newSamples = {};
        
        // Only keep samples for valid variables
        const validVariables = getValidVariables(newVariables, templateData.variableType);
        validVariables.forEach(variable => {
          newSamples[variable] = currentSamples[variable] || '';
        });
        
        // Handle edge case: if content is deleted but variables exist in samples, clear them
        if (!processedContent || processedContent.trim() === '') {
          Object.keys(newSamples).forEach(variable => {
            if (!validVariables.includes(variable)) {
              delete newSamples[variable];
            }
          });
        }
        
        return {
          ...prev,
          components: {
            ...prev.components,
            [section]: {
              ...prev.components[section],
              content: processedContent || '',
              variables: newVariables,
              variableSamples: newSamples,
              validationErrors: validation.errors,
              validationWarnings: validation.warnings,
              duplicateVariables: validation.duplicateVariables
            }
          }
        };
      });
    } else {
      // For footer section, no samples needed but set type based on content
      setTemplateData(prev => ({
        ...prev,
        components: {
          ...prev.components,
          [section]: {
            ...prev.components[section],
            type: (processedContent && processedContent.trim()) ? 'text' : 'none',
            content: processedContent || '',
            variables: newVariables,
            validationErrors: validation.errors,
            validationWarnings: validation.warnings,
            duplicateVariables: validation.duplicateVariables
          }
        }
      }));
    }
  };

  // Handle variable type change and re-validate all existing content
  const handleVariableTypeChange = (newVariableType) => {
    setSelectedVariableType(newVariableType);
    setTemplateData(prev => {
      const updatedComponents = { ...prev.components };
      
      // Re-validate all sections that have content
      if (updatedComponents.header.content) {
        const headerValidation = validateVariables(updatedComponents.header.content, newVariableType, 'header');
        updatedComponents.header.validationErrors = headerValidation.errors;
        updatedComponents.header.validationWarnings = headerValidation.warnings;
        updatedComponents.header.duplicateVariables = headerValidation.duplicateVariables;
        
        // Update header variable samples to only include valid variables
        const headerVariables = extractVariables(updatedComponents.header.content);
        const validHeaderVariables = getValidVariables(headerVariables, newVariableType);
        const currentHeaderSamples = updatedComponents.header.variableSamples || {};
        const newHeaderSamples = {};
        
        // Keep samples only for valid variables
        validHeaderVariables.forEach(variable => {
          newHeaderSamples[variable] = currentHeaderSamples[variable] || '';
        });
        
        updatedComponents.header.variableSamples = newHeaderSamples;
      }
      
      if (updatedComponents.body.content) {
        const bodyValidation = validateVariables(updatedComponents.body.content, newVariableType, 'body');
        updatedComponents.body.validationErrors = bodyValidation.errors;
        updatedComponents.body.validationWarnings = bodyValidation.warnings;
        updatedComponents.body.duplicateVariables = bodyValidation.duplicateVariables;
        
        // Update variable samples to only include valid variables
        const allVariables = extractVariables(updatedComponents.body.content);
        const validVariables = getValidVariables(allVariables, newVariableType);
        const currentSamples = updatedComponents.body.variableSamples || {};
        const newSamples = {};
        
        // Keep samples only for valid variables
        validVariables.forEach(variable => {
          newSamples[variable] = currentSamples[variable] || '';
        });
        
        updatedComponents.body.variableSamples = newSamples;
      }
      
      if (updatedComponents.footer.content) {
        const footerValidation = validateVariables(updatedComponents.footer.content, newVariableType, 'footer');
        updatedComponents.footer.validationErrors = footerValidation.errors;
        updatedComponents.footer.validationWarnings = footerValidation.warnings;
        updatedComponents.footer.duplicateVariables = footerValidation.duplicateVariables;
      }
      
      return {
        ...prev,
        variableType: newVariableType,
        components: updatedComponents
      };
    });
  };

  // Update variable sample for specific section
  const updateVariableSample = (section, variable, sample) => {
    setTemplateData(prev => ({
      ...prev,
      components: {
        ...prev.components,
        [section]: {
          ...prev.components[section],
          variableSamples: {
            ...prev.components[section].variableSamples,
            [variable]: sample
          }
        }
      }
    }));
  };

  // Legacy function for backwards compatibility (body samples)
  const updateBodyVariableSample = (variable, sample) => {
    updateVariableSample('body', variable, sample);
  };

  // New function for header samples
  const updateHeaderVariableSample = (variable, sample) => {
    updateVariableSample('header', variable, sample);
  };

  const normalizeTemplateCategoryForAi = () => {
    const category = templateData.category || 'MARKETING';
    return String(category).toLowerCase();
  };

  const getDefaultVariableExample = (variable, index, variableType = templateData.variableType) => {
    const lower = String(variable || '').toLowerCase();
    if (variableType === 'number') {
      return index === 0 ? 'Priya' : index === 1 ? '20%' : 'Sample value';
    }
    if (lower.includes('name')) return 'Priya';
    if (lower.includes('discount') || lower.includes('offer')) return '20%';
    if (lower.includes('order')) return 'ORD12345';
    if (lower.includes('date')) return 'May 10, 2026';
    if (lower.includes('amount') || lower.includes('price')) return '₹999';
    return 'Sample value';
  };

  const collectAiVariables = () => {
    const variables = [];
    const seen = new Set();

    const addVariablesFromSection = (sectionName) => {
      const section = templateData.components[sectionName];
      const validVariables = getValidVariables(section.variables || [], templateData.variableType);
      validVariables.forEach((variable) => {
        if (seen.has(variable)) return;
        seen.add(variable);
        variables.push({
          key: variable,
          placeholder: `{{${variable}}}`,
          example: section.variableSamples?.[variable] || getDefaultVariableExample(variable, variables.length)
        });
      });
    };

    addVariablesFromSection('header');
    addVariablesFromSection('body');

    return variables;
  };

  const normalizeAiNamedVariable = (value) => {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/^_+|_+$/g, '');

    return /^[a-z0-9_]*[a-z][a-z0-9_]*$/.test(normalized) ? normalized : '';
  };

  const getFallbackAiVariableName = (index) => {
    const names = [
      'customer_name',
      'offer_name',
      'discount',
      'coupon_code',
      'expiry_date',
      'product_name',
      'order_id',
      'amount',
      'city',
      'event_date'
    ];
    return names[index] || `custom_value_${index + 1}`;
  };

  const buildAiVariables = () => {
    const requestedCount = Math.max(0, Number(aiCopyVariableCount || 0));
    if (requestedCount === 0) return [];

    const existingVariables = collectAiVariables();
    const existingByKey = new Map(existingVariables.map(variable => [variable.key, variable]));
    const variables = [];
    const usedNames = new Set();

    for (let index = 0; index < requestedCount; index += 1) {
      let key;

      if (aiCopyVariableType === 'number') {
        key = String(index + 1);
      } else {
        const enteredName = normalizeAiNamedVariable(aiCopyNamedVariables[index]);
        const fallbackName = getFallbackAiVariableName(index);
        key = enteredName || fallbackName;

        while (usedNames.has(key)) {
          key = `${fallbackName}_${index + 1}`;
        }
        usedNames.add(key);
      }

      variables.push({
        key,
        type: aiCopyVariableType,
        index: index + 1,
        placeholder: `{{${key}}}`,
        example: existingByKey.get(key)?.example || getDefaultVariableExample(key, index, aiCopyVariableType)
      });
    }

    return variables;
  };

  const hasAiDraftShape = (value) => (
    !!value && typeof value === 'object' && (
      value.draft ||
      value.createPayload ||
      value.create_payload ||
      value.templatecontent ||
      value.templatename ||
      Array.isArray(value.component)
    )
  );

  const normalizeAiDraftVariant = (variant, index, parent = {}) => {
    if (!variant || typeof variant !== 'object') return null;

    const directDraft = variant.draft || variant.templateDraft || variant.template || (
      hasAiDraftShape(variant) && !variant.draft && !variant.createPayload ? variant : null
    );
    const directCreatePayload = variant.createPayload || variant.create_payload || variant.payload || null;

    return {
      ...variant,
      id: variant.id || variant.variantId || `ai_variant_${index + 1}`,
      label: variant.label || variant.title || `Variant ${index + 1}`,
      readyForCreate: variant.readyForCreate ?? parent.readyForCreate,
      validation: variant.validation || parent.validation,
      draft: directDraft || variant.draft,
      createPayload: directCreatePayload || variant.createPayload
    };
  };

  const getAiDraftVariants = (entity) => {
    const candidateArrays = [
      Array.isArray(entity) ? entity : null,
      entity?.variants,
      entity?.drafts,
      entity?.templateVariants,
      entity?.generatedVariants,
      entity?.templates,
      entity?.options,
      entity?.results,
      entity?.items,
      entity?.data?.variants,
      entity?.entity?.variants
    ];
    const variantArray = candidateArrays.find(candidate => Array.isArray(candidate) && candidate.length > 0);

    if (variantArray) {
      return variantArray
        .map((variant, index) => normalizeAiDraftVariant(variant, index, entity))
        .filter(Boolean);
    }

    if (hasAiDraftShape(entity)) {
      return [normalizeAiDraftVariant(entity, 0, entity)].filter(Boolean);
    }

    return [];
  };

  const getAiDraftRecommendedIndex = (entity, variants) => {
    const rawIndex = Number(
      entity?.recommendedVariantIndex ??
      entity?.recommendedIndex ??
      entity?.recommended_variant_index ??
      0
    );
    if (!variants.length || Number.isNaN(rawIndex)) return 0;
    return Math.min(Math.max(rawIndex, 0), variants.length - 1);
  };

  const isAiDraftVariantReady = (variant) => {
    const errors = variant?.validation?.errors || [];
    return !!variant && variant.readyForCreate !== false && variant.validation?.isValid !== false && errors.length === 0;
  };

  const getAiDraftVariantDraft = (variant) => variant?.draft || variant?.createPayload || (hasAiDraftShape(variant) ? variant : {});

  const getAiDraftVariantBodyText = (variant) => {
    const draft = getAiDraftVariantDraft(variant);
    const components = Array.isArray(variant?.createPayload?.component)
      ? variant.createPayload.component
      : Array.isArray(draft.component)
        ? draft.component
        : [];
    const bodyComponent = findAiDraftComponent(components, 'BODY');
    return bodyComponent?.text || draft.templatecontent || '';
  };

  const getSelectedAiDraftVariant = () => {
    const variants = getAiDraftVariants(aiDraftResult);
    if (!variants.length) return null;

    const recommendedIndex = getAiDraftRecommendedIndex(aiDraftResult, variants);
    return variants[aiDraftSelectedVariantIndex] || variants[recommendedIndex] || variants[0];
  };

  const getFlowDraftForPreview = () => {
    if (!hasFlowButton) return null;
    return flowDraft || buildFlowDraftFromPreset(flowPresets[0] || FALLBACK_FLOW_PRESETS[0]);
  };

  const getTemplateDataWithFlowPreview = (sourceTemplateData) => {
    const previewDraft = getFlowDraftForPreview();
    if (!previewDraft) return sourceTemplateData;

    return {
      ...sourceTemplateData,
      flowDraft: previewDraft,
      components: {
        ...sourceTemplateData.components,
        buttons: sourceTemplateData.components.buttons.map(button => (
          button.type === 'flow'
            ? { ...button, text: previewDraft.buttonText || button.text || 'Open form' }
            : button
        ))
      }
    };
  };

  const getLivePreviewTemplateData = () => {
    if (!useStepFlow && currentScreen === 'category') {
      const selectedVariant = getSelectedAiDraftVariant();
      if (selectedVariant) {
        return getTemplateDataWithFlowPreview(buildTemplateDataFromAiDraft(selectedVariant));
      }
    }

    return getTemplateDataWithFlowPreview(templateData);
  };

  const getAiVariableInstruction = (variables) => {
    if (!variables.length) {
      return 'Do not include template variables unless the prompt explicitly requires them.';
    }

    const formatLabel = aiCopyVariableType === 'number'
      ? 'numbered placeholders such as {{1}}, {{2}}'
      : 'named placeholders such as {{customer_name}}, {{order_id}}';
    const placeholders = variables.map(variable => variable.placeholder).join(', ');
    const targetLabel = aiCopyTarget === 'header'
      ? 'header'
      : aiCopyTarget === 'body'
        ? 'body'
        : 'header/body copy';

    return `Use exactly ${variables.length} ${aiCopyVariableType} type variable${variables.length === 1 ? '' : 's'} in the ${targetLabel}. Use ${formatLabel}. Required placeholders: ${placeholders}. Do not add extra placeholders. Do not place a variable at the very start or very end of the copy, and do not place variables next to each other.`;
  };

  const openAiCopyGenerator = (target) => {
    const currentVariables = collectAiVariables();
    const nextVariableType = templateData.variableType || selectedVariableType || 'number';
    const defaultVariableCount = target === 'header'
      ? Math.min(currentVariables.length, 1)
      : currentVariables.length;

    setAiCopyTarget(target);
    setAiCopyResult(null);
    setAiCopyVariableType(nextVariableType);
    setAiCopyVariableCount(defaultVariableCount);
    setAiCopyNamedVariables(
      nextVariableType === 'name'
        ? currentVariables.map(variable => normalizeAiNamedVariable(variable.key)).filter(Boolean).slice(0, defaultVariableCount)
        : []
    );
    setAiCopyPrompt(prev => prev || 'Create a clear WhatsApp template message for this business use case.');
    setAiCopyModalOpen(true);
  };

  const handleGenerateAiCopy = async () => {
    if (!aiCopyPrompt.trim()) {
      message.warning('Please enter a prompt for AI generation');
      return;
    }

    if (aiCopyTarget === 'header' && Number(aiCopyVariableCount || 0) > 1) {
      message.warning('WhatsApp text headers support only one variable. Please set the variable count to 0 or 1.');
      return;
    }

    try {
      setAiCopyLoading(true);
      setAiCopyResult(null);

      const variablesForAi = buildAiVariables();
      const variableInstruction = getAiVariableInstruction(variablesForAi);

      const payload = {
        templateCategory: normalizeTemplateCategoryForAi(),
        language: templateData.language || 'en_US',
        prompt: `${aiCopyPrompt.trim()}\n\nVariable instructions: ${variableInstruction}`,
        tone: aiCopyTone || undefined,
        audience: aiCopyAudience || undefined,
        generateHeader: aiCopyTarget === 'header' || aiCopyTarget === 'both',
        generateBody: aiCopyTarget === 'body' || aiCopyTarget === 'both',
        variableType: aiCopyVariableType,
        variableCount: Number(aiCopyVariableCount || 0),
        variables: variablesForAi,
        variants: aiCopyVariantsCount || 1
      };

      const response = await axiosConfig.post('/template/generate-ai-copy', payload);
      const entity = response.data?.entity || response.data?.data || response.data;
      const variants = entity?.variants || [];

      if ((response.data?.status === false || response.data?.success === false) || variants.length === 0) {
        throw new Error(response.data?.message || 'No AI variants were generated');
      }

      setAiCopyResult(entity);
    } catch (error) {
      console.error('Error generating AI template copy:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to generate AI template copy');
    } finally {
      setAiCopyLoading(false);
    }
  };

  const getComponentTextFromVariant = (variant, componentType) => {
    const component = variant.component?.find(item => String(item.type).toUpperCase() === componentType);
    return component?.text || '';
  };

  const mapExampleSamples = (component, variables) => {
    const sampleValues = component?.example?.body_text?.[0] || component?.example?.header_text || [];
    const samples = {};
    variables.forEach((variable, index) => {
      if (sampleValues[index]) {
        samples[variable] = Array.isArray(sampleValues[index]) ? sampleValues[index][0] : sampleValues[index];
      }
    });
    return samples;
  };

  const applyAiVariant = (variant) => {
    const headerText = variant.headerText || getComponentTextFromVariant(variant, 'HEADER');
    const bodyText = variant.bodyText || variant.templatecontent || getComponentTextFromVariant(variant, 'BODY');
    const footerText = variant.footerText || getComponentTextFromVariant(variant, 'FOOTER');
    const bodyComponent = variant.component?.find(item => String(item.type).toUpperCase() === 'BODY');
    const headerComponent = variant.component?.find(item => String(item.type).toUpperCase() === 'HEADER');
    const selectedAiVariableType = aiCopyVariableType || templateData.variableType || 'number';

    setSelectedVariableType(selectedAiVariableType);

    setTemplateData(prev => {
      const nextComponents = { ...prev.components };

      if ((aiCopyTarget === 'header' || aiCopyTarget === 'both') && headerText) {
        const headerVariables = extractVariables(headerText);
        const headerValidation = validateVariables(headerText, selectedAiVariableType, 'header');
        const validHeaderVariables = getValidVariables(headerVariables, selectedAiVariableType);
        const generatedHeaderSamples = mapExampleSamples(headerComponent, validHeaderVariables);
        const headerVariableSamples = {};
        validHeaderVariables.forEach(variable => {
          headerVariableSamples[variable] = generatedHeaderSamples[variable] || prev.components.header.variableSamples?.[variable] || '';
        });

        nextComponents.header = {
          ...prev.components.header,
          type: 'text',
          content: headerText,
          variables: headerVariables,
          variableSamples: headerVariableSamples,
          mediaUrl: null,
          mediaFile: null,
          validationErrors: headerValidation.errors,
          validationWarnings: headerValidation.warnings,
          duplicateVariables: headerValidation.duplicateVariables
        };
      }

      if ((aiCopyTarget === 'body' || aiCopyTarget === 'both') && bodyText) {
        const bodyVariables = extractVariables(bodyText);
        const bodyValidation = validateVariables(bodyText, selectedAiVariableType, 'body');
        const validBodyVariables = getValidVariables(bodyVariables, selectedAiVariableType);
        const generatedBodySamples = mapExampleSamples(bodyComponent, validBodyVariables);
        const bodyVariableSamples = {};
        validBodyVariables.forEach(variable => {
          bodyVariableSamples[variable] = generatedBodySamples[variable] || prev.components.body.variableSamples?.[variable] || '';
        });

        nextComponents.body = {
          ...prev.components.body,
          content: bodyText,
          variables: bodyVariables,
          variableSamples: bodyVariableSamples,
          validationErrors: bodyValidation.errors,
          validationWarnings: bodyValidation.warnings,
          duplicateVariables: bodyValidation.duplicateVariables
        };
        bodyContentInEditor.current = bodyText;
      }

      if (footerText) {
        const footerVariables = extractVariables(footerText);
        const footerValidation = validateVariables(footerText, selectedAiVariableType, 'footer');
        nextComponents.footer = {
          ...prev.components.footer,
          type: 'text',
          content: footerText,
          variables: footerVariables,
          validationErrors: footerValidation.errors,
          validationWarnings: footerValidation.warnings,
          duplicateVariables: footerValidation.duplicateVariables
        };
      }

      return {
        ...prev,
        variableType: selectedAiVariableType,
        components: nextComponents
      };
    });

    validateField('body', bodyText || templateData.components.body.content, false);
    setAiCopyModalOpen(false);
    message.success('AI copy added to the template. Please review before submitting.');
  };

  const getAiDraftEntity = (data) => data?.entity || data?.data?.entity || data?.data || data?.result || data || null;

  const getAiDraftFallbackPrompt = (prompt, index) => {
    const direction = AI_DRAFT_FALLBACK_DIRECTIONS[index % AI_DRAFT_FALLBACK_DIRECTIONS.length];
    return `${prompt}\n\nCreate variant ${index + 1} of ${AI_DRAFT_TARGET_VARIANTS}. ${direction} Keep it Meta compliant and return one complete WhatsApp template draft.`;
  };

  const requestAiDraftEntity = async (prompt, variantCount = AI_DRAFT_TARGET_VARIANTS) => {
    const response = await axiosConfig.post('/template/generate-ai-draft', {
      prompt,
      variants: variantCount
    });
    const statusCode = Number(response.data?.statuscode ?? response.data?.statusCode ?? response.status);

    if (statusCode !== 200 || response.data?.status === false || response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to generate a Meta-safe template draft.');
    }

    return getAiDraftEntity(response.data);
  };

  const mergeAiDraftEntities = (primaryEntity, extraEntities) => {
    const variants = [primaryEntity, ...extraEntities]
      .flatMap(entity => getAiDraftVariants(entity))
      .slice(0, AI_DRAFT_TARGET_VARIANTS)
      .map((variant, index) => ({
        ...variant,
        id: variant.id || `ai_variant_${index + 1}`,
        label: variant.label || `Variant ${index + 1}`
      }));

    return {
      ...primaryEntity,
      variants,
      recommendedVariantIndex: Math.min(
        getAiDraftRecommendedIndex(primaryEntity, variants),
        Math.max(variants.length - 1, 0)
      )
    };
  };

  const normalizeAiDraftCategory = (category) => {
    const normalized = String(category || '').toLowerCase();
    if (normalized.includes('auth')) return 'Authentication';
    if (normalized.includes('util')) return 'UTILITY';
    return 'MARKETING';
  };

  const sanitizeAiTemplateName = (name, prompt) => {
    const fallback = String(prompt || 'ai_generated_template')
      .slice(0, 42)
      .replace(/\{\{[^}]+\}\}/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_');
    const cleaned = String(name || fallback || 'ai_generated_template')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 50);

    return cleaned || `ai_template_${Date.now()}`.slice(0, 50);
  };

  const getAiDraftComponents = (entity) => {
    const draft = entity?.draft || {};
    const createPayload = entity?.createPayload || {};
    return Array.isArray(createPayload.component)
      ? createPayload.component
      : Array.isArray(draft.component)
        ? draft.component
        : [];
  };

  const findAiDraftComponent = (components, type) => (
    components.find(component => String(component?.type || '').toUpperCase() === type)
  );

  const inferAiDraftVariableType = (explicitType, texts) => {
    const normalized = String(explicitType || '').toLowerCase();
    if (normalized === 'name' || normalized === 'number') return normalized;

    const variables = texts.flatMap(text => extractVariables(text || ''));
    if (!variables.length) return 'number';
    return variables.every(variable => /^\d+$/.test(variable)) ? 'number' : 'name';
  };

  const getAiDraftVariableSamples = (component, variables, draftVariables = [], variableType = 'number') => {
    const samples = mapExampleSamples(component, variables);

    draftVariables.forEach(variable => {
      const key = String(variable?.key || variable?.name || '').trim();
      if (key && variables.includes(key) && variable?.example) {
        samples[key] = String(variable.example);
      }
    });

    variables.forEach((variable, index) => {
      if (!samples[variable]) {
        samples[variable] = getDefaultVariableExample(variable, index, variableType);
      }
    });

    return samples;
  };

  const mapAiDraftButtons = (buttonsComponent) => {
    const buttons = Array.isArray(buttonsComponent?.buttons) ? buttonsComponent.buttons : [];
    return buttons.slice(0, 3).map((button, index) => {
      const buttonType = String(button?.type || '').toUpperCase();
      const baseButton = {
        id: `ai-button-${Date.now()}-${index}`,
        text: button?.text || `Button ${index + 1}`
      };

      if (buttonType === 'URL') {
        return {
          ...baseButton,
          type: 'url',
          url: button?.url || ''
        };
      }

      if (buttonType === 'PHONE_NUMBER') {
        return {
          ...baseButton,
          type: 'phone',
          phone: button?.phone_number || button?.phone || ''
        };
      }

      return {
        ...baseButton,
        type: 'quick_reply'
      };
    });
  };

  const buildTemplateDataFromAiDraft = (entity) => {
    const draft = entity?.draft || {};
    const createPayload = entity?.createPayload || {};
    const components = getAiDraftComponents(entity);
    const headerComponent = findAiDraftComponent(components, 'HEADER');
    const bodyComponent = findAiDraftComponent(components, 'BODY');
    const footerComponent = findAiDraftComponent(components, 'FOOTER');
    const buttonsComponent = findAiDraftComponent(components, 'BUTTONS');
    const headerFormat = String(headerComponent?.format || '').toUpperCase();
    const headerText = headerComponent && (!headerFormat || headerFormat === 'TEXT') ? headerComponent.text || '' : '';
    const bodyText = bodyComponent?.text || draft.templatecontent || createPayload.templatecontent || '';
    const footerText = footerComponent?.text || '';
    const aiCategory = normalizeAiDraftCategory(draft.templatecategory || createPayload.templatecategory || templateData.category);
    const variableType = inferAiDraftVariableType(draft.variableType || createPayload.variableType, [headerText, bodyText]);
    const draftVariables = Array.isArray(draft.variables) ? draft.variables : [];
    const headerVariables = extractVariables(headerText);
    const bodyVariables = extractVariables(bodyText);
    const footerVariables = extractVariables(footerText);
    const headerValidation = validateVariables(headerText, variableType, 'header');
    const bodyValidation = validateVariables(bodyText, variableType, 'body');
    const footerValidation = validateVariables(footerText, variableType, 'footer');

    return {
      ...templateData,
      name: sanitizeAiTemplateName(draft.templatename || createPayload.templatename, aiDraftPrompt),
      templateName: sanitizeAiTemplateName(draft.templatename || createPayload.templatename, aiDraftPrompt),
      category: aiCategory,
      language: draft.templatelanguage || createPayload.templatelanguage || 'en_US',
      variableType,
      addressBook: null,
      components: {
        ...templateData.components,
        header: {
          ...templateData.components.header,
          type: headerText ? 'text' : 'none',
          content: headerText,
          variables: headerVariables,
          variableSamples: getAiDraftVariableSamples(headerComponent, getValidVariables(headerVariables, variableType), draftVariables, variableType),
          mediaUrl: null,
          mediaFile: null,
          validationErrors: headerValidation.errors,
          validationWarnings: headerValidation.warnings,
          duplicateVariables: headerValidation.duplicateVariables
        },
        body: {
          ...templateData.components.body,
          content: bodyText,
          variables: bodyVariables,
          variableSamples: getAiDraftVariableSamples(bodyComponent, getValidVariables(bodyVariables, variableType), draftVariables, variableType),
          validationErrors: bodyValidation.errors,
          validationWarnings: bodyValidation.warnings,
          duplicateVariables: bodyValidation.duplicateVariables
        },
        footer: {
          ...templateData.components.footer,
          type: footerText ? 'text' : 'none',
          content: footerText,
          variables: footerVariables,
          validationErrors: footerValidation.errors,
          validationWarnings: footerValidation.warnings,
          duplicateVariables: footerValidation.duplicateVariables
        },
        buttons: mapAiDraftButtons(buttonsComponent)
      },
      authentication: aiCategory === 'Authentication'
        ? {
            ...templateData.authentication,
            codeDelivery: 'copy-code',
            zeroTapAgreement: false,
            apps: [{ id: 'app-1', packageName: '', signatureHash: '' }]
          }
        : templateData.authentication
    };
  };

  const getAiDraftLocalValidationErrors = (draftTemplateData) => {
    const errors = [];
    const isAuthentication = draftTemplateData.category === 'Authentication';

    if (!draftTemplateData.templateName) errors.push('Template name is required');
    if (!draftTemplateData.category) errors.push('Category is required');

    if (!isAuthentication) {
      const header = draftTemplateData.components.header;
      const body = draftTemplateData.components.body;
      const footer = draftTemplateData.components.footer;

      if (!body.content?.trim()) {
        errors.push('Body content is required');
      }

      if (header.type === 'text' && header.content) {
        const headerValidation = validateVariables(header.content, draftTemplateData.variableType, 'header');
        errors.push(...headerValidation.errors);
        getValidVariables(header.variables || [], draftTemplateData.variableType).forEach(variable => {
          if (!header.variableSamples?.[variable]?.trim()) {
            errors.push(`Example content is required for header variable {{${variable}}}.`);
          }
        });
      }

      if (body.content) {
        const bodyValidation = validateVariables(body.content, draftTemplateData.variableType, 'body');
        errors.push(...bodyValidation.errors);
        getValidVariables(body.variables || [], draftTemplateData.variableType).forEach(variable => {
          if (!body.variableSamples?.[variable]?.trim()) {
            errors.push(`Example content is required for variable {{${variable}}}.`);
          }
        });
      }

      if (footer.content) {
        const footerValidation = validateVariables(footer.content, draftTemplateData.variableType, 'footer');
        errors.push(...footerValidation.errors);
      }
    }

    return [...new Set(errors)];
  };

  const handleGenerateAiDraft = async () => {
    const prompt = aiDraftPrompt.trim();

    if (!prompt) {
      message.warning('Tell AI what template you want to create.');
      return;
    }

    try {
      setAiDraftLoading(true);
      setAiDraftResult(null);
      setAiDraftError('');
      setValidationErrors([]);

      const entity = await requestAiDraftEntity(prompt, AI_DRAFT_TARGET_VARIANTS);
      let variants = getAiDraftVariants(entity);

      if (!entity || variants.length === 0) {
        throw new Error('AI response did not include template variants. Please try again.');
      }

      let resultEntity = entity;

      if (variants.length < AI_DRAFT_TARGET_VARIANTS) {
        const missingCount = AI_DRAFT_TARGET_VARIANTS - variants.length;
        const fallbackRequests = Array.from({ length: missingCount }, (_, offset) => {
          const variantIndex = variants.length + offset;
          return requestAiDraftEntity(getAiDraftFallbackPrompt(prompt, variantIndex), 1)
            .catch((fallbackError) => {
              console.warn('Additional AI draft variant failed:', fallbackError);
              return null;
            });
        });
        const fallbackEntities = (await Promise.all(fallbackRequests)).filter(Boolean);
        resultEntity = mergeAiDraftEntities(entity, fallbackEntities);
        variants = getAiDraftVariants(resultEntity);
      }

      const recommendedIndex = getAiDraftRecommendedIndex(resultEntity, variants);
      const recommendedVariant = variants[recommendedIndex];
      const backendErrors = recommendedVariant?.validation?.errors || resultEntity?.validation?.errors || [];
      const backendWarnings = recommendedVariant?.validation?.warnings || resultEntity?.validation?.warnings || [];

      setAiDraftResult(resultEntity);
      setAiDraftSelectedVariantIndex(recommendedIndex);

      if (!isAiDraftVariantReady(recommendedVariant)) {
        setAiDraftError(backendErrors[0] || backendWarnings[0] || 'The recommended AI draft needs review. Choose another ready variant or refine the prompt.');
      }

      message.success(`AI generated ${variants.length} template draft${variants.length === 1 ? '' : 's'}. Choose one to review in the builder.`);
    } catch (error) {
      console.error('Error generating AI template draft:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate AI template draft';
      setAiDraftError(errorMessage);
      message.error(errorMessage);
    } finally {
      setAiDraftLoading(false);
    }
  };

  const applyAiDraftVariantToBuilder = (variant) => {
    const selectedVariant = variant || getAiDraftVariants(aiDraftResult)[aiDraftSelectedVariantIndex] || aiDraftResult;
    const backendErrors = selectedVariant?.validation?.errors || [];
    const backendWarnings = selectedVariant?.validation?.warnings || [];

    if (!isAiDraftVariantReady(selectedVariant)) {
      const reviewMessage = backendErrors[0] || backendWarnings[0] || 'This AI draft is not ready for Meta submission yet.';
      setAiDraftError(reviewMessage);
      message.warning(reviewMessage);
      return;
    }

    const nextTemplateData = buildTemplateDataFromAiDraft(selectedVariant);
    const localErrors = getAiDraftLocalValidationErrors(nextTemplateData);

    if (localErrors.length > 0) {
      setValidationErrors(localErrors);
      setAiDraftError(localErrors[0]);
      message.error('AI draft failed frontend Meta validation. Please refine the prompt and try again.');
      return;
    }

    setTemplateData(nextTemplateData);
    setSelectedVariableType(nextTemplateData.variableType);
    setFieldErrors({});
    setTouchedFields({});
    touchedFieldsRef.current = {};
    form.setFieldsValue({
      name: nextTemplateData.templateName,
      language: nextTemplateData.language
    });
    bodyContentInEditor.current = nextTemplateData.components.body.content || '';

    if (nextTemplateData.category === 'Authentication') {
      setUseStepFlow(true);
      setCurrentStep(2);
    } else {
      setUseStepFlow(false);
      setCurrentScreen('preview');
    }

    setAiDraftError('');
    message.success('AI draft copied into the builder. Review and edit before submitting to Meta.');
    scrollToTop();
  };

  // Generate preview content with sample values and validation highlighting
  const getPreviewContent = (content, variables, samples) => {
    if (!content) return 'Your message content will appear here...';
    
    let previewContent = content;
    
    // Replace variables with sample values if available
    variables.forEach(variable => {
      const sample = samples?.[variable];
      if (sample) {
        const variablePattern = new RegExp(`\\{\\{${variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}\\}`, 'g');
        previewContent = previewContent.replace(variablePattern, sample);
      }
    });
    
    return previewContent;
  };

  // ── WhatsApp markdown ↔ HTML converters for the body rich editor ──

  const markdownToDisplayHtml = (text) => {
    if (!text) return '';
    // Escape HTML entities first
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Apply WhatsApp formatting markers
    html = html
      .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
      .replace(/_([^_\n]+)_/g, '<em>$1</em>')
      .replace(/~([^~\n]+)~/g, '<s>$1</s>')
      .replace(/`([^`\n]+)`/g, '<code style="font-family:monospace;background:rgba(0,0,0,0.06);padding:0 2px;border-radius:3px">$1</code>')
      .replace(/\n/g, '<br>');
    return html;
  };

  const stripListItemMarkup = (fragment) => fragment
    .replace(/<strong>([\s\S]*?)<\/strong>/gi, '*$1*')
    .replace(/<b>([\s\S]*?)<\/b>/gi, '*$1*')
    .replace(/<em>([\s\S]*?)<\/em>/gi, '_$1_')
    .replace(/<i>([\s\S]*?)<\/i>/gi, '_$1_')
    .replace(/<s>([\s\S]*?)<\/s>/gi, '~$1~')
    .replace(/<del>([\s\S]*?)<\/del>/gi, '~$1~')
    .replace(/<strike>([\s\S]*?)<\/strike>/gi, '~$1~')
    .replace(/<code[^>]*>([\s\S]*?)<\/code>/gi, '`$1`')
    .replace(/<[^>]+>/g, '');

  const convertListMarkupToMarkdown = (html, prefix) => {
    const items = [];
    html.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (_, item) => {
      const text = stripListItemMarkup(item).trim();
      if (text) {
        items.push(`${prefix}${text}`);
      }
      return '';
    });
    return items.join('\n');
  };

  const displayHtmlToMarkdown = (html) => {
    if (!html) return '';
    return html
      .replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner) => convertListMarkupToMarkdown(inner, '• '))
      .replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner) => {
        const items = [];
        let index = 1;
        inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (_, item) => {
          const text = stripListItemMarkup(item).trim();
          if (text) {
            items.push(`${index++}. ${text}`);
          }
          return '';
        });
        return items.join('\n');
      })
      .replace(/<strong>([\s\S]*?)<\/strong>/gi, '*$1*')
      .replace(/<b>([\s\S]*?)<\/b>/gi, '*$1*')
      .replace(/<em>([\s\S]*?)<\/em>/gi, '_$1_')
      .replace(/<i>([\s\S]*?)<\/i>/gi, '_$1_')
      .replace(/<s>([\s\S]*?)<\/s>/gi, '~$1~')
      .replace(/<del>([\s\S]*?)<\/del>/gi, '~$1~')
      .replace(/<strike>([\s\S]*?)<\/strike>/gi, '~$1~')
      .replace(/<code[^>]*>([\s\S]*?)<\/code>/gi, '`$1`')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<div>/gi, '\n')
      .replace(/<\/div>/gi, '')
      .replace(/<p>/gi, '')
      .replace(/<\/p>/gi, '\n')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/<[^>]+>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/^\n+|\n+$/g, '');
  };

  // Sync external state changes (variable insert, emoji) → contentEditable
  useEffect(() => {
    const editor = bodyEditorRef.current;
    if (!editor) return;
    const newContent = templateData.components.body.content || '';
    const editorHasText = (editor.textContent || '').trim().length > 0;
    const shouldSync = bodyContentInEditor.current !== newContent || (!editorHasText && newContent);
    if (shouldSync) {
      bodyContentInEditor.current = newContent;
      const newHtml = markdownToDisplayHtml(newContent);
      editor.innerHTML = newHtml;
      // Move cursor to end
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateData.components.body.content, currentScreen]);

  // Render plain text with WhatsApp markdown formatting applied
  const renderWhatsAppFormattedText = (text, keyPrefix = '') => {
    if (!text) return null;

    const markerStyles = {
      '*': { type: 'strong', style: { fontWeight: 700 } },
      '_': { type: 'em', style: { fontStyle: 'italic' } },
      '~': { type: 's', style: null },
      '`': {
        type: 'code',
        style: { fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', padding: '0 2px', borderRadius: 3 }
      }
    };

    const pushPlainText = (buffer, parts, prefix) => {
      if (!buffer) return;
      const segments = buffer.split('\n');
      segments.forEach((segment, index) => {
        if (index > 0) {
          parts.push(<br key={`${prefix}br-${index}-${parts.length}`} />);
        }
        if (segment) {
          parts.push(segment);
        }
      });
    };

    const parseInline = (source, prefix) => {
      const parts = [];
      let buffer = '';
      let index = 0;

      const flushBuffer = () => {
        if (!buffer) return;
        pushPlainText(buffer, parts, `${prefix}${parts.length}`);
        buffer = '';
      };

      while (index < source.length) {
        const char = source[index];
        const markerInfo = markerStyles[char];

        if (!markerInfo) {
          buffer += char;
          index += 1;
          continue;
        }

        const closingIndex = source.indexOf(char, index + 1);
        if (closingIndex === -1) {
          buffer += char;
          index += 1;
          continue;
        }

        flushBuffer();

        const inner = source.slice(index + 1, closingIndex);
        const innerChildren = parseInline(inner, `${prefix}${parts.length}-`);
        const elementKey = `${prefix}${parts.length}-${index}`;

        if (markerInfo.type === 'strong') {
          parts.push(<strong key={elementKey} style={markerInfo.style}>{innerChildren}</strong>);
        } else if (markerInfo.type === 'em') {
          parts.push(<em key={elementKey} style={markerInfo.style}>{innerChildren}</em>);
        } else if (markerInfo.type === 's') {
          parts.push(<s key={elementKey}>{innerChildren}</s>);
        } else if (markerInfo.type === 'code') {
          parts.push(<code key={elementKey} style={markerInfo.style}>{inner}</code>);
        }

        index = closingIndex + 1;
      }

      flushBuffer();
      return parts;
    };

    const renderParagraphLine = (line, lineKey) => {
      const content = parseInline(line, `${keyPrefix}${lineKey}-`);
      return content.length > 0 ? content : line;
    };

    const lines = text.split('\n');
    const blocks = [];
    let lineIndex = 0;

    while (lineIndex < lines.length) {
      const line = lines[lineIndex];
      const bulletMatch = line.match(/^\s*[•*-]\s+(.*)$/);
      const orderedMatch = line.match(/^\s*(\d+)\.\s+(.*)$/);

      if (bulletMatch || orderedMatch) {
        const isOrdered = !!orderedMatch;
        const listItems = [];

        while (lineIndex < lines.length) {
          const currentLine = lines[lineIndex];
          const currentBullet = currentLine.match(/^\s*[•*-]\s+(.*)$/);
          const currentOrdered = currentLine.match(/^\s*(\d+)\.\s+(.*)$/);

          if (isOrdered) {
            if (!currentOrdered) break;
            listItems.push({ text: currentOrdered[2], number: currentOrdered[1] });
          } else {
            if (!currentBullet) break;
            listItems.push({ text: currentBullet[1] });
          }

          lineIndex += 1;
        }

        blocks.push(
          <div key={`${keyPrefix}list-${lineIndex}`} style={{ margin: '2px 0' }}>
            {listItems.map((item, itemIndex) => (
              <div key={`${keyPrefix}li-${lineIndex}-${itemIndex}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <span style={{ flexShrink: 0, minWidth: isOrdered ? 20 : 12 }}>
                  {isOrdered ? `${item.number}.` : '•'}
                </span>
                <span>{renderParagraphLine(item.text, `${lineIndex}-${itemIndex}`)}</span>
              </div>
            ))}
          </div>
        );

        continue;
      }

      blocks.push(
        <React.Fragment key={`${keyPrefix}line-${lineIndex}`}>
          {renderParagraphLine(line, lineIndex)}
          {lineIndex < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      );

      lineIndex += 1;
    }

    return blocks.length > 0 ? blocks : text;
  };

  // Generate preview content with variable validation highlighting for React rendering
  const getPreviewContentWithValidation = (content, variables, samples, variableType, section = 'body') => {
    if (!content) return 'Your message content will appear here...';
    
    // Get validation info including duplicate variables
    const validation = validateVariables(content, variableType, section);
    const duplicateVariables = validation.duplicateVariables || [];
    const hasPlacementError = validation.errors?.some(error =>
      error.includes("can't start") ||
      error.includes("can't end") ||
      error.includes("adjacent variables") ||
      error.includes("only variable placeholders")
    );
    
    // Track which variables we've already shown samples for
    const variableFirstOccurrence = {};
    
    // Split content by variables to create array of text and variable segments
    const parts = [];
    let lastIndex = 0;
    const variableRegex = /\{\{([^}]+)\}\}/g;
    let match;
    
    while ((match = variableRegex.exec(content)) !== null) {
      const [fullMatch, variable] = match;
      const startIndex = match.index;
      
      // Add text before variable
      if (startIndex > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, startIndex)
        });
      }
      
      // Check if variable is valid for format
      const isValidFormat = getValidVariables([variable], variableType).length > 0;
      
      // Check if this is the first occurrence of this variable
      const isFirstOccurrence = !variableFirstOccurrence.hasOwnProperty(variable);
      if (isFirstOccurrence) {
        variableFirstOccurrence[variable] = true;
      }
      
      // Check if this is a duplicate variable (appears more than once in content)
      const isDuplicate = duplicateVariables.includes(variable);
      
      // Get sample value
      const sample = samples?.[variable];
      
      // Determine what content to show and styling
      let displayContent;
      let isValid;
      let tooltipMessage;
      
      if (section === 'footer') {
        displayContent = fullMatch;
        isValid = false;
        tooltipMessage = `Invalid footer variable: Meta footer components support static text only`;
      } else if (!isValidFormat) {
        // Invalid format - always show as invalid regardless of occurrence
        displayContent = fullMatch;
        isValid = false;
        tooltipMessage = `Invalid variable: {{${variable}}} - doesn't match ${variableType} format`;
      } else if (hasPlacementError) {
        displayContent = fullMatch;
        isValid = false;
        tooltipMessage = `Meta validation issue: variables must have fixed text around them and cannot be adjacent`;
      } else if (isDuplicate && !isFirstOccurrence) {
        // This is a duplicate occurrence - show as red even if there's a sample
        displayContent = fullMatch;
        isValid = false;
        tooltipMessage = `Duplicate variable: {{${variable}}} - this variable appears multiple times which is not allowed`;
      } else if (isFirstOccurrence && sample) {
        // First occurrence with sample - show the sample value
        displayContent = sample;
        isValid = true;
      } else if (isFirstOccurrence && !sample) {
        // First occurrence without sample - show as missing sample
        displayContent = fullMatch;
        isValid = 'missing-sample';
        tooltipMessage = `Missing sample: {{${variable}}} - please provide example content`;
      } else {
        // Fallback
        displayContent = fullMatch;
        isValid = false;
      }
      
      // Add variable part
      parts.push({
        type: 'variable',
        variable: variable,
        content: displayContent,
        isValid: isValid,
        tooltipMessage: tooltipMessage
      });
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }
    
    // Return JSX elements
    return parts.map((part, index) => {
      if (part.type === 'text') {
        return <React.Fragment key={index}>{renderWhatsAppFormattedText(part.content, `p${index}`)}</React.Fragment>;
      } else {
        // Variable part with validation styling
        if (part.isValid === false) {
          return (
            <span 
              key={index}
              style={{ 
                color: '#f66e70ff',
                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                padding: '1px 3px',
                borderRadius: '3px',
                fontWeight: 'bold'
              }}
              title={part.tooltipMessage}
            >
              {part.content}
            </span>
          );
        } else if (part.isValid === 'missing-sample') {
          return (
            <span 
              key={index}
              style={{ 
                color: '#faad14',
                backgroundColor: 'rgba(250, 173, 20, 0.1)',
                padding: '1px 3px',
                borderRadius: '3px',
                fontWeight: 'bold'
              }}
              title={part.tooltipMessage}
            >
              {part.content}
            </span>
          );
        } else {
          // Valid variable with sample
          return part.content;
        }
      }
    });
  };

  // Add button
  const addButton = (type) => {
    if (type === 'flow' && templateData.components.buttons.some(button => button.type === 'flow')) {
      message.warning('Only one Flow button is allowed per template');
      return;
    }

    if (templateData.components.buttons.length >= 3) {
      message.warning('Maximum 3 buttons allowed');
      return;
    }

    const newButton = {
      id: Date.now(),
      type,
      text: '',
      url: type === 'url' ? '' : undefined,
      phone: type === 'phone' ? '' : undefined
    };

    if (type === 'flow') {
      const preset = flowPresets[0] || FALLBACK_FLOW_PRESETS[0];
      const nextDraft = flowDraft || buildFlowDraftFromPreset(preset);
      newButton.text = nextDraft.buttonText;
      setFlowDraft(nextDraft);
      setFlowPreviewResult(null);
      setFlowCreateResult(null);
      setFlowErrors([]);
      if (!flowPresetsLoaded) {
        fetchFlowPresets();
      }
    }

    setTemplateData(prev => ({
      ...prev,
      components: {
        ...prev.components,
        buttons: [...prev.components.buttons, newButton]
      }
    }));
  };

  // Remove button
  const removeButton = (buttonId) => {
    const removedButton = templateData.components.buttons.find(btn => btn.id === buttonId);
    setTemplateData(prev => ({
      ...prev,
      components: {
        ...prev.components,
        buttons: prev.components.buttons.filter(btn => btn.id !== buttonId)
      }
    }));
    if (removedButton?.type === 'flow') {
      setFlowPreviewResult(null);
      setFlowCreateResult(null);
      setFlowErrors([]);
    }
    setButtonUrlErrors(prev => { const next = { ...prev }; delete next[buttonId]; return next; });
  };

  // Validate a URL value — returns error string or ''
  const validateUrl = (value) => {
    if (!value || !value.trim()) return ''; // optional field
    if (!/^(https?:\/\/|www\.)/i.test(value)) return 'URL must start with http://, https://, or www.';
    try {
      const urlToTest = /^www\./i.test(value) ? 'https://' + value : value;
      const url = new URL(urlToTest);
      if (!url.hostname || url.hostname.indexOf('.') === -1) return 'Enter a valid URL (e.g. https://example.com or www.example.com)';
    } catch {
      return 'Enter a valid URL (e.g. https://example.com or www.example.com)';
    }
    return '';
  };

  // Update button
  const updateButton = (buttonId, field, value) => {
    if (field === 'type' && value === 'flow') {
      const hasAnotherFlowButton = templateData.components.buttons.some(btn => btn.type === 'flow' && btn.id !== buttonId);
      if (hasAnotherFlowButton) {
        message.warning('Only one Flow button is allowed per template');
        return;
      }

      const preset = flowPresets[0] || FALLBACK_FLOW_PRESETS[0];
      const nextDraft = flowDraft || buildFlowDraftFromPreset(preset);
      setFlowDraft(nextDraft);
      setFlowPreviewResult(null);
      setFlowCreateResult(null);
      setFlowErrors([]);
      if (!flowPresetsLoaded) {
        fetchFlowPresets();
      }
    }

    if (field === 'text' && templateData.components.buttons.some(btn => btn.id === buttonId && btn.type === 'flow')) {
      updateFlowDraft({ buttonText: value });
    }

    setTemplateData(prev => ({
      ...prev,
      components: {
        ...prev.components,
        buttons: prev.components.buttons.map(btn =>
          btn.id === buttonId ? (() => {
            const nextButton = { ...btn, [field]: value };
            if (field === 'type') {
              nextButton.url = value === 'url' ? (btn.url || '') : undefined;
              nextButton.phone = value === 'phone' ? (btn.phone || '') : undefined;
              if (value === 'flow') {
                nextButton.text = flowDraft?.buttonText || nextButton.text || 'Open form';
              }
            }
            return nextButton;
          })() : btn
        )
      }
    }));
    // Clear URL error while typing so user sees live feedback
    if (field === 'url') {
      const err = validateUrl(value);
      setButtonUrlErrors(prev => ({ ...prev, [buttonId]: err }));
    }
  };

  // Validate template
  const validateTemplate = () => {
    const errors = [];
    const isAuthentication = templateData.category === 'Authentication';
    
    if (!templateData.templateName) errors.push('Template name is required');
    if (!templateData.category) errors.push('Category is required');
    
    // For non-authentication templates, body content is required
    // For authentication templates, body content is automatically generated
    if (!isAuthentication && (!templateData.components.body.content || !templateData.components.body.content.trim())) {
      errors.push('Body content is required');
    }

    // Validate header if it has text content and variables
    if (!isAuthentication && templateData.components.header.type === 'text' && templateData.components.header.content) {
      const headerValidation = validateVariables(templateData.components.header.content, templateData.variableType, 'header');
      if (!headerValidation.isValid) {
        errors.push(...headerValidation.errors);
      }
      
      // Check if samples are provided for all VALID header variables (MANDATORY)
      const headerVariables = templateData.components.header.variables || [];
      const validHeaderVariables = getValidVariables(headerVariables, templateData.variableType);
      const headerSamples = templateData.components.header.variableSamples || {};
      
      validHeaderVariables.forEach(variable => {
        if (!headerSamples[variable] || headerSamples[variable].trim() === '') {
          errors.push(`Example content is required for header variable {{${variable}}}. Please provide a sample value.`);
        }
      });
      
      // Additional validation: Check if header examples are valid
      validHeaderVariables.forEach(variable => {
        const sample = headerSamples[variable];
        if (sample && sample.trim() !== '') {
          if (sample.length < 2) {
            errors.push(`Example for header variable {{${variable}}} should be at least 2 characters long.`);
          }
        }
      });
    }

    // Validate variable formatting based on Meta guidelines
    if (!isAuthentication && templateData.components.body.content) {
      const validation = validateVariables(templateData.components.body.content, templateData.variableType, 'body');
      if (!validation.isValid) {
        errors.push(...validation.errors);
      }
      
      // Check if samples are provided for all VALID variables (MANDATORY)
      const bodyVariables = templateData.components.body.variables || [];
      const validVariables = getValidVariables(bodyVariables, templateData.variableType);
      const bodySamples = templateData.components.body.variableSamples || {};
      
      validVariables.forEach(variable => {
        if (!bodySamples[variable] || bodySamples[variable].trim() === '') {
          errors.push(`Example content is required for variable {{${variable}}}. Please provide a sample value.`);
        }
      });
      
      // Additional validation: Check if examples are valid based on variable type (only for valid variables)
      validVariables.forEach(variable => {
        const sample = bodySamples[variable];
        if (sample && sample.trim() !== '') {
          if (templateData.variableType === 'number') {
            // For number variables, check if sample is meaningful (not just random text)
            if (sample.length < 2) {
              errors.push(`Example for variable {{${variable}}} should be at least 2 characters long.`);
            }
          } else if (templateData.variableType === 'name') {
            // For name variables, check if sample follows naming conventions
            if (sample.length < 2) {
              errors.push(`Example for variable {{${variable}}} should be at least 2 characters long.`);
            }
          }
        }
      });
    }

    // Validate footer based on Meta component rules: max 60 chars, static text only
    if (!isAuthentication && templateData.components.footer.content) {
      const footerValidation = validateVariables(templateData.components.footer.content, templateData.variableType, 'footer');
      if (!footerValidation.isValid) {
        errors.push(...footerValidation.errors);
      }
    }
    
    // Validate buttons (only for non-authentication templates since auth templates have predefined buttons)
    if (!isAuthentication) {
      const urlErrs = {};
      const flowButtons = templateData.components.buttons.filter(btn => btn.type === 'flow');
      if (flowButtons.length > 1) {
        errors.push('Only one Flow button is allowed per template');
      }
      templateData.components.buttons.forEach((btn, index) => {
        if (!btn.text) errors.push(`Button ${index + 1} text is required`);
        if (btn.type === 'url') {
          const urlErr = validateUrl(btn.url);
          if (urlErr) {
            errors.push(`Button ${index + 1}: ${urlErr}`);
            urlErrs[btn.id] = urlErr;
          }
        }
        if (btn.type === 'phone' && !btn.phone) errors.push(`Button ${index + 1} phone number is required`);
        if (btn.type === 'flow') {
          const flowValidationErrors = validateFlowDraft(flowDraft);
          if (flowValidationErrors.length > 0) {
            errors.push(...flowValidationErrors);
          }
          if (!getFlowButtonComponentForSubmit()) {
            errors.push('Create the WhatsApp Flow in Meta before submitting the template.');
          }
        }
      });
      if (Object.keys(urlErrs).length > 0) {
        setButtonUrlErrors(prev => ({ ...prev, ...urlErrs }));
      }
    }

    // Validate authentication template specific fields
    if (isAuthentication) {
      // Validate apps only if code delivery is not 'copy-code'
      if (templateData.authentication.codeDelivery !== 'copy-code') {
        if (!templateData.authentication.apps || templateData.authentication.apps.length === 0) {
          errors.push('At least one app configuration is required for autofill authentication');
        } else {
          // Validate each app
          templateData.authentication.apps.forEach((app, index) => {
            const appNumber = index + 1;
            
            // Check if fields are empty
            if (!app.packageName || !app.packageName.trim()) {
              errors.push(`App ${appNumber}: Package name is required`);
            }
            if (!app.signatureHash || !app.signatureHash.trim()) {
              errors.push(`App ${appNumber}: App signature hash is required`);
            }
            
            // Validate package name format if provided
            if (app.packageName && app.packageName.trim()) {
              const packageValidation = validatePackageName(app.packageName);
              if (!packageValidation.isValid) {
                errors.push(`App ${appNumber}: ${packageValidation.error}`);
              }
            }
            
            // Validate signature hash format if provided
            if (app.signatureHash && app.signatureHash.trim()) {
              const hashValidation = validateSignatureHash(app.signatureHash);
              if (!hashValidation.isValid) {
                errors.push(`App ${appNumber}: ${hashValidation.error}`);
              }
            }
          });
        }
      }
      
      // Validate zero-tap agreement checkbox
      if (templateData.authentication.codeDelivery === 'zero-tap') {
        if (!templateData.authentication.zeroTapAgreement) {
          errors.push('You must agree to the WhatsApp Business Terms of Service to use zero-tap authentication');
        }
      }
    }

    return errors;
  };

  // Validate current step before proceeding to next
  const validateCurrentStep = (step) => {
    const errors = [];
    const isAuthentication = templateData.category === 'Authentication';
    
    if (step === 0) {
      // Step 1: Template Info validation
      if (!templateData.templateName) errors.push('Template name is required');
      if (!templateData.category) errors.push('Category is required');
    } else if (step === 1) {
      // Step 2: Build Template validation
      if (!isAuthentication && (!templateData.components.body.content || !templateData.components.body.content.trim())) {
        errors.push('Body content is required');
      }

      // Validate header if it has text content and variables
      if (!isAuthentication && templateData.components.header.type === 'text' && templateData.components.header.content) {
        const headerValidation = validateVariables(templateData.components.header.content, templateData.variableType, 'header');
        if (!headerValidation.isValid) {
          errors.push(...headerValidation.errors);
        }
        
        // Check if samples are provided for all VALID header variables
        const headerVariables = templateData.components.header.variables || [];
        const validHeaderVariables = getValidVariables(headerVariables, templateData.variableType);
        const headerSamples = templateData.components.header.variableSamples || {};
        
        validHeaderVariables.forEach(variable => {
          if (!headerSamples[variable] || headerSamples[variable].trim() === '') {
            errors.push(`Example content is required for header variable {{${variable}}}. Please provide a sample value.`);
          }
        });
        
        // Additional validation: Check if header examples are valid
        validHeaderVariables.forEach(variable => {
          const sample = headerSamples[variable];
          if (sample && sample.trim() !== '') {
            if (sample.length < 2) {
              errors.push(`Example for header variable {{${variable}}} should be at least 2 characters long.`);
            }
          }
        });
      }

      // Validate body variables
      if (!isAuthentication && templateData.components.body.content) {
        const validation = validateVariables(templateData.components.body.content, templateData.variableType, 'body');
        if (!validation.isValid) {
          errors.push(...validation.errors);
        }
        
        // Check if samples are provided for all VALID variables
        const bodyVariables = templateData.components.body.variables || [];
        const validVariables = getValidVariables(bodyVariables, templateData.variableType);
        const bodySamples = templateData.components.body.variableSamples || {};
        
        validVariables.forEach(variable => {
          if (!bodySamples[variable] || bodySamples[variable].trim() === '') {
            errors.push(`Example content is required for variable {{${variable}}}. Please provide a sample value.`);
          }
        });
        
        // Additional validation: Check if examples are valid based on variable type
        validVariables.forEach(variable => {
          const sample = bodySamples[variable];
          if (sample && sample.trim() !== '') {
            if (templateData.variableType === 'number') {
              if (sample.length < 2) {
                errors.push(`Example for variable {{${variable}}} should be at least 2 characters long.`);
              }
            } else if (templateData.variableType === 'name') {
              if (sample.length < 2) {
                errors.push(`Example for variable {{${variable}}} should be at least 2 characters long.`);
              }
            }
          }
        });
      }
      
      // Validate buttons (only for non-authentication templates)
      if (!isAuthentication) {
        const flowButtons = templateData.components.buttons.filter(btn => btn.type === 'flow');
        if (flowButtons.length > 1) {
          errors.push('Only one Flow button is allowed per template');
        }
        templateData.components.buttons.forEach((btn, index) => {
          if (!btn.text) errors.push(`Button ${index + 1} text is required`);
          if (btn.type === 'url' && !btn.url) errors.push(`Button ${index + 1} URL is required`);
          if (btn.type === 'phone' && !btn.phone) errors.push(`Button ${index + 1} phone number is required`);
          if (btn.type === 'flow') {
            errors.push(...validateFlowDraft(flowDraft));
            if (!getFlowButtonComponentForSubmit()) {
              errors.push('Create the WhatsApp Flow in Meta before submitting the template.');
            }
          }
        });
      }

      // Validate authentication template specific fields
      if (isAuthentication) {
        // Validate apps only if code delivery is not 'copy-code'
        if (templateData.authentication.codeDelivery !== 'copy-code') {
          if (!templateData.authentication.apps || templateData.authentication.apps.length === 0) {
            errors.push('At least one app configuration is required for autofill authentication');
          } else {
            // Validate each app
            templateData.authentication.apps.forEach((app, index) => {
              const appNumber = index + 1;
              
              // Check if fields are empty
              if (!app.packageName || !app.packageName.trim()) {
                errors.push(`App ${appNumber}: Package name is required`);
              }
              if (!app.signatureHash || !app.signatureHash.trim()) {
                errors.push(`App ${appNumber}: App signature hash is required`);
              }
              
              // Validate package name format if provided
              if (app.packageName && app.packageName.trim()) {
                const packageValidation = validatePackageName(app.packageName);
                if (!packageValidation.isValid) {
                  errors.push(`App ${appNumber}: ${packageValidation.error}`);
                }
              }
              
              // Validate signature hash format if provided
              if (app.signatureHash && app.signatureHash.trim()) {
                const hashValidation = validateSignatureHash(app.signatureHash);
                if (!hashValidation.isValid) {
                  errors.push(`App ${appNumber}: ${hashValidation.error}`);
                }
              }
            });
          }
        }
        
        // Validate zero-tap agreement checkbox
        if (templateData.authentication.codeDelivery === 'zero-tap') {
          if (!templateData.authentication.zeroTapAgreement) {
            errors.push('You must agree to the WhatsApp Business Terms of Service to use zero-tap authentication');
          }
        }
      }
    }
    
    return errors;
  };

  // Submit template for approval
  const submitTemplate = () => {
    const errors = validateTemplate();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);

    Modal.confirm({
      title: null,
      icon: null,
      width: 440,
      className: 'bc-confirm-modal',
      content: (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', border: '3px solid #bfdbfe'
          }}>
            <CheckCircleOutlined style={{ fontSize: 28, color: '#0036b0' }} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>
            Submit Template for Approval?
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.5 }}>
            Your template will be submitted to WhatsApp for approval. This process typically takes 24-48 hours.
          </div>
          <div style={{
            background: '#f8fafc', borderRadius: 10, padding: '14px 16px',
            border: '1px solid #e2e8f0', textAlign: 'left'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Template</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{templateData.templateName || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Category</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{templateData.category || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Language</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{templateData.language || '—'}</span>
            </div>
          </div>
        </div>
      ),
      okText: 'Submit for Approval',
      cancelText: 'Cancel',
      okButtonProps: {
        style: {
          background: '#0036b0', borderColor: '#0036b0', borderRadius: 8,
          height: 40, fontWeight: 600, fontSize: 14, padding: '0 24px'
        }
      },
      cancelButtonProps: {
        style: { borderRadius: 8, height: 40, fontSize: 14, padding: '0 20px' }
      },
      onOk: async () => {
        try {
          if (onSave) {
            const templateDataForSave = getTemplateDataForSave();
            // Check if we have a media file that needs upload session
            const hasMediaFile = templateDataForSave.components.header.mediaFile &&
                                templateDataForSave.components.header.type !== 'none' &&
                                templateDataForSave.components.header.type !== 'text';
            
            let result;
            
            if (hasMediaFile) {
              // Handle file upload flow
              const { mediaFile, type: headerFormat, fileHandle } = templateDataForSave.components.header;
              
              // Check if we already have a fileHandle from the initial upload
              if (fileHandle) {
                const templateDataWithFileHandle = {
                  ...templateDataForSave,
                  fileHandle,
                  mediaId: templateDataForSave.components.header.mediaId || "",
                  s3Key: templateDataForSave.components.header.s3Key || "",
                  headerFormat: headerFormat.toUpperCase()
                };
                
                // Call onSave with the enhanced template data
                result = await onSave(templateDataWithFileHandle);
              } else {
                // Fallback: if no fileHandle exists, start upload session (should not happen)
                message.loading({ content: 'Starting upload session...', key: 'upload', duration: 0 });
                const uploadSessionId = await startUploadSession(
                  mediaFile.name,
                  mediaFile.type,
                  mediaFile.size
                );
                
                // Upload file data
                message.loading({ content: 'Uploading file...', key: 'upload', duration: 0 });
                const { fileHandle: newFileHandle, mediaId, s3Key } = await uploadFileData(uploadSessionId, mediaFile, mediaFile.type);
                
                // Prepare template data with file handle, mediaId and header format
                const templateDataWithFileHandle = {
                  ...templateDataForSave,
                  fileHandle: newFileHandle,
                  mediaId: mediaId,
                  s3Key: s3Key,
                  headerFormat: headerFormat.toUpperCase()
                };
                
                // Call onSave with the enhanced template data
                result = await onSave(templateDataWithFileHandle);
                
                message.destroy('upload');
              }
            } else {
              // Regular template creation flow
              result = await onSave(templateDataForSave);
            }
            
            // If onSave returns an error result, the error message is already shown
            // We don't need to throw an error here, just return
            if (result && !result.success) {
              // Log for debugging but don't throw to prevent runtime error
              console.warn('Template submission failed:', result.error);
              return;
            }
            
            // Success case - return success
            return Promise.resolve();
          }
        } catch (error) {
          // Handle any unexpected errors gracefully
          console.error('Error in submitTemplate:', error);
          message.destroy('upload');
          
          // Show error message to user
          message.error(error.message || 'Failed to submit template');
          
          // Don't throw the error to prevent runtime errors
          return Promise.resolve();
        }
      },
      onCancel() {
        // Handle cancel gracefully
        console.log('Template submission cancelled');
      }
    });
  };

  // Render WhatsApp Preview with simple chat section design
  const renderWhatsAppPreview = (previewTemplateData = templateData) => {
    const activeTemplateData = previewTemplateData || templateData;
    const { header, body, footer, buttons } = activeTemplateData.components;
    const isAuthentication = activeTemplateData.category === 'Authentication';
    const flowPreviewDraft = activeTemplateData.flowDraft || (buttons.some(button => button.type === 'flow') ? getFlowDraftForPreview() : null);
    const previewButtons = flowPreviewDraft
      ? buttons.map(button => (
        button.type === 'flow'
          ? { ...button, text: flowPreviewDraft.buttonText || button.text || 'Open form' }
          : button
      ))
      : buttons;
    const shouldShowFlowPreview = !!flowPreviewDraft && isFlowPreviewOpen;
    // Resolve company name from session storage (fallback to WhatsApp Business)
    let companyName = 'WhatsApp Business';
    try {
      const usersStr = sessionStorage.getItem('user');
      if (usersStr) {
        const usersObj = JSON.parse(usersStr);
        if (usersObj && usersObj.name) {
          companyName = usersObj.name;
        }
      }
    } catch (err) {
      // keep default
    }
    
    return (
      <div className="whatsapp-preview-container">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="chat-avatar whatsapp-avatar">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="20" cy="20" r="20" fill="#25D366"/>
              <path d="M20 10C17.79 10 16 11.79 16 14C16 16.21 17.79 18 20 18C22.21 18 24 16.21 24 14C24 11.79 22.21 10 20 10ZM20 20C16.67 20 10 21.67 10 25V28H30V25C30 21.67 23.33 20 20 20Z" fill="white"/>
            </svg>
          </div>
          <div className="chat-info">
            <h3 className="chat-name">{companyName}</h3>
            <p className="chat-status">Template Preview</p>
          </div>
          <span className="template-category">{activeTemplateData.category || 'MARKETING'}</span>
        </div>

        {/* Chat Area */}
        <div className="chat-area" style={{ position: 'relative' }}>
          <div className="message-container">
              <div className="message-bubble">
              {/* Header Content */}
              {header.type !== 'none' && (
                <div className="message-header-content">
                  {header.type === 'text' && (
                    <div className="header-text">
                      {header.content ? (
                        (() => {
                          // For header preview, only use the first valid variable (since headers can only have 1 variable)
                          const allHeaderVariables = header.variables || [];
                          const validHeaderVariables = getValidVariables(allHeaderVariables, activeTemplateData.variableType);
                          const firstValidVariable = validHeaderVariables.length > 0 ? [validHeaderVariables[0]] : [];
                          
                          return getPreviewContentWithValidation(
                            header.content,
                            firstValidVariable, // Only pass the first valid variable
                            header.variableSamples || {},
                            activeTemplateData.variableType,
                            'header'
                          );
                        })()
                      ) : (
                        'Header text'
                      )}
                    </div>
                  )}
                  {header.type === 'image' && (
                    <div className="header-image">
                      {!header.mediaUrl && '📷 Image Preview'}
                      {header.mediaUrl && (
                        <img 
                          src={header.mediaUrl} 
                          alt="Header preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      )}
                    </div>
                  )}
                  {header.type === 'video' && (
                    <div className="header-video">
                      {!header.mediaUrl && '🎥 Video Preview'}
                      {header.mediaUrl && (
                        <video 
                          src={header.mediaUrl} 
                          alt="Header preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'cover'
                          }}
                          controls
                        />
                      )}
                    </div>
                  )}
                  {header.type === 'document' && (
                    <div className="header-document">
                      {!header.mediaUrl && '📄 Document Preview'}
                      {header.mediaUrl && (
                        <div className="document-preview">
                          <div className="document-icon">📄</div>
                          <div className="document-info">
                            <div className="document-name">Document</div>
                            <div className="document-size">PDF File</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Message Body */}
              <div className="message-content" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {isAuthentication ? (
                  (() => {
                    // Build authentication template content dynamically
                    let authContent = "{{1}} is your verification code.";
                    
                    // Add security recommendation if selected
                    if (activeTemplateData.authentication.securityRecommendation) {
                      authContent += " For your security, do not share this code.";
                    }
                    
                    return authContent;
                  })()
                ) : (
                  getPreviewContentWithValidation(
                    body.content, 
                    body.variables || [], 
                    body.variableSamples || {},
                    activeTemplateData.variableType,
                    'body'
                  )
                )}
              </div>

              {/* Footer */}
              {(footer.content || (isAuthentication && activeTemplateData.authentication.codeExpiration && activeTemplateData.authentication.expirationTime)) && (
                <div className="message-footer-text">
                  {isAuthentication && activeTemplateData.authentication.codeExpiration && activeTemplateData.authentication.expirationTime ? 
                    `This code expires in ${activeTemplateData.authentication.expirationTime} minutes.` :
                    getPreviewContentWithValidation(
                      footer.content,
                      footer.variables || [],
                      {}, // Footers don't have samples, show validation only
                      activeTemplateData.variableType,
                      'footer'
                    )
                  }
                </div>
              )}

              {/* Message Footer with Time and Checkmarks */}
              <div className="message-footer">
                <div className="message-time">
                  {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L3.724 9.587a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033L10.676 3.84a.366.366 0 0 0-.063-.51z" fill="#4fc3f7"/>
                  </svg>
                </div>
              </div>

              {/* Buttons */}
              {(isAuthentication ? 
                (activeTemplateData.authentication.codeDelivery === 'copy-code' ? 1 : 
                 activeTemplateData.authentication.codeDelivery === 'one-tap' ? 2 : 0) : 
                previewButtons.length
              ) > 0 && (
                <div className="template-buttons">
                  {isAuthentication ? (
                    <>
                      {/* Authentication Template Buttons */}
                      {activeTemplateData.authentication.codeDelivery === 'one-tap' ? (
                        <button className="template-button">
                          📱 {activeTemplateData.authentication.autofillButtonText || 'Autofill'}
                        </button>
                      ) : (
                        <button className="template-button">
                          📋 {activeTemplateData.authentication.copyButtonText || 'Copy code'}
                        </button>
                      )}
                    </>
                  ) : (
                    /* Regular Template Buttons */
                    previewButtons.map((button, index) => (
                      <button
                        key={button.id}
                        type="button"
                        className="template-button"
                        onClick={button.type === 'flow' ? () => setIsFlowPreviewOpen(true) : undefined}
                      >
                        {button.type === 'url' && '🔗'}
                        {button.type === 'phone' && '📞'}
                        {button.type === 'quick_reply' && '💬'}
                        {button.type === 'flow' && '▣'}
                        {button.text || `Button ${index + 1}`}
                      </button>
                    ))
                  )}
                </div>
              )}
              </div>
            </div>
          {shouldShowFlowPreview && (
            <div className="whatsapp-flow-screen-preview">
              <div className="whatsapp-flow-preview-header">
                <button
                  type="button"
                  className="whatsapp-flow-preview-back"
                  onClick={() => setIsFlowPreviewOpen(false)}
                  aria-label="Back to message preview"
                >
                  ‹
                </button>
                <span className="whatsapp-flow-preview-title">{flowPreviewDraft ? (flowPreviewDraft.screenTitle || 'WhatsApp Flow') : 'WhatsApp Flow'}</span>
                <span className="whatsapp-flow-preview-menu">⋯</span>
              </div>
              <div className="whatsapp-flow-preview-card">
                {flowPreviewDraft && getFlowContentItems(flowPreviewDraft).length > 0 ? (
                  getFlowContentItems(flowPreviewDraft).map(renderFlowPreviewField)
                ) : (
                  <div className="whatsapp-flow-preview-copy">Select Add content to start building your Flow.</div>
                )}
              </div>
              <button type="button" className="whatsapp-flow-preview-submit">
                {flowPreviewDraft ? (flowPreviewDraft.submitLabel || 'Submit') : 'Submit'}
              </button>
            </div>
          )}
        </div>
        {/* Chat Footer */}
        <div className="chat-footer">
          <div className="footer-input">
            <span className="footer-placeholder">Type a message...</span>
            <button className="send-button" type="button" aria-label="Send">➤</button>
          </div>
        </div>
      </div>
    );
  };

  const renderLivePreviewCard = () => (
    <div className="mobile-preview-container">
      <Card title="Live Preview" size="small" className="preview-card">
        <div style={{ display: 'flex', width: '100%' }}>
          {renderWhatsAppPreview(getLivePreviewTemplateData())}
        </div>
      </Card>
    </div>
  );

  const getFlowOptionLabel = (option, fallback = '') => {
    if (typeof option === 'string') return option;
    return option?.title || option?.label || option?.value || option?.id || fallback;
  };

  const renderFlowPreviewField = (field, fieldIndex) => {
    const label = field.label || `Question ${fieldIndex + 1}`;
    const options = Array.isArray(field.options) && field.options.length > 0 ? field.options : ['Option 1'];
    const requiredMark = field.required ? <span className="flow-preview-required">*</span> : null;

    if (field.type === 'LargeHeading') {
      return <div className="flow-preview-large-heading" key={field.id || fieldIndex}>{field.text || 'Large heading'}</div>;
    }

    if (field.type === 'SmallHeading') {
      return <div className="flow-preview-small-heading" key={field.id || fieldIndex}>{field.text || 'Small heading'}</div>;
    }

    if (field.type === 'Body') {
      return <div className="whatsapp-flow-preview-copy" key={field.id || fieldIndex}>{field.text || 'Body'}</div>;
    }

    if (field.type === 'Caption') {
      return <div className="flow-preview-caption" key={field.id || fieldIndex}>{field.text || 'Caption'}</div>;
    }

    if (field.type === 'Image') {
      return (
        <div className="flow-preview-image" key={field.id || fieldIndex}>
          {field.imageUrl ? <img src={field.imageUrl} alt={field.altText || 'Flow'} /> : <span>Image</span>}
        </div>
      );
    }

    if (field.type === 'TextArea') {
      return (
        <div className="flow-preview-field" key={`${field.name || 'field'}-${fieldIndex}`}>
          <label>{label}{requiredMark}</label>
          <div className="flow-preview-textarea">Type your answer</div>
        </div>
      );
    }

    if (field.type === 'Dropdown') {
      return (
        <div className="flow-preview-field" key={`${field.name || 'field'}-${fieldIndex}`}>
          <label>{label}{requiredMark}</label>
          <div className="flow-preview-select">
            <span>{getFlowOptionLabel(options[0], 'Select an option')}</span>
            <span className="flow-preview-chevron">⌄</span>
          </div>
        </div>
      );
    }

    if (field.type === 'RadioButtonsGroup') {
      return (
        <div className="flow-preview-field" key={`${field.name || 'field'}-${fieldIndex}`}>
          <label>{label}{requiredMark}</label>
          <div className="flow-preview-choice-list">
            {options.slice(0, 5).map((option, optionIndex) => (
              <div className="flow-preview-choice" key={`${fieldIndex}-radio-${optionIndex}`}>
                <span className={`flow-preview-radio${optionIndex === 0 ? ' selected' : ''}`} />
                <span>{getFlowOptionLabel(option, `Option ${optionIndex + 1}`)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'CheckboxGroup') {
      return (
        <div className="flow-preview-field" key={`${field.name || 'field'}-${fieldIndex}`}>
          <label>{label}{requiredMark}</label>
          <div className="flow-preview-choice-list">
            {options.slice(0, 5).map((option, optionIndex) => (
              <div className="flow-preview-choice" key={`${fieldIndex}-checkbox-${optionIndex}`}>
                <span className={`flow-preview-checkbox${optionIndex === 0 ? ' selected' : ''}`}>
                  {optionIndex === 0 ? '✓' : ''}
                </span>
                <span>{getFlowOptionLabel(option, `Option ${optionIndex + 1}`)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'DatePicker') {
      return (
        <div className="flow-preview-field" key={`${field.name || 'field'}-${fieldIndex}`}>
          <label>{label}{requiredMark}</label>
          <div className="flow-preview-input with-icon">
            <span>DD/MM/YYYY</span>
            <span className="flow-preview-input-icon">▦</span>
          </div>
        </div>
      );
    }

    if (field.type === 'OptIn') {
      return (
        <div className="flow-preview-field" key={`${field.name || 'field'}-${fieldIndex}`}>
          <div className="flow-preview-choice opt-in">
            <span className="flow-preview-checkbox selected">✓</span>
            <span>{label}{requiredMark}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="flow-preview-field" key={`${field.name || 'field'}-${fieldIndex}`}>
        <label>{label}{requiredMark}</label>
        <div className="flow-preview-input">
          {field.inputType === 'email' && 'name@example.com'}
          {field.inputType === 'phone' && '+91 98765 43210'}
          {field.inputType === 'number' && '123'}
          {field.inputType === 'password' && '••••••••'}
          {field.inputType === 'passcode' && '123456'}
          {(!field.inputType || field.inputType === 'text') && 'Type your answer'}
        </div>
      </div>
    );
  };

  const renderFlowBuilderCard = () => {
    if (!hasFlowButton) return null;

    const presets = flowPresets.length > 0 ? flowPresets : FALLBACK_FLOW_PRESETS;
    const currentDraft = flowDraft || buildFlowDraftFromPreset(presets[0]);
    const currentContentItems = getFlowContentItems(currentDraft);
    const answerFieldCount = getFlowAnswerFields(currentDraft).length;
    const flowReadyForTemplate = !!getFlowButtonComponentForSubmit();
    const previewJson = flowPreviewResult?.flow_json || flowCreateResult?.flow_json || null;
    const flowFieldErrors = mergeFlowFieldErrors(getFlowLocalFieldErrors(currentDraft), flowApiFieldErrors);

    return (
      <Card
        className="flow-builder-card"
        title={
          <div className="flow-builder-title">
            <span><FormOutlined /> WhatsApp Flow</span>
            <div className="flow-builder-title-actions">
              {flowReadyForTemplate
                ? <Tag color="green">Ready for template</Tag>
                : <Tag color="blue">Create Flow first</Tag>}
              <button
                type="button"
                className="flow-ai-header-btn"
                onClick={() => { setAiFlowModalOpen(true); setAiFlowError(''); }}
              >
                <ThunderboltOutlined /> Generate with AI
              </button>
            </div>
          </div>
        }
        size="small"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {!flowReadyForTemplate && (
            <div className="flow-meta-notice">
              <InfoCircleOutlined className="flow-meta-notice-icon" />
              <span>Create this Flow in Meta to attach it to the template. Preview validates your fields locally.</span>
            </div>
          )}

          <div className="flow-status-strip">
            <div className="flow-status-step done">
              <CheckCircleOutlined />
              <span>Purpose selected</span>
            </div>
            <div className={`flow-status-step ${currentContentItems.length > 0 ? 'done' : ''}`}>
              <CheckCircleOutlined />
              <span>{currentContentItems.length} block{currentContentItems.length === 1 ? '' : 's'} / {answerFieldCount} answer{answerFieldCount === 1 ? '' : 's'}</span>
            </div>
            <div className={`flow-status-step ${flowReadyForTemplate ? 'done' : 'pending'}`}>
              <CloudUploadOutlined />
              <span>{flowReadyForTemplate ? 'Created in Meta' : 'Not created yet'}</span>
            </div>
          </div>

          <div className="flow-preset-grid">
            {presets.map(preset => (
              <button
                key={preset.id}
                type="button"
                className={`flow-preset-card${currentDraft.preset === preset.id ? ' active' : ''}`}
                onClick={() => applyFlowPreset(preset)}
                disabled={flowPresetLoading}
              >
                <span className="flow-preset-card-title">{preset.title}</span>
                <span className="flow-preset-card-desc">{preset.description}</span>
                <Tag>{preset.category || 'OTHER'}</Tag>
              </button>
            ))}
          </div>

          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Flow name"
                required
                validateStatus={flowFieldErrors.flowName ? 'error' : ''}
                help={flowFieldErrors.flowName || ''}
              >
                <Input
                  value={currentDraft.flowName}
                  onChange={(event) => updateFlowDraft({ flowName: event.target.value })}
                  maxLength={200}
                  placeholder="customer_feedback_survey"
                />
                <Text type="secondary" style={{ fontSize: 12 }}>Must be unique in your WhatsApp Business account.</Text>
                {isFlowNameUniqueError(flowFieldErrors.flowName) && (
                  <Button
                    size="small"
                    className="flow-inline-fix-button"
                    onClick={() => updateFlowDraft({ flowName: makeUniqueFlowName(currentDraft.flowName) })}
                  >
                    Use new unique name
                  </Button>
                )}
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="WhatsApp button text"
                required
                validateStatus={flowFieldErrors.buttonText ? 'error' : ''}
                help={flowFieldErrors.buttonText || ''}
              >
                <Input
                  value={currentDraft.buttonText}
                  onChange={(event) => updateFlowDraft({ buttonText: event.target.value })}
                  maxLength={25}
                  placeholder="Start survey"
                />
                <Text type="secondary" style={{ fontSize: 12 }}>{currentDraft.buttonText?.length || 0}/25 characters</Text>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Flow submit button"
                required
                validateStatus={flowFieldErrors.submitLabel ? 'error' : ''}
                help={flowFieldErrors.submitLabel || ''}
              >
                <Input
                  value={currentDraft.submitLabel}
                  onChange={(event) => updateFlowDraft({ submitLabel: event.target.value })}
                  maxLength={35}
                  placeholder="Submit"
                />
              </Form.Item>
            </Col>
          </Row>

          <Collapse ghost className="flow-advanced-collapse">
            <Panel header="Advanced Flow settings" key="advanced-flow-settings">
              <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Flow category"
                    validateStatus={flowFieldErrors.category ? 'error' : ''}
                    help={flowFieldErrors.category || ''}
                  >
                    <Select value={currentDraft.category} onChange={(value) => updateFlowDraft({ category: value })}>
                      {FLOW_CATEGORIES.map(category => <Option key={category} value={category}>{category}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Screen ID"
                    required
                    validateStatus={flowFieldErrors.screenId ? 'error' : ''}
                    help={flowFieldErrors.screenId || ''}
                  >
                    <Input
                      value={currentDraft.screenId}
                      onChange={(event) => updateFlowDraft({ screenId: event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                      maxLength={50}
                      placeholder="SURVEY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Screen title">
                    <Input
                      value={currentDraft.screenTitle}
                      onChange={(event) => updateFlowDraft({ screenTitle: event.target.value })}
                      maxLength={80}
                      placeholder="Quick survey"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>

          <div className="flow-fields-header">
            <Text strong>Flow content</Text>
            <Dropdown menu={{ items: FLOW_ADD_CONTENT_MENU_ITEMS, onClick: ({ key }) => addFlowField(key) }} trigger={['click']}>
              <Button size="small" icon={<PlusOutlined />}>
                Add content <DownOutlined />
              </Button>
            </Dropdown>
          </div>

          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {currentContentItems.map((field, fieldIndex) => {
              const fieldErrorsForIndex = flowFieldErrors.fields?.[fieldIndex] || {};
              const validation = FLOW_CONTENT_VALIDATION[field.type] || {};
              const isTextContent = FLOW_TEXT_CONTENT_TYPES.includes(field.type);
              const isImageContent = field.type === 'Image';
              const isAnswerContent = FLOW_ANSWER_CONTENT_TYPES.includes(field.type);

              return (
                <div className="flow-field-panel flow-content-panel" key={field.id || `${field.type}-${fieldIndex}`}>
                  <div className="flow-field-title-row">
                    <Text strong>{getFlowContentLabel(field.type)}</Text>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeFlowField(fieldIndex)}
                      disabled={currentContentItems.length <= 1}
                    >
                      Remove
                    </Button>
                  </div>

                  {isTextContent && (
                    <Form.Item
                      label={getFlowContentLabel(field.type)}
                      required
                      validateStatus={fieldErrorsForIndex.text ? 'error' : ''}
                      help={fieldErrorsForIndex.text || ''}
                    >
                      {field.type === 'Body' ? (
                        <TextArea
                          value={field.text}
                          onChange={(event) => updateFlowField(fieldIndex, { text: event.target.value })}
                          maxLength={validation.textMax}
                          autoSize={{ minRows: 3, maxRows: 6 }}
                          placeholder="Add body text"
                        />
                      ) : (
                        <Input
                          value={field.text}
                          onChange={(event) => updateFlowField(fieldIndex, { text: event.target.value })}
                          maxLength={validation.textMax}
                          placeholder={getFlowContentLabel(field.type)}
                        />
                      )}
                      <Text type="secondary" style={{ fontSize: 12 }}>{field.text?.length || 0}/{validation.textMax}</Text>
                    </Form.Item>
                  )}

                  {isImageContent && (
                    <Space direction="vertical" style={{ width: '100%' }} size={10}>
                      <Form.Item
                        label="Image URL"
                        required
                        validateStatus={fieldErrorsForIndex.imageUrl ? 'error' : ''}
                        help={fieldErrorsForIndex.imageUrl || 'Use an HTTPS image URL for the final Meta Flow. Upload here is for local preview.'}
                      >
                        <Input
                          value={field.imageUrl}
                          onChange={(event) => updateFlowField(fieldIndex, { imageUrl: event.target.value })}
                          maxLength={validation.urlMax}
                          placeholder="https://example.com/image.png"
                        />
                      </Form.Item>
                      <Upload
                        accept="image/*"
                        maxCount={1}
                        showUploadList={false}
                        beforeUpload={() => false}
                        onChange={(info) => {
                          const file = info.file?.originFileObj || info.file;
                          if (file) {
                            updateFlowField(fieldIndex, { imageUrl: URL.createObjectURL(file), mediaFile: file });
                          }
                        }}
                      >
                        <Button icon={<UploadOutlined />}>Upload preview image</Button>
                      </Upload>
                    </Space>
                  )}

                  {isAnswerContent && (
                    <>
                      <Row gutter={[12, 8]} align="top">
                        <Col xs={24} md={10}>
                          <Form.Item
                            label="Question text"
                            required
                            validateStatus={fieldErrorsForIndex.label ? 'error' : ''}
                            help={fieldErrorsForIndex.label || fieldErrorsForIndex.name || ''}
                          >
                            <Input
                              value={field.label}
                              onChange={(event) => updateFlowField(fieldIndex, {
                                label: event.target.value,
                                name: buildFlowFieldNameFromLabel(event.target.value, fieldIndex)
                              })}
                              maxLength={validation.labelMax}
                              placeholder="Label"
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>{field.label?.length || 0}/{validation.labelMax}</Text>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={7}>
                          <Form.Item
                            label="Answer type"
                            required
                            validateStatus={fieldErrorsForIndex.type ? 'error' : ''}
                            help={fieldErrorsForIndex.type || ''}
                          >
                            <Select
                              value={field.type}
                              onChange={(value) => updateFlowField(fieldIndex, { type: value })}
                              style={{ width: '100%' }}
                            >
                              {acceptedFlowFieldTypes.map(type => <Option key={type} value={type}>{getFlowFieldLabel(type)}</Option>)}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={7}>
                          <Form.Item label="Required">
                            <Switch
                              checked={!!field.required}
                              onChange={(checked) => updateFlowField(fieldIndex, { required: checked })}
                              checkedChildren="Required"
                              unCheckedChildren="Optional"
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {field.type === 'TextInput' && (
                        <div className="flow-field-extra">
                          <Text type="secondary">Text format</Text>
                          <Select
                            value={field.inputType || 'text'}
                            onChange={(value) => updateFlowField(fieldIndex, { inputType: value })}
                            status={fieldErrorsForIndex.inputType ? 'error' : ''}
                            style={{ width: 180 }}
                          >
                            {FLOW_INPUT_TYPES.map(type => <Option key={type} value={type}>{getFlowInputLabel(type)}</Option>)}
                          </Select>
                          {fieldErrorsForIndex.inputType && (
                            <Text type="danger" style={{ fontSize: 12 }}>{fieldErrorsForIndex.inputType}</Text>
                          )}
                        </div>
                      )}

                      {FLOW_CHOICE_FIELD_TYPES.includes(field.type) && (
                        <div className="flow-options-editor">
                          <div className="flow-options-header">
                            <Text type="secondary">Answer choices</Text>
                            <Button size="small" onClick={() => addFlowOption(fieldIndex)} disabled={(field.options || []).length >= 20}>Add option</Button>
                          </div>
                          {fieldErrorsForIndex.optionsRoot && (
                            <Text type="danger" style={{ fontSize: 12 }}>{fieldErrorsForIndex.optionsRoot}</Text>
                          )}
                          {(field.options || []).map((option, optionIndex) => (
                            <React.Fragment key={`${fieldIndex}-${optionIndex}`}>
                              <Input.Group compact className="flow-option-row">
                                <Input
                                  value={typeof option === 'string' ? option : option?.title || option?.label || option?.value || ''}
                                  onChange={(event) => updateFlowOption(fieldIndex, optionIndex, event.target.value)}
                                  maxLength={validation.optionMax}
                                  status={fieldErrorsForIndex.options?.[optionIndex] ? 'error' : ''}
                                  style={{ width: 'calc(100% - 36px)' }}
                                  placeholder={`Option ${optionIndex + 1}`}
                                />
                                <Button
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeFlowOption(fieldIndex, optionIndex)}
                                  disabled={(field.options || []).length <= 1}
                                />
                              </Input.Group>
                              {fieldErrorsForIndex.options?.[optionIndex] && (
                                <div className="flow-inline-error">{fieldErrorsForIndex.options[optionIndex]}</div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </Space>

          {flowErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="Flow needs attention"
              description={
                <ul className="flow-error-list">
                  {flowErrors.map((error, index) => <li key={index}>{error}</li>)}
                </ul>
              }
            />
          )}

          <div className="flow-action-row">
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={createWhatsAppFlow}
              loading={flowCreateLoading}
              disabled={flowReadyForTemplate}
            >
              {flowReadyForTemplate ? 'Flow created in Meta' : 'Create and attach Flow'}
            </Button>
            {/* <Button icon={<CodeOutlined />} onClick={previewWhatsAppFlow} loading={flowPreviewLoading}>
              Preview JSON
            </Button> */}
          </div>

          {(previewJson || flowPreviewResult?.template_button || flowCreateResult?.template_button) && (
            <Collapse ghost>
              {previewJson && (
                <Panel header="Flow JSON preview" key="flow-json">
                  <pre className="flow-json-preview">{JSON.stringify(previewJson, null, 2)}</pre>
                </Panel>
              )}
              {(flowPreviewResult?.template_button || flowCreateResult?.template_button) && (
                <Panel header="Template Flow button" key="template-button">
                  <pre className="flow-json-preview">{JSON.stringify(flowCreateResult?.template_button || flowPreviewResult?.template_button, null, 2)}</pre>
                </Panel>
              )}
            </Collapse>
          )}
        </Space>
      </Card>
    );
  };

  // Step 1: Template Info
  const renderTemplateInfo = () => {
    const isAuthentication = templateData.category === 'Authentication';
    
    return (
      <div className="redesigned-template-info">
        <h2 className="redesigned-section-title">
          {isAuthentication ? 'Authentication Template' : 'Set up your template'}
        </h2>
        
        {/* Show category selection only for non-authentication templates */}
        {!isAuthentication && (
          <div className="redesigned-category-section">
            <div className="redesigned-category-buttons">
              {templateCategories.map(cat => (
                <Tooltip
                  key={cat.value}
                  title={
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{cat.description}</div>
                      <div style={{ fontSize: '12px' }}>
                        <strong>Examples:</strong> {cat.examples.join(', ')}
                      </div>
                    </div>
                  }
                  placement="top"
                >
                  <button
                    className={`redesigned-category-btn ${templateData.category === cat.value ? 'selected' : ''} ${fieldErrors.category && touchedFields.category ? 'error' : ''}`}
                    onClick={() => {
                      const value = cat.value;
                      setTemplateData(prev => ({ ...prev, category: value }));
                      validateField('category', value);
                      
                      // Switch to step-based flow for authentication templates
                      if (value === 'Authentication') {
                        setUseStepFlow(true);
                      } else {
                        setUseStepFlow(false);
                      }
                    }}
                  >
                    {cat.label}
                    {cat.isSpecial && (
                      <span className="redesigned-category-badge">Special</span>
                    )}
                  </button>
                </Tooltip>
              ))}
            </div>
            {fieldErrors.category && touchedFields.category && (
              <div className="redesigned-error-message">{fieldErrors.category}</div>
            )}
          </div>
        )}
        
       

      {/* Template Name and Language - Same Line */}
      <div className="redesigned-form-row">
        <div className="redesigned-form-field redesigned-template-name">
          <Form.Item
            name="name"
            rules={[
              { required: true, message: 'You need to enter a name for your template.' },
              { validator: validateTemplateName }
            ]}
            validateStatus={fieldErrors.name && touchedFields.name ? 'error' : ''}
            help={fieldErrors.name && touchedFields.name ? fieldErrors.name : ''}
          >
            <label className="redesigned-field-label required">Template name</label>
            <input
              className={`redesigned-form-input ${fieldErrors.name && touchedFields.name ? 'error' : ''}`}
              placeholder="Enter template name (e.g., welcome_message_01)"
              value={templateData.templateName}
              onChange={(e) => {
                const value = e.target.value
                  .replace(/\s+/g, '_')
                  .toLowerCase()
                  .replace(/[^a-z0-9_]/g, '');
                setTemplateData(prev => ({ ...prev, templateName: value }));
                form.setFieldsValue({ name: value });
                validateField('templateName', value);
              }}
              maxLength={512}
              style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace' }}
            />
            <div className="redesigned-char-count">
              {templateData.templateName?.length || 0}/512
            </div>
          </Form.Item>
        </div>
        
        <div className="redesigned-form-field redesigned-language">
          <Form.Item
            name="language"
            rules={[{ required: true, message: 'Please select language' }]}
            initialValue="en_US"
            validateStatus={fieldErrors.language && touchedFields.language ? 'error' : ''}
            help={fieldErrors.language && touchedFields.language ? fieldErrors.language : ''}
          >
            <label className="redesigned-field-label required">Language</label>
            <select
              className={`redesigned-form-select ${fieldErrors.language && touchedFields.language ? 'error' : ''}`}
              value={templateData.language}
              onChange={(e) => {
                const value = e.target.value;
                setTemplateData(prev => ({ ...prev, language: value }));
                validateField('language', value);
              }}
            >
              {languages.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </Form.Item>
        </div>
      </div>

      {/* Address Book Selection - Same Width as Template Name */}
      

      {templateData.category === 'Authentication' && (
        <div className="redesigned-info-message">
          <span style={{ fontWeight: 600 }}>Authentication Template</span>
          <span style={{ margin: '0 8px', color: '#bbb' }}>·</span>
          Authentication templates have fixed content structure for OTP & verification messages.
        </div>
      )}
    </div>
  );
};

  // Step 2: Template Builder with Authentication Support
  const renderTemplateBuilder = () => {
    const isAuthentication = templateData.category === 'Authentication';
    
    if (isAuthentication) {
      return renderAuthenticationBuilder();
    }
    
    return renderRegularTemplateBuilder();
  };

  // Authentication Template Builder
  const renderAuthenticationBuilder = () => (
    <Row gutter={[24, 16]}>
      <Col xs={24} sm={24} md={24} lg={14} xl={14}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Code Delivery Setup */}
          <Card title="Code Delivery Setup" size="small">
            <Text style={{ color: '#666', marginBottom: 16, display: 'block' }}>
              Choose how customers send the code from WhatsApp to your app.
            </Text>
            
            <Radio.Group
              value={templateData.authentication.codeDelivery}
              onChange={(e) => setTemplateData(prev => ({
                ...prev,
                authentication: { ...prev.authentication, codeDelivery: e.target.value }
              }))}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Card size="small" style={{ 
                  border: templateData.authentication.codeDelivery === 'zero-tap' ? '2px solid #1890ff' : '1px solid #f0f0f0',
                  borderColor: templateData.authentication.codeDelivery === 'zero-tap' && !templateData.authentication.zeroTapAgreement ? '#ff4d4f' : 
                              templateData.authentication.codeDelivery === 'zero-tap' ? '#1890ff' : '#f0f0f0'
                }}>
                  <Radio value="zero-tap">
                    <div>
                      <Text strong>Zero-tap autofill</Text>
                      <Text style={{ display: 'block', color: '#666', fontSize: 12, marginTop: 4 }}>
                        This is recommended as the easiest option for your customers. Zero-tap will automatically send code without requiring your customer to tap a button. An autofill or copy code message will be sent if zero-tap and autofill aren't possible.
                      </Text>
                      {templateData.authentication.codeDelivery === 'zero-tap' && (
                        <div style={{ marginTop: 12 }}>
                          <Checkbox
                            checked={templateData.authentication.zeroTapAgreement}
                            onChange={(e) => setTemplateData(prev => ({
                              ...prev,
                              authentication: { ...prev.authentication, zeroTapAgreement: e.target.checked }
                            }))}
                            style={{ fontSize: 11 }}
                          >
                            <Text style={{ fontSize: 11, color: '#666' }}>
                              By selecting zero-tap, I understand that NextGenCommunicator's use of zero-tap authentication is subject to the{' '}
                              <a 
                                href="https://www.whatsapp.com/legal/business-terms/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ color: '#1890ff' }}
                              >
                                WhatsApp Business Terms of Service
                              </a>
                              . It's NextGenCommunicator's responsibility to ensure its customers expect that the code will be automatically filled-in on their behalf when they choose to receive the zero-tap code through WhatsApp.
                            </Text>
                          </Checkbox>
                          {!templateData.authentication.zeroTapAgreement && (
                            <Text type="danger" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                              This box must be checked to submit this template.
                            </Text>
                          )}
                          <div style={{ marginTop: 8 }}>
                            <a 
                              href="https://developers.facebook.com/docs/whatsapp/cloud-api/guides/authentication-templates/"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: 11, color: '#1890ff' }}
                            >
                              Learn more and review best practices
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </Radio>
                </Card>
                
                <Card size="small" style={{ 
                  border: templateData.authentication.codeDelivery === 'one-tap' ? '2px solid #1890ff' : '1px solid #f0f0f0'
                }}>
                  <Radio value="one-tap">
                    <div>
                      <Text strong>One-tap autofill</Text>
                      <Text style={{ display: 'block', color: '#666', fontSize: 12, marginTop: 4 }}>
                        The code sends to your app when customers tap the button. A copy code message will be sent if autofill isn't possible.
                      </Text>
                    </div>
                  </Radio>
                </Card>
                
                <Card size="small" style={{ 
                  border: templateData.authentication.codeDelivery === 'copy-code' ? '2px solid #1890ff' : '1px solid #f0f0f0'
                }}>
                  <Radio value="copy-code">
                    <div>
                      <Text strong>Copy code</Text>
                      <Text style={{ display: 'block', color: '#666', fontSize: 12, marginTop: 4 }}>
                        Basic authentication with quick setup. Your customers copy and paste the code into your app.
                      </Text>
                    </div>
                  </Radio>
                </Card>
              </Space>
            </Radio.Group>
          </Card>

          {/* App Setup (only for autofill options) */}
          {templateData.authentication.codeDelivery !== 'copy-code' && (
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>App Setup</span>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {templateData.authentication.apps.length}/5 apps
                  </Text>
                </div>
              } 
              size="small"
            >
              <Text style={{ color: '#666', marginBottom: 16, display: 'block' }}>
                You can add up to 5 apps.
              </Text>
              
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {templateData.authentication.apps.map((app, index) => {
                  const packageNameValidation = validatePackageName(app.packageName);
                  const signatureHashValidation = validateSignatureHash(app.signatureHash);
                  
                  return (
                    <Card key={app.id || index} size="small" style={{ background: '#fafafa' }}>
                      <Row gutter={[16, 16]} align="top">
                        <Col xs={24} sm={24} md={11} lg={11} xl={11}>
                          <Form.Item 
                            label="Package name"
                            validateStatus={packageNameValidation.isValid ? 'success' : 'error'}
                            help={!packageNameValidation.isValid ? packageNameValidation.error : null}
                          >
                            <Input
                              placeholder="com.example.myapplication"
                              value={app.packageName}
                              onChange={(e) => updateApp(app.id || index, 'packageName', e.target.value)}
                              maxLength={224}
                              status={!packageNameValidation.isValid ? 'error' : undefined}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {app.packageName?.length || 0}/224
                              </Text>
                              {!packageNameValidation.isValid && app.packageName && (
                                <Text type="danger" style={{ fontSize: 11 }}>
                                  Invalid format
                                </Text>
                              )}
                            </div>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={11} lg={11} xl={11}>
                          <Form.Item 
                            label="App signature hash"
                            validateStatus={signatureHashValidation.isValid ? 'success' : 'error'}
                            help={!signatureHashValidation.isValid ? signatureHashValidation.error : null}
                          >
                            <Input
                              placeholder="Enter text"
                              value={app.signatureHash}
                              onChange={(e) => updateApp(app.id || index, 'signatureHash', e.target.value)}
                              maxLength={11}
                              status={!signatureHashValidation.isValid ? 'error' : undefined}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {app.signatureHash?.length || 0}/11
                              </Text>
                              {!signatureHashValidation.isValid && app.signatureHash && (
                                <Text type="danger" style={{ fontSize: 11 }}>
                                  Must be 11 characters
                                </Text>
                              )}
                            </div>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={2} lg={2} xl={2}>
                          <Form.Item label=" " style={{ marginTop: 30 }}>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => removeApp(app.id || index)}
                              disabled={templateData.authentication.apps.length === 1}
                              title={templateData.authentication.apps.length === 1 ? "At least one app is required" : "Remove app"}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}

                {/* Add Another App Button */}
                {templateData.authentication.apps.length < 5 && (
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={addApp}
                    style={{ width: '100%' }}
                  >
                    Add Another App
                  </Button>
                )}

                {/* Overall validation messages */}
                {(() => {
                  const hasInvalidApps = templateData.authentication.apps.some(app => 
                    !validatePackageName(app.packageName).isValid || 
                    !validateSignatureHash(app.signatureHash).isValid
                  );
                  const hasEmptyFields = templateData.authentication.apps.some(app => 
                    !app.packageName?.trim() || !app.signatureHash?.trim()
                  );

                  if (hasEmptyFields) {
                    return (
                      <Alert
                        message="Required Fields Missing"
                        description="Add text in all required fields to publish your message template."
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    );
                  }

                  if (hasInvalidApps) {
                    return (
                      <Alert
                        message="App Configuration Errors"
                        description="Please fix the validation errors above before proceeding."
                        type="error"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    );
                  }

                  return null;
                })()}
              </Space>
            </Card>
          )}

          {/* Content */}
          <Card title="Content" size="small">
            <Alert
              message="Content for authentication message templates can't be edited. You can add additional content from the options below."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text>Add security recommendation</Text>
                <Switch
                  checked={templateData.authentication.securityRecommendation}
                  onChange={(checked) => setTemplateData(prev => ({
                    ...prev,
                    authentication: { ...prev.authentication, securityRecommendation: checked }
                  }))}
                />
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Text>Add expiration time for the code</Text>
                  <Text style={{ display: 'block', color: '#666', fontSize: 12 }}>
                    After the code expires, the autofill button will be disabled.
                  </Text>
                </div>
                <Switch
                  checked={templateData.authentication.codeExpiration}
                  onChange={(checked) => setTemplateData(prev => ({
                    ...prev,
                    authentication: { ...prev.authentication, codeExpiration: checked }
                  }))}
                />
              </div>
              
              {templateData.authentication.codeExpiration && (
                <Form.Item 
                  label="Expires in"
                  validateStatus={
                    templateData.authentication.expirationTime === null || 
                    templateData.authentication.expirationTime === undefined ||
                    templateData.authentication.expirationTime < 1 || 
                    templateData.authentication.expirationTime > 90 
                      ? 'error' 
                      : 'success'
                  }
                  help={
                    templateData.authentication.expirationTime === null || 
                    templateData.authentication.expirationTime === undefined ||
                    templateData.authentication.expirationTime < 1 || 
                    templateData.authentication.expirationTime > 90
                      ? 'Enter a value between 1 and 90.'
                      : null
                  }
                >
                  <InputNumber
                    value={templateData.authentication.expirationTime}
                    onChange={(value) => setTemplateData(prev => ({
                      ...prev,
                      authentication: { ...prev.authentication, expirationTime: value }
                    }))}
                    placeholder="Enter minutes"
                    style={{ width: '150px' }}
                    addonAfter="minutes"
                  />
                </Form.Item>
              )}
            </Space>
          </Card>

          {/* Buttons */}
          <Card title="Buttons" size="small">
            <Text style={{ color: '#666', marginBottom: 16, display: 'block' }}>
              You can customize the button text for both autofill and copy code. Even when zero-tap is turned on, buttons are still needed for the backup code delivery method.
            </Text>
            
            <Row gutter={[16, 16]}>
              {templateData.authentication.codeDelivery !== 'copy-code' && (
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <Form.Item label="Autofill">
                    <Input
                      value={templateData.authentication.autofillButtonText}
                      onChange={(e) => setTemplateData(prev => ({
                        ...prev,
                        authentication: { ...prev.authentication, autofillButtonText: e.target.value }
                      }))}
                      maxLength={25}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {templateData.authentication.autofillButtonText.length}/25
                    </Text>
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={24} md={templateData.authentication.codeDelivery !== 'copy-code' ? 12 : 24} lg={templateData.authentication.codeDelivery !== 'copy-code' ? 12 : 24} xl={templateData.authentication.codeDelivery !== 'copy-code' ? 12 : 24}>
                <Form.Item label="Copy code">
                  <Input
                    value={templateData.authentication.copyButtonText}
                    onChange={(e) => setTemplateData(prev => ({
                      ...prev,
                      authentication: { ...prev.authentication, copyButtonText: e.target.value }
                    }))}
                    maxLength={25}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {templateData.authentication.copyButtonText.length}/25
                  </Text>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Message Validity Period for Authentication Templates */}
          <Card title="Message Validity Period" size="small">
            <Alert
              message="Message Validity Period"
              description="Set how long your message remains valid before expiring. Undelivered messages won't be charged."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form.Item
              label="Set custom validity period for your message"
            >
              <Switch
                checked={templateData.validityPeriod.enabled}
                onChange={(checked) => setTemplateData(prev => ({
                  ...prev,
                  validityPeriod: {
                    ...prev.validityPeriod,
                    enabled: checked
                  }
                }))}
                checkedChildren="Custom"
                unCheckedChildren="Standard"
              />
              <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                {templateData.validityPeriod.enabled 
                  ? 'Custom validity period selected' 
                  : 'If you don\'t set a custom validity period, the standard 10 minutes WhatsApp message validity period will be applied.'
                }
              </div>
            </Form.Item>

            {templateData.validityPeriod.enabled && (
              <>
                <Form.Item label="Validity Period">
                  <Select
                    style={{ width: '100%' }}
                    value={`${templateData.validityPeriod.value} ${templateData.validityPeriod.unit}`}
                    onChange={(value) => {
                      const [val, unit] = value.split(' ');
                      setTemplateData(prev => ({
                        ...prev,
                        validityPeriod: {
                          ...prev.validityPeriod,
                          value: parseInt(val),
                          unit: unit
                        }
                      }));
                    }}
                  >
                    <Option value="30 seconds">30 seconds</Option>
                    <Option value="1 minute">1 minute</Option>
                    <Option value="2 minutes">2 minutes</Option>
                    <Option value="3 minutes">3 minutes</Option>
                    <Option value="5 minutes">5 minutes</Option>
                    <Option value="10 minutes">10 minutes</Option>
                    <Option value="15 minutes">15 minutes</Option>
                  </Select>
                </Form.Item>
                
                <Alert
                  message="Recommendation"
                  description="It's recommended to set a validity period that is equal to or less than your code expiration time to ensure the code is still usable when users get the message."
                  type="warning"
                  showIcon
                  style={{ fontSize: 11 }}
                />
              </>
            )}
          </Card>
        </Space>
      </Col>

      <Col xs={24} sm={24} md={24} lg={10} xl={10}>
        <div className="mobile-preview-container">
          <Card title="Live Preview" size="small" className="preview-card">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {renderWhatsAppPreview()}
            </div>
          </Card>
        </div>
      </Col>
    </Row>
  );

  // Review & Submit Step for Authentication Templates
  const renderReviewSubmitStep = () => (
    <Card title="Review Template">
      <Row gutter={[24, 16]}>
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Template Name:</Text> {templateData.templateName}
            </div>
            <div>
              <Text strong>Category:</Text> {templateData.category}
            </div>
            <div>
              <Text strong>Language:</Text> {templateData.language}
            </div>
            <div>
              <Text strong>Code Delivery:</Text> {templateData.authentication.codeDelivery}
            </div>
            {templateData.authentication.codeDelivery !== 'copy-code' && (
              <div>
                <Text strong>Apps Configured:</Text> {templateData.authentication.apps.length}
              </div>
            )}
            <div>
              <Text strong>Security Recommendation:</Text> {templateData.authentication.securityRecommendation ? 'Yes' : 'No'}
            </div>
            <div>
              <Text strong>Code Expiration:</Text> {templateData.authentication.codeExpiration ? `${templateData.authentication.expirationTime} minutes` : 'No'}
            </div>
            <div>
              <Text strong>Message Validity:</Text> {templateData.validityPeriod.enabled ? `${templateData.validityPeriod.value} ${templateData.validityPeriod.unit}` : 'Standard (10 minutes)'}
            </div>
          </Space>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <div className="review-template-preview review-template-preview--inline">
            {renderLivePreviewCard()}
          </div>
        </Col>
      </Row>
      
      <Alert
        message="Approval Process"
        description="Your template will be submitted to WhatsApp for approval. This typically takes 24-48 hours. You'll receive a notification once approved."
        type="info"
        showIcon
        style={{ marginTop: 24 }}
      />
    </Card>
  );

  // Regular Template Builder (existing functionality)
  const renderRegularTemplateBuilder = () => (
    <Row gutter={[24, 16]}>
      <Col xs={24} sm={24} md={24} lg={14} xl={14}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          
          {/* Type of Variable Section */}
          <div className="form-section">
            <h2 className="section-title">Type of Variable</h2>
            <p className="section-description">Select variable type of your template</p>
            
            <div className="variable-format-cards">
              <div 
                className={`variable-format-card ${templateData.variableType === 'number' ? 'selected' : ''}`}
                onClick={() => handleVariableTypeChange('number')}
              >
                <div className="variable-format-header">
                  <div className="variable-format-icon">🔢</div>
                  <div className="variable-format-title">Number Format</div>
                </div>
                <div className="variable-format-description">Variables like {'{{1}}'}, {'{{2}}'}, {'{{3}}'}</div>
                <div className="variable-format-example">Example: Hello {'{{1}}'}, your order {'{{2}}'} is ready!</div>
              </div>
              
              <div 
                className={`variable-format-card ${templateData.variableType === 'name' ? 'selected' : ''}`}
                onClick={() => handleVariableTypeChange('name')}
              >
                <div className="variable-format-header">
                  <div className="variable-format-icon">📝</div>
                  <div className="variable-format-title">Name Format</div>
                </div>
                <div className="variable-format-description">Variables like {'{{name}}'}, {'{{order_id}}'}, {'{{date}}'}</div>
                <div className="variable-format-example">Example: Hello {'{{name}}'}, your order {'{{order_id}}'} is ready!</div>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <Card title="Header (Optional)" size="small">
            <Form.Item label="Header Type">
              <Radio.Group
                value={templateData.components.header.type}
                onChange={(e) => setTemplateData(prev => ({
                  ...prev,
                  components: {
                    ...prev.components,
                    header: { 
                      ...prev.components.header, 
                      type: e.target.value, 
                      content: '',
                      mediaUrl: null,
                      mediaFile: null
                    }
                  }
                }))}
              >
                <Radio value="none">None</Radio>
                <Radio value="text">Text</Radio>
                <Radio value="image">Image</Radio>
                <Radio value="video">Video</Radio>
                <Radio value="document">Document</Radio>
              </Radio.Group>
            </Form.Item>

            {templateData.components.header.type === 'text' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <Button
                    type="dashed"
                    icon={<ThunderboltOutlined style={{ color: '#25d366' }} />}
                    onClick={() => openAiCopyGenerator('header')}
                    size="small"
                    style={{ color: '#25d366', borderColor: '#25d366' }}
                  >
                    Generate with AI
                  </Button>
                </div>
                <Form.Item label="Header Text">
                  <Input
                    placeholder="Enter header text (max 60 characters)"
                    maxLength={60}
                    value={templateData.components.header.content}
                    onChange={(e) => updateVariables('header', e.target.value)}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {templateData.components.header.content?.length || 0}/60 characters
                  </Text>
                </Form.Item>

                {/* Header Variables Validation */}
                {templateData.components.header.validationErrors?.length > 0 && (
                  <Alert
                    message="Header Validation Errors"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {templateData.components.header.validationErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    }
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Header Variable Samples */}
                {(() => {
                  const allHeaderVariables = templateData.components.header.variables || [];
                  const validHeaderVariables = getValidVariables(allHeaderVariables, templateData.variableType);
                  const headerValidation = validateVariables(templateData.components.header.content, templateData.variableType, 'header');
                  const hasHeaderErrors = headerValidation.errors.length > 0;
                  
                  // For headers, show sample for the first valid variable only (since WhatsApp allows max 1 variable)
                  // Even if there are validation errors about multiple variables, still show sample for the first one
                  const firstValidVariable = validHeaderVariables.length > 0 ? [validHeaderVariables[0]] : [];
                  
                  return firstValidVariable.length > 0 && (
                    <div className="variable-samples-section" style={{ marginBottom: 16 }}>
                      <Alert
                        message={<span style={{fontSize: 13, fontWeight: 600}}>Samples for header content</span>}
                        description={<span style={{fontSize: 12, lineHeight: 1.3, display: 'block', marginTop: 2}}>Provide examples of variables to help us review. Do not include customer information.</span>}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16, padding: '8px 12px' }}
                      />
                      
                      <Space direction="vertical" style={{ width: '100%', gap: 12 }}>
                        {firstValidVariable.map(variable => {
                          const sampleValue = templateData.components.header.variableSamples?.[variable] || '';
                          const isRequired = !sampleValue.trim();
                          
                          return (
                            <Form.Item
                              key={`header-${variable}`}
                              label={<span style={{fontWeight: 600}}>{`{{${variable}}}`}</span>}
                              style={{ marginBottom: 0 }}
                              validateStatus={isRequired ? 'error' : 'success'}
                            >
                              <Input
                                placeholder={
                                  templateData.variableType === 'number' 
                                    ? (variable === '1' ? 'e.g., Welcome' : variable === '2' ? 'e.g., Alert' : 'e.g., Header text')
                                    : (variable.includes('name') ? 'e.g., John Doe' : 
                                       variable.includes('company') ? 'e.g., ABC Corp' :
                                       variable.includes('title') ? 'e.g., Important Update' :
                                       'e.g., Header content')
                                }
                                value={sampleValue}
                                onChange={(e) => updateHeaderVariableSample(variable, e.target.value)}
                                maxLength={100}
                                status={isRequired ? 'error' : undefined}
                                size="small"
                              />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text type="secondary" style={{ fontSize: 11, color: '#666' }}>
                                  {sampleValue.length}/100 characters
                                </Text>
                                {isRequired && (
                                  <Text type="danger" style={{ fontSize: 11 }}>
                                    Required *
                                  </Text>
                                )}
                              </div>
                            </Form.Item>
                          );
                        })}
                      </Space>
                    </div>
                  );
                })()}
              </>
            )}

            {['image', 'video', 'document'].includes(templateData.components.header.type) && (
              <Form.Item label={`Upload ${templateData.components.header.type}`}>
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  onChange={handleFileUpload}
                  accept={templateData.components.header.type === 'image' ? 'image/*' : 
                         templateData.components.header.type === 'video' ? 'video/*' : '.pdf'}
                  fileList={templateData.components.header.mediaFile ? [{
                    uid: '1',
                    name: templateData.components.header.mediaFile.name,
                    status: 'done',
                    url: templateData.components.header.mediaUrl,
                  }] : []}
                >
                  {templateData.components.header.mediaFile ? null : (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Supported formats: {templateData.components.header.type === 'image' ? 'JPEG, PNG' : 
                                    templateData.components.header.type === 'video' ? 'MP4' : 'PDF'}
                </Text>
              </Form.Item>
            )}
          </Card>

          {/* Body Section */}
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>Body <span style={{ color: 'red' }}>*</span></span>
                <Space size="small">
                  <Button
                    type="dashed"
                    icon={<ThunderboltOutlined style={{ color: '#25d366' }} />}
                    onClick={() => openAiCopyGenerator('body')}
                    size="small"
                    style={{ color: '#25d366', borderColor: '#25d366' }}
                  >
                    Generate with AI
                  </Button>
                  {/* Emoji Picker Button */}
                  <div style={{ position: 'relative' }}>
                    {renderEmojiPicker('body', (emojiData) => handleEmojiSelect(emojiData.emoji, 'body'))}
                    <Button 
                      type="dashed"
                      icon={<SmileOutlined />}
                      onClick={(event) => toggleEmojiPicker('body', event)}
                      size="small"
                      style={{
                        color: showEmojiPicker && emojiPickerTarget === 'body' ? '#1890ff' : undefined
                      }}
                    >
                      Emoji
                    </Button>
                  </div>
                  {/* Add Variable Button - always show */}
                  {templateData.addressBook && addressBookColumns.length > 0 ? (
                    <Dropdown
                      menu={{
                        items: addressBookColumns.map((column, index) => ({
                          key: column.key,
                          label: `${column.label} - {{${column.key}}}`,
                          onClick: () => handleVariableInsert(column.key)
                        }))
                      }}
                      placement="bottomRight"
                      trigger={['click']}
                    >
                      <Button 
                        type="dashed" 
                        icon={<PlusOutlined />}
                        size="small"
                        className="add-variable-button"
                      >
                        Add Variable
                      </Button>
                    </Dropdown>
                  ) : (
                    <Button 
                      type="dashed" 
                      icon={<PlusOutlined />}
                      onClick={handleDirectVariableInsert}
                      size="small"
                      className="add-variable-button"
                    >
                      Add Variable
                    </Button>
                  )}
                </Space>
              </div>
            } 
            size="small">
            <Form.Item
              // label="Message Content"
              validateStatus={fieldErrors.body && touchedFields.body ? 'error' : ''}
              help={fieldErrors.body && touchedFields.body ? fieldErrors.body : ''}
              rules={[{ required: true, message: 'Please enter message content' }]}
            >
              <TextArea
                rows={6}
                placeholder={`Enter your message content. Use ${templateData.variableType === 'number' ? '{{1}}, {{2}}, {{3}}' : '{{customer_name}}, {{order_id}}'} format for dynamic content`}
                value={templateData.components.body.content}
                onChange={(e) => {
                  updateVariables('body', e.target.value);
                  validateField('body', e.target.value);
                }}
                maxLength={1024}
                className={fieldErrors.body && touchedFields.body ? 'error' : ''}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {templateData.components.body.content?.length || 0}/1024 characters
                </Text>
                {/* Emoji Picker Button */}
                <div style={{ position: 'relative' }}>
                  {renderEmojiPicker('body_main', (emojiData) => handleEmojiSelect(emojiData.emoji, 'body'))}
                  <Button 
                    type="text"
                    icon={<SmileOutlined />}
                    onClick={(event) => toggleEmojiPicker('body_main', event)}
                    size="small"
                    style={{
                      color: showEmojiPicker && emojiPickerTarget === 'body_main' ? '#1890ff' : '#8c8c8c'
                    }}
                  >
                    Add Emoji
                  </Button>
                </div>
              </div>
            </Form.Item>

            {/* Validation Errors Display */}
            {templateData.components.body.validationErrors && templateData.components.body.validationErrors.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {templateData.components.body.validationErrors.map((error, index) => (
                  <Alert
                    key={index}
                    message="Variable Formatting Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 8 }}
                    action={
                      <Button 
                        size="small" 
                        type="link"
                        onClick={() => window.open('https://developers.facebook.com/docs/whatsapp/message-templates/guidelines/', '_blank')}
                      >
                        Learn more
                      </Button>
                    }
                  />
                ))}
              </div>
            )}

            {/* Validation Warnings Display */}
            {templateData.components.body.validationWarnings && templateData.components.body.validationWarnings.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {templateData.components.body.validationWarnings.map((warning, index) => (
                  <Alert
                    key={index}
                    message="Variable Formatting Warning"
                    description={warning}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                    action={
                      <Button 
                        size="small" 
                        type="link"
                        onClick={() => window.open('https://developers.facebook.com/docs/whatsapp/message-templates/guidelines/', '_blank')}
                      >
                        Learn more
                      </Button>
                    }
                  />
                ))}
              </div>
            )}

            {/* Variables Found Section */}
            {(templateData.components.header.variables.length > 0 || templateData.components.body.variables.length > 0) && (
              <div>
                <Text strong>Variables found:</Text>
                
                {/* Header Variables */}
                {templateData.components.header.variables.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>Header:</Text>
                    <div style={{ marginTop: 4 }}>
                      {(() => {
                        // For header variables display, only show the first valid variable since WhatsApp allows max 1 variable
                        const allHeaderVariables = templateData.components.header.variables || [];
                        const validHeaderVariables = getValidVariables(allHeaderVariables, templateData.variableType);
                        const firstValidVariable = validHeaderVariables.length > 0 ? [validHeaderVariables[0]] : [];
                        
                        // Show first valid variable as green, and mark additional variables as invalid (red with ❌)
                        return templateData.components.header.variables.map((variable, index) => {
                          const isFirstValidVariable = firstValidVariable.includes(variable);
                          const isValid = getValidVariables([variable], templateData.variableType).length > 0;
                          const duplicateVariables = templateData.components.header.duplicateVariables || [];
                          const isDuplicate = duplicateVariables.includes(variable);
                          
                          // Mark as issue if: not the first valid variable, OR invalid format, OR duplicate
                          const hasIssue = !isFirstValidVariable || !isValid || isDuplicate;
                          
                          return (
                            <Tag 
                              key={`header-${variable}`} 
                              color={hasIssue ? "red" : "green"} 
                              style={{ margin: 2 }}
                            >
                              {`{{${variable}}}`}
                              {hasIssue && " ❌"}
                            </Tag>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Body Variables */}
                {templateData.components.body.variables.length > 0 && (
                  <div style={{ marginTop: templateData.components.header.variables.length > 0 ? 12 : 8 }}>
                    {templateData.components.header.variables.length > 0 && (
                      <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>Body:</Text>
                    )}
                    <div style={{ marginTop: 4 }}>
                      {templateData.components.body.variables.map(variable => {
                        const isValid = getValidVariables([variable], templateData.variableType).length > 0;
                        const duplicateVariables = templateData.components.body.duplicateVariables || [];
                        const isDuplicate = duplicateVariables.includes(variable);
                        const hasIssue = !isValid || isDuplicate;
                        
                        return (
                          <Tag 
                            key={`body-${variable}`} 
                            color={hasIssue ? "red" : "blue"} 
                            style={{ margin: 2 }}
                          >
                            {`{{${variable}}}`}
                            {hasIssue && " ❌"}
                          </Tag>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {(() => {
                  const headerInvalidVariables = templateData.components.header.variables.filter(
                    variable => getValidVariables([variable], templateData.variableType).length === 0
                  );
                  const headerDuplicateVariables = templateData.components.header.duplicateVariables || [];
                  const bodyInvalidVariables = templateData.components.body.variables.filter(
                    variable => getValidVariables([variable], templateData.variableType).length === 0
                  );
                  const bodyDuplicateVariables = templateData.components.body.duplicateVariables || [];
                  const hasIssues = headerInvalidVariables.length > 0 || headerDuplicateVariables.length > 0 || 
                                  bodyInvalidVariables.length > 0 || bodyDuplicateVariables.length > 0;
                  
                  return hasIssues && (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      Variables with ❌ are invalid for the selected format type or are duplicates and won't require sample values.
                    </Text>
                  );
                })()}
              </div>
            )}

            {/* Variable Samples Section - Only show for valid variables */}
            {(() => {
              const validVariables = getValidVariables(templateData.components.body.variables, templateData.variableType);
              return validVariables.length > 0 && (
                <div className="variable-samples-section" style={{ marginTop: 16 }}>
                  <Alert
                    message={<span style={{fontSize: 13, fontWeight: 600}}>Samples for body content</span>}
                    description={<span style={{fontSize: 12, lineHeight: 1.3, display: 'block', marginTop: 2}}>Add an example for each variable in your body text to help us review. Do not use real customer info.</span>}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16, padding: '8px 12px' }}
                  />
                  
                  <Space direction="vertical" style={{ width: '100%', gap: 12 }}>
                    {validVariables.map(variable => {
                      const sampleValue = templateData.components.body.variableSamples?.[variable] || '';
                      const isRequired = !sampleValue.trim();
                      
                      return (
                        <Form.Item
                          key={variable}
                          label={<span style={{fontWeight: 600}}>{`{{${variable}}}`}</span>}
                          style={{ marginBottom: 0 }}
                          validateStatus={isRequired ? 'error' : 'success'}
                        >
                          <Input
                            placeholder={
                              templateData.variableType === 'number' 
                                ? (variable === '1' ? 'e.g., John Doe' : variable === '2' ? 'e.g., ORD123456' : 'e.g., Sample text')
                                : (variable.includes('name') ? 'e.g., John Doe' : 
                                   variable.includes('order') || variable.includes('id') ? 'e.g., ORD123456' :
                                   variable.includes('amount') || variable.includes('price') ? 'e.g., $99.99' :
                                   variable.includes('date') || variable.includes('time') ? 'e.g., Jan 15, 2024' :
                                   'e.g., Sample content')
                            }
                            value={sampleValue}
                            onChange={(e) => updateBodyVariableSample(variable, e.target.value)}
                            maxLength={100}
                            status={isRequired ? 'error' : undefined}
                            size="small"
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 11, color: '#666' }}>
                              {sampleValue.length}/100 characters
                            </Text>
                            {isRequired && (
                              <Text type="danger" style={{ fontSize: 11 }}>
                                Required *
                              </Text>
                            )}
                          </div>
                        </Form.Item>
                      );
                    })}
                  </Space>
                </div>
              );
            })()}
          </Card>

          {/* Footer Section */}
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>Footer (Optional)</span>
                {/* Emoji Picker Button for Footer */}
                <div style={{ position: 'relative' }}>
                  {renderEmojiPicker('footer', (emojiData) => handleEmojiSelect(emojiData.emoji, 'footer'))}
                  <Button 
                    type="dashed"
                    icon={<SmileOutlined />}
                    onClick={(event) => toggleEmojiPicker('footer', event)}
                    size="small"
                    style={{
                      color: showEmojiPicker && emojiPickerTarget === 'footer' ? '#1890ff' : undefined
                    }}
                  >
                    Emoji
                  </Button>
                </div>
              </div>
            } 
            size="small"
          >
            <Form.Item label="Footer Text">
              <Input
                placeholder="Enter footer text (max 60 characters)"
                maxLength={60}
                value={templateData.components.footer.content}
                onChange={(e) => updateVariables('footer', e.target.value)}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {templateData.components.footer.content?.length || 0}/60 characters
              </Text>
            </Form.Item>
            {templateData.components.footer.validationErrors?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {templateData.components.footer.validationErrors.map((error, index) => (
                  <Alert
                    key={index}
                    message="Footer Validation Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 8 }}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* Buttons Section */}
          <Card 
            className="atb-buttons-card"
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Buttons (Optional)</span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {templateData.components.buttons.length}/3 buttons
                </Text>
              </div>
            }
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {templateData.components.buttons.map((button, index) => (
                <Card key={button.id} size="small" style={{ background: '#fafafa', overflow: 'visible' }}>
                  <Row gutter={[8, 8]} align="middle">
                    <Col xs={24} sm={6} md={6} lg={6} xl={6}>
                      <Select
                        value={button.type}
                        onChange={(value) => updateButton(button.id, 'type', value)}
                        style={{ width: '100%' }}
                      >
                        {buttonTypes.map(type => (
                          <Option key={type.value} value={type.value}>
                            {type.icon} {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                      <Input
                        placeholder="Button text"
                        value={button.text}
                        onChange={(e) => updateButton(button.id, 'text', e.target.value)}
                        maxLength={button.type === 'flow' ? 25 : 20}
                      />
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                      {button.type === 'url' && (
                        <div>
                          <Input
                            placeholder="https://example.com"
                            value={button.url}
                            status={buttonUrlErrors[button.id] ? 'error' : ''}
                            onChange={(e) => updateButton(button.id, 'url', e.target.value)}
                            onBlur={() => setButtonUrlErrors(prev => ({ ...prev, [button.id]: validateUrl(button.url) }))}
                          />
                          {buttonUrlErrors[button.id] && (
                            <div style={{ color: '#ff4d4f', fontSize: 11, marginTop: 2 }}>{buttonUrlErrors[button.id]}</div>
                          )}
                        </div>
                      )}
                      {button.type === 'phone' && (
                        <PhoneInput
                          country={'in'}
                          value={button.phone}
                          onChange={(value) => updateButton(button.id, 'phone', '+' + value)}
                          enableSearch
                          disableSearchIcon
                          countryCodeEditable={false}
                          inputStyle={{ width: '100%', height: '32px', fontSize: '14px' }}
                          dropdownClass="atb-phone-dropdown"
                        />
                      )}
                      {button.type === 'flow' && (
                        <Tag color={getFlowButtonComponentForSubmit() ? 'green' : 'blue'}>
                          {getFlowButtonComponentForSubmit() ? 'Flow ready' : 'Configure below'}
                        </Tag>
                      )}
                    </Col>
                    <Col xs={24} sm={2} md={2} lg={2} xl={2}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeButton(button.id)}
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>
                </Card>
              ))}

              {templateData.components.buttons.length < 3 && (
                <div>
                  <Text strong style={{ marginBottom: 12, display: 'block' }}>Add Button:</Text>
                  <Space wrap>
                    {buttonTypes.map(type => (
                      <Button
                        key={type.value}
                        size="small"
                        onClick={() => addButton(type.value)}
                        icon={type.icon}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </Space>
                </div>
              )}
            </Space>
          </Card>

          {renderFlowBuilderCard()}

          {/* Message Validity Period for Utility Templates */}
          {templateData.category === 'Utility' && (
            <Card title="Message Validity Period" size="small">
              <Alert
                message="Message Validity Period"
                description="Set how long your message remains valid before expiring. Undelivered messages won't be charged."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Form.Item
                label="Set custom validity period for your message"
              >
                <Switch
                  checked={templateData.validityPeriod.enabled}
                  onChange={(checked) => setTemplateData(prev => ({
                    ...prev,
                    validityPeriod: {
                      ...prev.validityPeriod,
                      enabled: checked
                    }
                  }))}
                  checkedChildren="Custom"
                  unCheckedChildren="Standard"
                />
                <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  {templateData.validityPeriod.enabled 
                    ? 'Custom validity period selected' 
                    : 'Use standard 10 minutes WhatsApp message validity period'
                  }
                </div>
              </Form.Item>

              {templateData.validityPeriod.enabled && (
                <>
                  <Form.Item label="Validity Period">
                    <Select
                      style={{ width: '100%' }}
                      value={`${templateData.validityPeriod.value} ${templateData.validityPeriod.unit}`}
                      onChange={(value) => {
                        const [val, unit] = value.split(' ');
                        setTemplateData(prev => ({
                          ...prev,
                          validityPeriod: {
                            ...prev.validityPeriod,
                            value: parseInt(val),
                            unit: unit
                          }
                        }));
                      }}
                    >
                      <Option value="30 seconds">30 seconds</Option>
                      <Option value="1 minute">1 minute</Option>
                      <Option value="2 minutes">2 minutes</Option>
                      <Option value="5 minutes">5 minutes</Option>
                      <Option value="10 minutes">10 minutes</Option>
                      <Option value="15 minutes">15 minutes</Option>
                      <Option value="30 minutes">30 minutes</Option>
                      <Option value="1 hour">1 hour</Option>
                      <Option value="3 hours">3 hours</Option>
                      <Option value="6 hours">6 hours</Option>
                      <Option value="12 hours">12 hours</Option>
                      <Option value="1 day">1 day</Option>
                    </Select>
                  </Form.Item>
                  
                  <Alert
                    message="Recommendation"
                    description="For authentication templates, it's recommended to set a validity period that matches your expected user response time while considering security requirements."
                    type="warning"
                    showIcon
                    style={{ fontSize: 11 }}
                  />
                </>
              )}
            </Card>
          )}
        </Space>
      </Col>

      <Col xs={24} sm={24} md={24} lg={10} xl={10}>
        <div className="mobile-preview-container">
          <Card title="Live Preview" size="small" className="preview-card">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {renderWhatsAppPreview()}
            </div>
          </Card>

          {/* Variables Summary */}
          <Card title="Variables Summary" size="small" style={{ marginTop: 16 }}>
            {(() => {
              const hasHeaderVariables = templateData.components.header.variables.length > 0;
              const hasBodyVariables = templateData.components.body.variables.length > 0;
              const hasFooterVariables = templateData.components.footer.variables.length > 0;
              const hasAnyVariables = hasHeaderVariables || hasBodyVariables || hasFooterVariables;
              
              return hasAnyVariables ? (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {/* Header Variables */}
                  {hasHeaderVariables && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block' }}>
                        Header:
                      </Text>
                      <Space wrap size="small">
                        {templateData.components.header.variables.map(variable => (
                          <div key={`header-${variable}`} style={{ 
                            padding: '4px 8px', 
                            background: '#f0f8ff', 
                            borderRadius: 4,
                            border: '1px solid #d6e4ff'
                          }}>
                            <Text code style={{ fontSize: 11 }}>{`{{${variable}}}`}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>
                  )}

                  {/* Body Variables */}
                  {hasBodyVariables && (
                    <div style={{ marginTop: hasHeaderVariables ? 12 : 0 }}>
                      {hasHeaderVariables && (
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block' }}>
                          Body:
                        </Text>
                      )}
                      <Space wrap size="small">
                        {templateData.components.body.variables.map(variable => (
                          <div key={`body-${variable}`} style={{ 
                            padding: '4px 8px', 
                            background: '#f6ffed', 
                            borderRadius: 4,
                            border: '1px solid #b7eb8f'
                          }}>
                            <Text code style={{ fontSize: 11 }}>{`{{${variable}}}`}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>
                  )}

                  {/* Footer Variables */}
                  {hasFooterVariables && (
                    <div style={{ marginTop: (hasHeaderVariables || hasBodyVariables) ? 12 : 0 }}>
                      {(hasHeaderVariables || hasBodyVariables) && (
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block' }}>
                          Footer:
                        </Text>
                      )}
                      <Space wrap size="small">
                        {templateData.components.footer.variables.map(variable => (
                          <div key={`footer-${variable}`} style={{ 
                            padding: '4px 8px', 
                            background: '#fff7e6', 
                            borderRadius: 4,
                            border: '1px solid #ffd591'
                          }}>
                            <Text code style={{ fontSize: 11 }}>{`{{${variable}}}`}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>
                  )}
                </Space>
              ) : (
                <Text type="secondary">No variables defined</Text>
              );
            })()}
          </Card>
        </div>
      </Col>
    </Row>
  );

  const renderAiDraftVariants = () => {
    const variants = getAiDraftVariants(aiDraftResult);
    if (!variants.length) return null;

    const recommendedIndex = getAiDraftRecommendedIndex(aiDraftResult, variants);
    const selectedVariant = variants[aiDraftSelectedVariantIndex] || variants[recommendedIndex] || variants[0];
    const selectedReady = isAiDraftVariantReady(selectedVariant);

    return (
      <div className="ai-draft-variants-panel">
        <div className="ai-draft-variants-header">
          <div>
            <Text strong>Generated drafts</Text>
            <div><Text type="secondary">Select one, then review and edit it in the builder.</Text></div>
          </div>
          <Tag color={aiDraftResult?.readyForCreate === false ? 'orange' : 'green'}>
            {variants.length} variant{variants.length === 1 ? '' : 's'}
          </Tag>
        </div>

        <div className="ai-draft-variant-grid">
          {variants.map((variant, index) => {
            const draft = getAiDraftVariantDraft(variant);
            const bodyText = getAiDraftVariantBodyText(variant);
            const errors = variant?.validation?.errors || [];
            const warnings = variant?.validation?.warnings || [];
            const isSelected = index === aiDraftSelectedVariantIndex;
            const isRecommended = index === recommendedIndex;
            const isReady = isAiDraftVariantReady(variant);

            return (
              <div
                key={variant.id || index}
                className={`ai-draft-variant-card${isSelected ? ' selected' : ''}${!isReady ? ' needs-review' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setAiDraftSelectedVariantIndex(index);
                  setAiDraftError(errors[0] || warnings[0] || '');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setAiDraftSelectedVariantIndex(index);
                    setAiDraftError(errors[0] || warnings[0] || '');
                  }
                }}
              >
                <div className="ai-draft-variant-topline">
                  <Space size={6} wrap>
                    <Text strong>{variant.label || `Variant ${index + 1}`}</Text>
                    {isRecommended && <Tag color="blue">Recommended</Tag>}
                    <Tag color={isReady ? 'green' : 'orange'}>
                      {isReady ? 'Ready' : 'Needs review'}
                    </Tag>
                  </Space>
                </div>

                <div className="ai-draft-variant-meta">
                  <Text type="secondary">{draft.templatename || 'Untitled template'}</Text>
                  <Space size={6} wrap>
                    {draft.templatecategory && <Tag>{String(draft.templatecategory).toUpperCase()}</Tag>}
                    {draft.templatelanguage && <Tag>{draft.templatelanguage}</Tag>}
                  </Space>
                </div>

                <div className="ai-draft-variant-preview">
                  {bodyText || 'No body copy returned'}
                </div>

                {(errors.length > 0 || warnings.length > 0) && (
                  <div className="ai-draft-variant-validation">
                    {errors.slice(0, 2).map((error, errorIndex) => (
                      <div key={`error-${errorIndex}`} className="ai-draft-validation-line error">
                        <ExclamationCircleOutlined /> {error}
                      </div>
                    ))}
                    {warnings.slice(0, 2).map((warning, warningIndex) => (
                      <div key={`warning-${warningIndex}`} className="ai-draft-validation-line warning">
                        <InfoCircleOutlined /> {warning}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="ai-draft-variant-actions">
          <Button
            type="primary"
            icon={<EditOutlined />}
            disabled={!selectedReady}
            onClick={() => applyAiDraftVariantToBuilder(selectedVariant)}
          >
            Use Draft in Builder
          </Button>
          {!selectedReady && (
            <Text type="secondary">Fix the selected draft by refining the prompt or choose a ready variant.</Text>
          )}
        </div>
      </div>
    );
  };

  // Render Category Selection Screen
  const renderCategoryScreen = () => (
    <div className="category-selection-screen">
      <div className="form-section">
        <Card className="ai-draft-card" bordered={false} style={{ marginBottom: 24 }}>
          <div className="ai-draft-orb" />
          <div className="ai-draft-header">
            <div>
              <h3 className="ai-draft-title">Create a WhatsApp template from one prompt</h3>
              <p className="ai-draft-subtitle">
                Generate three validated drafts, choose one, then review it in the normal builder before submitting.
              </p>
            </div>
            <Tag color="green" className="ai-draft-badge">3 drafts</Tag>
          </div>

          <div className="ai-draft-composer">
            <TextArea
              value={aiDraftPrompt}
              onChange={(e) => {
                setAiDraftPrompt(e.target.value);
                setAiDraftError('');
                setAiDraftResult(null);
              }}
              onPressEnter={(e) => {
                if ((e.ctrlKey || e.metaKey) && !aiDraftLoading) {
                  handleGenerateAiDraft();
                }
              }}
              placeholder="Example: Create a friendly WhatsApp marketing template for a New Year 50% off sale. Mention customer name, discount and offer end date."
              autoSize={{ minRows: 4, maxRows: 7 }}
              maxLength={1200}
              disabled={aiDraftLoading}
              className="ai-draft-textarea"
            />
            <div className="ai-draft-actions">
              <Text type="secondary" className="ai-draft-hint">
                Ctrl + Enter to generate
              </Text>
              <Button
                icon={<ThunderboltOutlined />}
                loading={aiDraftLoading}
                onClick={handleGenerateAiDraft}
                disabled={!aiDraftPrompt.trim()}
                className="ai-draft-submit"
              >
                Create with AI
              </Button>
            </div>
          </div>

          <div className="ai-draft-suggestions">
            {[
              'Create a utility template for order shipped with order id and delivery date.',
              'Create a marketing template for a weekend flash sale with coupon and expiry.',
              'Create a professional appointment reminder with customer name and appointment time.'
            ].map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="ai-draft-suggestion"
                onClick={() => {
                  setAiDraftPrompt(prompt);
                  setAiDraftError('');
                  setAiDraftResult(null);
                }}
                disabled={aiDraftLoading}
              >
                {prompt}
              </button>
            ))}
          </div>

          {aiDraftLoading && (
            <div className="ai-draft-loader">
              <div className="ai-draft-loader-header">
                <Skeleton.Avatar active size="small" />
                <div style={{ flex: 1 }}>
                  <Skeleton.Input active size="small" style={{ width: '42%' }} />
                </div>
              </div>
              <Skeleton active paragraph={{ rows: 4 }} title={false} />
              <div className="ai-draft-loader-grid">
                <Skeleton.Input active size="small" style={{ width: '100%' }} />
                <Skeleton.Input active size="small" style={{ width: '100%' }} />
                <Skeleton.Input active size="small" style={{ width: '100%' }} />
              </div>
            </div>
          )}

          {!aiDraftLoading && aiDraftError && (
            <Alert
              message="AI draft needs a safer prompt"
              description={aiDraftError}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {!aiDraftLoading && aiDraftResult?.validation?.warnings?.length > 0 && !aiDraftError && (
            <Alert
              message="Draft generated with review notes"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {aiDraftResult.validation.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              }
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {!aiDraftLoading && renderAiDraftVariants()}
        </Card>

        <Divider plain>or build manually</Divider>
        
        <Card title="Template Category" size="small" style={{ marginBottom: 24 }}>

          <Form.Item 
            label="Category"
            validateStatus={fieldErrors.category && touchedFields.category ? 'error' : ''}
            help={fieldErrors.category && touchedFields.category ? fieldErrors.category : null}
            required
          >
            <div className="category-buttons" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className={`category-button ${templateData.category === 'MARKETING' ? 'selected' : ''}`}
                onMouseDown={() => setActiveButton('MARKETING')}
                onMouseUp={() => setActiveButton(null)}
                onMouseLeave={() => setActiveButton(null)}
                onClick={() => {
                  setTemplateData(prev => ({
                    ...prev,
                    category: 'MARKETING'
                  }));
                  // Clear error when user selects
                  if (fieldErrors.category) {
                    setFieldErrors(prev => ({
                      ...prev,
                      category: ''
                    }));
                  }
                  // Reset to screen-based flow for non-authentication templates
                  setUseStepFlow(false);
                }}
                style={{
                  padding: '12px 24px',
                  border: activeButton === 'MARKETING'
                    ? '2px solid #389e0d'
                    : templateData.category === 'MARKETING' 
                      ? '2px solid #52c41a' 
                      : '1px solid #d9d9d9',
                  borderRadius: '6px',
                  background: activeButton === 'MARKETING'
                    ? '#f0faeb'
                    : templateData.category === 'MARKETING' 
                      ? '#f6ffed' 
                      : '#fff',
                  color: templateData.category === 'MARKETING' ? '#52c41a' : '#666',
                  cursor: 'pointer',
                  fontWeight: templateData.category === 'MARKETING' ? '600' : '400',
                  outline: 'none'
                }}
              >
                📈 Marketing
              </button>
              
              <button
                type="button"
                className={`category-button ${templateData.category === 'UTILITY' ? 'selected' : ''}`}
                onMouseDown={() => setActiveButton('UTILITY')}
                onMouseUp={() => setActiveButton(null)}
                onMouseLeave={() => setActiveButton(null)}
                onClick={() => {
                  setTemplateData(prev => ({
                    ...prev,
                    category: 'UTILITY'
                  }));
                  // Clear error when user selects
                  if (fieldErrors.category) {
                    setFieldErrors(prev => ({
                      ...prev,
                      category: ''
                    }));
                  }
                  // Reset to screen-based flow for non-authentication templates
                  setUseStepFlow(false);
                }}
                style={{
                  padding: '12px 24px',
                  border: activeButton === 'UTILITY'
                    ? '2px solid #d46b08'
                    : templateData.category === 'UTILITY' 
                      ? '2px solid #fa8c16' 
                      : '1px solid #d9d9d9',
                  borderRadius: '6px',
                  background: activeButton === 'UTILITY'
                    ? '#fff3e0'
                    : templateData.category === 'UTILITY' 
                      ? '#fff7e6' 
                      : '#fff',
                  color: templateData.category === 'UTILITY' ? '#fa8c16' : '#666',
                  cursor: 'pointer',
                  fontWeight: templateData.category === 'UTILITY' ? '600' : '400',
                  outline: 'none'
                }}
              >
                🔧 Utility
              </button>
              
              <button
                type="button"
                className={`category-button ${templateData.category === 'Authentication' ? 'selected' : ''}`}
                onMouseDown={() => setActiveButton('Authentication')}
                onMouseUp={() => setActiveButton(null)}
                onMouseLeave={() => setActiveButton(null)}
                onClick={() => {
                  setTemplateData(prev => ({
                    ...prev,
                    category: 'Authentication',
                    templateName: prev.templateName || 'authentication_template_01' // Set default name if empty
                  }));
                  // Clear error when user selects
                  if (fieldErrors.category) {
                    setFieldErrors(prev => ({
                      ...prev,
                      category: ''
                    }));
                  }
                  // Do not auto-advance; Next button will handle flow
                }}
                style={{
                  padding: '12px 24px',
                  border: activeButton === 'Authentication'
                    ? '2px solid #531dab'
                    : templateData.category === 'Authentication' 
                      ? '2px solid #722ed1' 
                      : '1px solid #d9d9d9',
                  borderRadius: '6px',
                  background: activeButton === 'Authentication'
                    ? '#f5e6ff'
                    : templateData.category === 'Authentication' 
                      ? '#f9f0ff' 
                      : '#fff',
                  color: templateData.category === 'Authentication' ? '#722ed1' : '#666',
                  cursor: 'pointer',
                  fontWeight: templateData.category === 'Authentication' ? '600' : '400',
                  outline: 'none'
                }}
              >
                🔐 Authentication
              </button>
            </div>
            <Text type="secondary" style={{ fontSize: 12, marginTop: '8px', display: 'block' }}>
              Choose the appropriate category for your template
            </Text>
          </Form.Item>
        </Card>
        
      </div>
    </div>
  );

  // Render Variable Type Selection Screen
  const renderVariableTypeSelection = () => (
    <div className="variable-type-selection-screen">
      <div className="form-section">
        <h2 className="section-title">Type of Variable</h2>
        <p className="section-description">Select variable type of your template</p>
        
        <div className="variable-format-cards">
          <div 
            className={`variable-format-card ${selectedVariableType === 'number' ? 'selected' : ''}`}
            onClick={() => handleVariableTypeChange('number')}
          >
            <div className="variable-format-header">
              <div className="variable-format-icon">🔢</div>
              <div className="variable-format-title">Number Format</div>
            </div>
            <div className="variable-format-description">Variables like {'{{1}}'}, {'{{2}}'}, {'{{3}}'}</div>
            <div className="variable-format-example">Example: Hello {'{{1}}'}, your order {'{{2}}'} is ready!</div>
          </div>
          
          <div 
            className={`variable-format-card ${selectedVariableType === 'name' ? 'selected' : ''}`}
            onClick={() => handleVariableTypeChange('name')}
          >
            <div className="variable-format-header">
              <div className="variable-format-icon">📝</div>
              <div className="variable-format-title">Name Format</div>
            </div>
            <div className="variable-format-description">Variables like {'{{name}}'}, {'{{order_id}}'}, {'{{date}}'}</div>
            <div className="variable-format-example">Example: Hello {'{{name}}'}, your order {'{{order_id}}'} is ready!</div>
          </div>
        </div>
        
      </div>
    </div>
  );

  // Render Header Configuration Screen
  const renderHeaderScreen = () => (
    <div className="template-section-screen">
      <div className="form-section">
        <h2 className="section-title">Header <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#888', marginLeft: '8px' }}>(Optional)</span></h2>
        <p className="section-description">Headers can contain text, images, videos, or documents to grab attention.</p>
        
        {/* Template Information Section */}
        <div className="modern-config-panel">
          <div className="modern-panel-header">
            <h3 className="modern-panel-title">Template Information</h3>
          </div>
          <div className="modern-panel-body">
            <Row gutter={24}>
              <Col xs={24} md={14}>
                <Form.Item 
                  label={<span className="modern-label">Template Name</span>}
                  validateStatus={fieldErrors.templateName && touchedFields.templateName ? 'error' : ''}
                  help={fieldErrors.templateName && touchedFields.templateName ? fieldErrors.templateName : null}
                  required
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    className="modern-input"
                    placeholder="e.g., welcome_message_01"
                    value={templateData.templateName}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\s+/g, '_')
                        .toLowerCase()
                        .replace(/[^a-z0-9_]/g, '');
                      
                      setTemplateData(prev => ({ ...prev, templateName: value }));
                      if (fieldErrors.templateName) {
                        setFieldErrors(prev => ({ ...prev, templateName: '' }));
                      }
                    }}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, templateName: true }))}
                    maxLength={50}
                  />
                  <div className="modern-char-count">
                    {templateData.templateName?.length || 0}/50 characters
                  </div>
                </Form.Item>
              </Col>
              
              <Col xs={24} md={10}>
                <Form.Item
                  label={<span className="modern-label">Language</span>}
                  required
                  extra={<span style={{fontSize: '12px', color: '#888'}}>Must match template body.</span>}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    className="modern-select"
                    showSearch
                    placeholder="Select language"
                    value={templateData.language || 'en_US'}
                    onChange={(value) => {
                      setTemplateData(prev => ({ ...prev, language: value }));
                    }}
                    optionFilterProp="label"
                    options={languages.map(lang => ({ value: lang.value, label: lang.label }))}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>
        
        {/* Header Configuration Section */}
        <div className="modern-config-panel">
          <div className="modern-panel-header">
            <h3 className="modern-panel-title">Header Content</h3>
          </div>
          
          <div className="modern-panel-body">
            <Form.Item label={<span className="modern-label">Header Type</span>} style={{ marginBottom: templateData.components.header.type === 'none' ? 0 : 12 }}>
              <Radio.Group
                className="modern-radio-group"
                value={templateData.components.header.type}
                onChange={(e) => setTemplateData(prev => ({
                  ...prev,
                  components: {
                    ...prev.components,
                    header: { 
                      ...prev.components.header, 
                      type: e.target.value, 
                      content: '',
                      mediaUrl: null,
                      mediaFile: null
                    }
                  }
                }))}
              >
                <Radio.Button value="none">None</Radio.Button>
                <Radio.Button value="text">Text</Radio.Button>
                <Radio.Button value="image">Image</Radio.Button>
                <Radio.Button value="video">Video</Radio.Button>
                <Radio.Button value="document">Document</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {templateData.components.header.type === 'text' && (
              <div className="header-type-container">
                <div className="header-text-header">
                   <span className="modern-label" style={{ marginBottom: 0 }}>Header Text</span>
                   <Space size="small">
                      <Button
                        type="primary"
                        ghost
                        icon={<ThunderboltOutlined />}
                        onClick={() => openAiCopyGenerator('header')}
                        size="small"
                        className="modern-ai-btn"
                      >
                        AI Generate
                      </Button>
                      {templateData.addressBook && addressBookColumns.length > 0 ? (
                        <Dropdown
                          menu={{
                            items: addressBookColumns.map((column) => ({
                              key: column.key,
                              label: `${column.label} - {{${column.key}}}`,
                              onClick: () => handleHeaderVariableInsert(column.key)
                            }))
                          }}
                          placement="bottomRight"
                          trigger={['click']}
                        >
                          <Button size="small" type="default" icon={<PlusOutlined />} className="modern-var-btn">
                            Variable
                          </Button>
                        </Dropdown>
                      ) : (
                        <Button 
                          size="small" 
                          type="default" 
                          icon={<PlusOutlined />}
                          onClick={handleHeaderDirectVariableInsert}
                          className="modern-var-btn"
                        >
                          Variable
                        </Button>
                      )}
                    </Space>
                </div>
                
                <Form.Item 
                  validateStatus={fieldErrors.headerContent && touchedFields.headerContent ? 'error' : ''}
                  help={fieldErrors.headerContent && touchedFields.headerContent ? fieldErrors.headerContent : null}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    className="modern-input"
                    placeholder="Enter header text (max 60 characters)"
                    maxLength={60}
                    value={templateData.components.header.content}
                    onChange={(e) => updateVariables('header', e.target.value)}
                  />
                  <div className="modern-char-count">
                    {templateData.components.header.content?.length || 0}/60
                  </div>
                </Form.Item>

                {/* Header Variables Validation */}
                {templateData.components.header.validationErrors?.length > 0 && (
                  <div className="modern-validation-error">
                    <div className="error-header">
                      <i className="fi fi-rr-cross-circle"></i> Header Validation Errors
                    </div>
                    <ul className="error-list">
                      {templateData.components.header.validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Header Variable Samples */}
                {(() => {
                  const allHeaderVariables = templateData.components.header.variables || [];
                  const validHeaderVariables = getValidVariables(allHeaderVariables, templateData.variableType);
                  const firstValidVariable = validHeaderVariables.length > 0 ? [validHeaderVariables[0]] : [];
                  
                  return firstValidVariable.length > 0 && (
                    <div className="modern-samples-section">
                      <div className="samples-info-banner">
                        <i className="fi fi-rr-info"></i>
                        <div>
                          <span className="samples-banner-title">Samples for header content: </span>
                          <span className="samples-banner-text">Provide examples of variables to help Meta review your template. Do not include customer info.</span>
                        </div>
                      </div>
                      
                      <div className="samples-inputs-container">
                        {firstValidVariable.map(variable => {
                          const sampleValue = templateData.components.header.variableSamples?.[variable] || '';
                          const isRequired = !sampleValue.trim();
                          
                          return (
                            <div key={`header-${variable}`} className={`modern-sample-row ${isRequired ? 'has-error' : ''}`}>
                              <div className="sample-var-badge">{`{{${variable}}}`}</div>
                              <div className="sample-input-wrapper">
                                <Input
                                  className="modern-input no-margin"
                                  placeholder={
                                    templateData.variableType === 'number' 
                                      ? (variable === '1' ? 'e.g., Welcome' : variable === '2' ? 'e.g., Alert' : 'e.g., Header text')
                                      : (variable.includes('name') ? 'e.g., John Doe' : 
                                         variable.includes('company') ? 'e.g., ABC Corp' :
                                         variable.includes('title') ? 'e.g., Important Update' :
                                         'e.g., Header content')
                                  }
                                  value={sampleValue}
                                  onChange={(e) => updateHeaderVariableSample(variable, e.target.value)}
                                  maxLength={100}
                                />
                                <div className="sample-footer">
                                  {isRequired && <span className="sample-req-text">Required</span>}
                                  <span className="sample-count">{sampleValue.length}/100</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {['image', 'video', 'document'].includes(templateData.components.header.type) && (
              <div className="header-type-container">
                <Form.Item 
                  label={<span className="modern-label">{`Upload ${templateData.components.header.type}`}</span>}
                  validateStatus={fieldErrors.headerMedia && touchedFields.headerMedia ? 'error' : ''}
                  help={fieldErrors.headerMedia && touchedFields.headerMedia ? fieldErrors.headerMedia : null}
                  style={{ marginBottom: 0 }}
                >
                  <Upload
                    listType="picture-card"
                    className="modern-upload"
                    maxCount={1}
                    beforeUpload={() => false}
                    onChange={handleFileUpload}
                    accept={templateData.components.header.type === 'image' ? 'image/*' : 
                           templateData.components.header.type === 'video' ? 'video/*' : '.pdf'}
                    fileList={templateData.components.header.mediaFile ? [{
                      uid: '1',
                      name: templateData.components.header.mediaFile.name,
                      status: 'done',
                      url: templateData.components.header.mediaUrl,
                    }] : []}
                  >
                    {templateData.components.header.mediaFile ? null : (
                      <div className="modern-upload-inner">
                        <UploadOutlined style={{ fontSize: '20px', color: '#888' }} />
                        <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>Upload Media</div>
                      </div>
                    )}
                  </Upload>
                  <div style={{ fontSize: '13px', color: '#888', marginTop: '12px' }}>
                    Supported formats: {templateData.components.header.type === 'image' ? 'JPEG, PNG' : 
                                      templateData.components.header.type === 'video' ? 'MP4' : 'PDF'}
                  </div>
                </Form.Item>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render Body Configuration Screen
  const renderBodyScreen = () => (
    <div className="template-section-screen">
      <div className="form-section">
        <h2 className="section-title">Body <span style={{ color: 'red' }}>*</span></h2>
        <p className="section-description">Enter your main message content. This is the core of your template.</p>
        
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>Message Content</span>
                <Button 
                  type="dashed"
                  icon={<ThunderboltOutlined style={{ color: '#25d366' }} />}
                  onClick={() => openAiCopyGenerator('body')}
                  size="small"
                  style={{ color: '#25d366', borderColor: '#25d366' }}
                >
                  Generate with AI
                </Button>
            </div>
          } 
          size="small">
          {/* WhatsApp formatting toolbar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 4px',
            borderBottom: '1px solid #f0f0f0',
            marginBottom: 8,
            flexWrap: 'wrap'
          }}>
            <Tooltip title="Bold (*text*)">
              <Button
                type="text"
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyBodyFormat('*')}
                className={`body-toolbar-btn ${bodyToolbarState.bold ? 'is-active' : ''}`}
              >B</Button>
            </Tooltip>
            <Tooltip title="Italic (_text_)">
              <Button
                type="text"
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyBodyFormat('_')}
                className={`body-toolbar-btn ${bodyToolbarState.italic ? 'is-active' : ''}`}
              >I</Button>
            </Tooltip>
            <Tooltip title="Strikethrough (~text~)">
              <Button
                type="text"
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyBodyFormat('~')}
                className={`body-toolbar-btn ${bodyToolbarState.strike ? 'is-active' : ''}`}
              >S</Button>
            </Tooltip>
            <Tooltip title="Bullet list">
              <Button
                type="text"
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyBodyFormat('bullet')}
                className={`body-toolbar-btn ${bodyToolbarState.bullet ? 'is-active' : ''}`}
                icon={<UnorderedListOutlined />}
              />
            </Tooltip>
            <Tooltip title="Numbered list">
              <Button
                type="text"
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyBodyFormat('number')}
                className={`body-toolbar-btn ${bodyToolbarState.ordered ? 'is-active' : ''}`}
                icon={<OrderedListOutlined />}
              />
            </Tooltip>
            <span style={{ marginLeft: 6, color: '#bbb', fontSize: 11 }}>WhatsApp formatting</span>
            {/* Add Variable Button */}
            {templateData.addressBook && addressBookColumns.length > 0 ? (
              <Dropdown
                menu={{
                  items: addressBookColumns.map((column, index) => ({
                    key: column.key,
                    label: `${column.label} - {{${column.key}}}`,
                    onClick: () => handleVariableInsert(column.key)
                  }))
                }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button 
                  type="dashed" 
                  icon={<PlusOutlined />}
                  size="small"
                  className="add-variable-button"
                >
                  Add Variable
                </Button>
              </Dropdown>
            ) : (
              <Button 
                type="dashed" 
                icon={<PlusOutlined />}
                onClick={handleDirectVariableInsert}
                size="small"
                className="add-variable-button"
              >
                Add Variable
              </Button>
            )}
            {/* Emoji Picker Button */}
            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              {renderEmojiPicker('body_textarea', (emojiData) => handleEmojiSelect(emojiData.emoji, 'body'))}
              <Button 
                type="text"
                icon={<SmileOutlined />}
                onClick={(event) => toggleEmojiPicker('body_textarea', event)}
                size="small"
                style={{
                  color: showEmojiPicker && emojiPickerTarget === 'body_textarea' ? '#1890ff' : '#8c8c8c'
                }}
              >
                Add Emoji
              </Button>
            </div>
          </div>
          <Form.Item
            // label="Message Content"
            validateStatus={fieldErrors.body && touchedFields.body ? 'error' : ''}
            help={fieldErrors.body && touchedFields.body ? fieldErrors.body : ''}
            rules={[{ required: true, message: 'Please enter message content' }]}
          >
            <div
              ref={bodyEditorRef}
              contentEditable
              suppressContentEditableWarning
              data-placeholder={`Enter your message content. Use ${templateData.variableType === 'number' ? '{{1}}, {{2}}, {{3}}' : '{{customer_name}}, {{order_id}}'} format for dynamic content`}
              onInput={(e) => {
                const markdown = displayHtmlToMarkdown(e.currentTarget.innerHTML);
                bodyContentInEditor.current = markdown;
                updateVariables('body', markdown);
                validateField('body', markdown);
                syncBodyToolbarState();
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#4096ff';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(5,145,255,0.1)';
                syncBodyToolbarState();
              }}
              onMouseUp={() => {
                syncBodyToolbarState();
              }}
              onKeyUp={() => {
                syncBodyToolbarState();
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = fieldErrors.body && touchedFields.body ? '#ff4d4f' : '#d9d9d9';
                e.currentTarget.style.boxShadow = 'none';
                const markdown = displayHtmlToMarkdown(e.currentTarget.innerHTML);
                validateField('body', markdown);
                setBodyToolbarState({
                  bold: false,
                  italic: false,
                  strike: false,
                  code: false,
                  bullet: false,
                  ordered: false,
                });
              }}
              style={{
                minHeight: 120,
                maxHeight: 300,
                overflowY: 'auto',
                padding: '8px 11px',
                border: `1px solid ${fieldErrors.body && touchedFields.body ? '#ff4d4f' : '#d9d9d9'}`,
                borderRadius: 6,
                fontSize: 14,
                lineHeight: 1.5714,
                color: 'rgba(0,0,0,0.88)',
                backgroundColor: '#ffffff',
                outline: 'none',
                cursor: 'text',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
            />
            <style>{`.body-rich-editor[contenteditable]:empty::before{content:attr(data-placeholder);color:#bfbfbf;pointer-events:none}`}</style>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {templateData.components.body.content?.length || 0}/1024 characters
              </Text>
            </div>
          </Form.Item>

          {/* Validation Errors Display */}
          {templateData.components.body.validationErrors && templateData.components.body.validationErrors.length > 0 && (
            <div style={{ marginTop: 14 }}>
              {templateData.components.body.validationErrors.map((error, index) => (
                <div key={index} className="modern-validation-error">
                  <div className="error-header">
                    <ExclamationCircleOutlined /> Body Validation Error
                  </div>
                  <div style={{ fontSize: 13, color: '#5c0011', marginTop: 2 }}>{error}</div>
                </div>
              ))}
            </div>
          )}

          {/* Body Variable Samples */}
          {(() => {
            const allBodyVariables = templateData.components.body.variables || [];
            const validBodyVariables = getValidVariables(allBodyVariables, templateData.variableType);

            return validBodyVariables.length > 0 && (
              <div className="modern-samples-section" style={{ marginTop: 14 }}>
                {/* Info banner */}
                <div style={{
                  display: 'flex', gap: 10, padding: '12px 14px', background: '#f0f7ff',
                  border: '1px solid #bae0ff', borderRadius: 8, marginBottom: 16, alignItems: 'flex-start'
                }}>
                  <InfoCircleOutlined style={{ fontSize: 15, color: '#1677ff', marginTop: 1, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#003eb3', marginBottom: 3 }}>Samples for body content</div>
                    <div style={{ fontSize: 12, color: '#1d4ed8', lineHeight: 1.5 }}>
                      To help us review your content, provide examples of the variables in the body. Do not include any customer information.
                    </div>
                  </div>
                </div>

                {/* Variable inputs */}
                <div className="samples-inputs-container">
                  {validBodyVariables.map(variable => {
                    const sampleValue = templateData.components.body.variableSamples?.[variable] || '';
                    const hasError = !sampleValue.trim();

                    return (
                      <div key={`body-${variable}`} style={{ marginBottom: hasError ? 24 : 12 }}>
                        <div className={`modern-sample-row ${hasError ? 'has-error' : ''}`}>
                          <span className="sample-var-badge">{'{{' + variable + '}}'}</span>
                          <div className="sample-input-wrapper">
                            <Input
                              className="modern-input"
                              placeholder={
                                templateData.variableType === 'number'
                                  ? (variable === '1' ? 'e.g., John Doe' : variable === '2' ? 'e.g., ORD123' : 'e.g., Sample text')
                                  : (variable.includes('name') ? 'e.g., John Doe' :
                                     variable.includes('order') ? 'e.g., ORD123' :
                                     variable.includes('date') ? 'e.g., 2024-01-15' :
                                     'e.g., Sample content')
                              }
                              value={sampleValue}
                              onChange={(e) => updateBodyVariableSample(variable, e.target.value)}
                              maxLength={100}
                              status={hasError ? 'error' : undefined}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, paddingLeft: 2 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>{sampleValue.length}/100 characters</Text>
                          {hasError && <Text type="danger" style={{ fontSize: 11, fontWeight: 500 }}>Required *</Text>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Variable samples validation error */}
                {fieldErrors.variableSamples && touchedFields.variableSamples && (
                  <div className="redesigned-error-message" style={{ marginTop: 8 }}>
                    {fieldErrors.variableSamples}
                  </div>
                )}
              </div>
            );
          })()}
        </Card>
      </div>
    </div>
  );

  // Render Footer and Buttons Configuration Screen
  const renderFooterScreen = () => (
    <div className="template-section-screen">
      <div className="form-section">
        <h2 className="section-title">Footer & Buttons (Optional)</h2>
        <p className="section-description">Add a footer text and interactive buttons to your template.</p>
        
        {/* Footer Section */}
        <Card title="Footer (Optional)" size="small" style={{ marginBottom: 16 }}>
          <Form.Item label="Footer Text">
            <Input
              placeholder="Enter footer text (max 60 characters)"
              maxLength={60}
              value={templateData.components.footer.content}
              onChange={(e) => updateVariables('footer', e.target.value)}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {templateData.components.footer.content?.length || 0}/60 characters
            </Text>
          </Form.Item>
          {templateData.components.footer.validationErrors?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {templateData.components.footer.validationErrors.map((error, index) => (
                <Alert
                  key={index}
                  message="Footer Validation Error"
                  description={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 8 }}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Buttons Section */}
        <Card 
          className="atb-buttons-card"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Buttons (Optional)</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {templateData.components.buttons.length}/3 buttons
              </Text>
            </div>
          }
          size="small"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {templateData.components.buttons.map((button, index) => (
              <Card key={button.id} size="small" style={{ background: '#fafafa', overflow: 'visible' }}>
                <Row gutter={[8, 8]} align="middle">
                  <Col xs={24} sm={6} md={6} lg={6} xl={6}>
                    <Select
                      value={button.type}
                      onChange={(value) => updateButton(button.id, 'type', value)}
                      style={{ width: '100%' }}
                    >
                      {buttonTypes.map(type => (
                        <Option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                    <Input
                      placeholder="Button text"
                      value={button.text}
                      onChange={(e) => updateButton(button.id, 'text', e.target.value)}
                      maxLength={button.type === 'flow' ? 25 : 20}
                    />
                  </Col>
                  <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                    {button.type === 'url' && (
                      <div>
                        <Input
                          placeholder="https://example.com"
                          value={button.url}
                          status={buttonUrlErrors[button.id] ? 'error' : ''}
                          onChange={(e) => updateButton(button.id, 'url', e.target.value)}
                          onBlur={() => setButtonUrlErrors(prev => ({ ...prev, [button.id]: validateUrl(button.url) }))}
                        />
                        {buttonUrlErrors[button.id] && (
                          <div style={{ color: '#ff4d4f', fontSize: 11, marginTop: 2 }}>{buttonUrlErrors[button.id]}</div>
                        )}
                      </div>
                    )}
                    {button.type === 'phone' && (
                      <PhoneInput
                        country={'in'}
                        value={button.phone}
                        onChange={(value) => updateButton(button.id, 'phone', '+' + value)}
                        enableSearch
                        disableSearchIcon
                        countryCodeEditable={false}
                        inputStyle={{ width: '100%', height: '32px', fontSize: '14px' }}
                        dropdownClass="atb-phone-dropdown"
                      />
                    )}
                    {button.type === 'flow' && (
                      <Tag color={getFlowButtonComponentForSubmit() ? 'green' : 'blue'}>
                        {getFlowButtonComponentForSubmit() ? 'Flow ready' : 'Configure below'}
                      </Tag>
                    )}
                  </Col>
                  <Col xs={24} sm={2} md={2} lg={2} xl={2}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeButton(button.id)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                </Row>
              </Card>
            ))}

            {templateData.components.buttons.length < 3 && (
              <div>
                <Text strong style={{ marginBottom: 12, display: 'block' }}>Add Button:</Text>
                <Space wrap>
                  {buttonTypes.map(type => (
                    <Button
                      key={type.value}
                      size="small"
                      onClick={() => addButton(type.value)}
                      icon={type.icon}
                    >
                      {type.label}
                    </Button>
                  ))}
                </Space>
              </div>
            )}
          </Space>
        </Card>

        {renderFlowBuilderCard()}
      </div>
    </div>
  );

  // Render Preview Screen
  const renderPreviewScreen = () => (
    <div className="template-section-screen">
      <div className="review-template-layout review-template-layout--live">
        <div className="form-section">
          <h2 className="section-title">Review &amp; Submit</h2>
          <p className="section-description">Review all your template information and live message preview before submitting for approval.</p>

          {/* ── Template Information ── */}
          <div className="rv-card rv-card--tpl">
            <div className="rv-card-head rv-card-head--tpl">
              <span className="rv-card-icon"><InfoCircleOutlined /></span>
              <span className="rv-card-title">Template Information</span>
            </div>
            <div className="rv-info-grid">
              <div className="rv-info-cell">
                <span className="rv-info-label">Template Name</span>
                <span className="rv-info-value rv-info-value--bold">{templateData.templateName || <em className="rv-empty-text">Not specified</em>}</span>
              </div>
              <div className="rv-info-cell">
                <span className="rv-info-label">Category</span>
                <span className={`rv-cat-badge rv-cat-badge--${(templateData.category || 'default').toLowerCase()}`}>
                  {templateData.category || 'Not specified'}
                </span>
              </div>
              <div className="rv-info-cell rv-info-cell--full">
                <span className="rv-info-label">Variable Type</span>
                <span className="rv-info-value">
                  {templateData.variableType === 'number' ? 'Number-based ({{1}}, {{2}})' : 'Name-based ({{name}}, {{company}})'}
                </span>
              </div>
              <div className="rv-info-cell">
                <span className="rv-info-label">Total Variables</span>
                <span className="rv-var-count-badge">
                  {(() => {
                    const allVariables = [
                      ...(templateData.components.header.variables || []),
                      ...(templateData.components.body.variables || []),
                      ...(templateData.components.footer.variables || [])
                    ];
                    return [...new Set(allVariables)].length;
                  })()}
                </span>
              </div>
            </div>
          </div>

          {/* ── Header Configuration ── */}
          <div className="rv-card rv-card--header">
            <div className="rv-card-head rv-card-head--header">
              <span className="rv-card-icon"><UnorderedListOutlined /></span>
              <span className="rv-card-title">Header Configuration</span>
              {templateData.components.header.type !== 'none' && (
                <span className="rv-type-badge">{templateData.components.header.type}</span>
              )}
            </div>
            {templateData.components.header.type === 'none' ? (
              <div className="rv-empty-state">
                <span className="rv-empty-dash">—</span>
                <span>No header configured</span>
              </div>
            ) : (
              <div className="rv-field-block">
                {templateData.components.header.type === 'text' && (
                  <>
                    <div className="rv-content-box">{templateData.components.header.content || 'No content'}</div>
                    {templateData.components.header.variables?.length > 0 && (
                      <div className="rv-var-row">
                        <span className="rv-var-label">Variables:</span>
                        {templateData.components.header.variables.map(v => (
                          <span key={v} className="rv-var-tag">{`{{${v}}}`}</span>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {templateData.components.header.type !== 'text' && templateData.components.header.type !== 'none' && (
                  <div className="rv-media-row">
                    {templateData.components.header.mediaUrl
                      ? <><CheckCircleOutlined className="rv-media-ok" /> File uploaded</>
                      : <span className="rv-media-none">No file uploaded</span>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Body Configuration ── */}
          <div className="rv-card rv-card--body">
            <div className="rv-card-head rv-card-head--body">
              <span className="rv-card-icon"><FormOutlined /></span>
              <span className="rv-card-title">Body Configuration</span>
            </div>
            <div className="rv-field-block">
              <div className="rv-content-box rv-content-box--body">
                {templateData.components.body.content || 'No content'}
              </div>
              {templateData.components.body.variables?.length > 0 && (
                <div className="rv-var-row">
                  <span className="rv-var-label">Variables:</span>
                  {templateData.components.body.variables.map(v => (
                    <span key={v} className="rv-var-tag">{`{{${v}}}`}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Footer Configuration ── */}
          <div className="rv-card rv-card--footer">
            <div className="rv-card-head rv-card-head--footer">
              <span className="rv-card-icon"><EditOutlined /></span>
              <span className="rv-card-title">Footer Configuration</span>
              {templateData.components.footer.type !== 'none' && (
                <span className="rv-type-badge">{templateData.components.footer.type}</span>
              )}
            </div>
            {templateData.components.footer.type === 'none' ? (
              <div className="rv-empty-state">
                <span className="rv-empty-dash">—</span>
                <span>No footer configured</span>
              </div>
            ) : (
              <div className="rv-field-block">
                {templateData.components.footer.type === 'text' && (
                  <>
                    <div className="rv-content-box">{templateData.components.footer.content || 'No content'}</div>
                    {templateData.components.footer.variables?.length > 0 && (
                      <div className="rv-var-row">
                        <span className="rv-var-label">Variables:</span>
                        {templateData.components.footer.variables.map(v => (
                          <span key={v} className="rv-var-tag">{`{{${v}}}`}</span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Action Footer ── */}
          <div className="rv-action-footer">
            <Button
              onClick={handleScreenPrev}
              icon={<ArrowLeftOutlined />}
              className="rv-btn-prev"
              size="large"
            >
              Previous
            </Button>
            <div className="rv-action-meta">
              <CheckCircleOutlined className="rv-action-check" />
              <span>All sections reviewed</span>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<SendOutlined />}
              onClick={submitTemplate}
              disabled={hasFlowButton && !getFlowButtonComponentForSubmit()}
              className="rv-btn-submit"
            >
              Submit for Approval
            </Button>
          </div>
        </div>

        <div className="review-template-preview">
          {renderLivePreviewCard()}
        </div>
      </div>
    </div>
  );

  // ── Screen-based step definitions (non-auth flow) ──
  const screenSteps = [
    { key: 'category', title: 'Category', subtitle: 'Template type' },
    { key: 'variableType', title: 'Variable Type', subtitle: 'Select format' },
    { key: 'header', title: 'Header', subtitle: 'Configure header' },
    { key: 'body', title: 'Body', subtitle: 'Message content' },
    { key: 'footer', title: 'Footer & Buttons', subtitle: 'Optional extras' },
    { key: 'preview', title: 'Review & Submit', subtitle: 'Live preview' },
  ];

  // Auth-flow step definitions
  const authSteps = [
    { key: 'templateInfo', title: 'Template Info', subtitle: 'Name & language' },
    { key: 'buildTemplate', title: 'Build Template', subtitle: 'Configure auth' },
    { key: 'reviewSubmit', title: 'Review & Submit', subtitle: 'Live preview' },
  ];

  const activeSteps = useStepFlow ? authSteps : screenSteps;
  const currentStepIndex = useStepFlow
    ? currentStep
    : screenSteps.findIndex(s => s.key === currentScreen);

  const stepIndicatorSteps = activeSteps.map((step, index) => ({
    number: index + 1,
    title: step.title
  }));

  const handleStepIndicatorClick = (stepNumber) => {
    const index = stepNumber - 1;
    if (index < currentStepIndex) {
      if (useStepFlow) {
        setCurrentStep(index);
      } else {
        setCurrentScreen(activeSteps[index].key);
      }
      scrollToTop();
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Centralized navigation for non-auth (screen) flow
  const handleScreenNext = () => {
    switch (currentScreen) {
      case 'category': {
        if (!templateData.category) {
          setFieldErrors(prev => ({ ...prev, category: 'Category is required' }));
          setTouchedFields(prev => ({ ...prev, category: true }));
          return;
        }
        setFieldErrors(prev => ({ ...prev, category: '' }));
        if (templateData.category === 'Authentication') {
          setUseStepFlow(true);
          setCurrentStep(0);
        } else {
          setCurrentScreen('variableType');
        }
        scrollToTop();
        break;
      }
      case 'variableType': {
        if (!selectedVariableType) return;
        setCurrentScreen('header');
        scrollToTop();
        break;
      }
      case 'header': {
        let hasErrors = false;
        const newErrors = {};
        const newTouched = {};
        if (!templateData.templateName?.trim()) {
          newErrors.templateName = 'Template name is required';
          newTouched.templateName = true;
          hasErrors = true;
        }
        if (templateData.components.header.type === 'text') {
          if (!templateData.components.header.content?.trim()) {
            newErrors.headerContent = 'Please enter header text before proceeding';
            newTouched.headerContent = true;
            hasErrors = true;
          }
          const headerVariables = templateData.components.header.variables || [];
          const validHeaderVars = getValidVariables(headerVariables, templateData.variableType);
          const missing = validHeaderVars.filter(v => !templateData.components.header.variableSamples?.[v]?.trim());
          if (missing.length > 0) {
            newErrors.headerVariables = `Please provide sample values for all header variables: ${missing.map(v => `{{${v}}}`).join(', ')}`;
            newTouched.headerVariables = true;
            hasErrors = true;
          }
        }
        if (templateData.components.header.type !== 'text' && templateData.components.header.type !== 'none') {
          if (!templateData.components.header.mediaUrl) {
            newErrors.headerMedia = `Please upload a ${templateData.components.header.type} file before proceeding`;
            newTouched.headerMedia = true;
            hasErrors = true;
          }
        }
        if (hasErrors) {
          setFieldErrors(prev => ({ ...prev, ...newErrors }));
          setTouchedFields(prev => ({ ...prev, ...newTouched }));
          return;
        }
        setFieldErrors(prev => {
          const updated = { ...prev };
          delete updated.templateName;
          delete updated.headerContent;
          delete updated.headerVariables;
          delete updated.headerMedia;
          return updated;
        });
        setCurrentScreen('body');
        scrollToTop();
        break;
      }
      case 'body': {
        const bodyError = validateField('body', templateData.components.body.content);
        const variableSamplesError = validateVariableSamples();
        if (!bodyError && !variableSamplesError) {
          setCurrentScreen('footer');
          scrollToTop();
        }
        break;
      }
      case 'footer': {
        setCurrentScreen('preview');
        scrollToTop();
        break;
      }
      case 'preview': {
        submitTemplate();
        break;
      }
      default:
        break;
    }
  };

  const handleScreenPrev = () => {
    switch (currentScreen) {
      case 'category':
        onBack();
        break;
      case 'variableType':
        setCurrentScreen('category');
        scrollToTop();
        break;
      case 'header':
        setCurrentScreen('variableType');
        scrollToTop();
        break;
      case 'body':
        setCurrentScreen('header');
        scrollToTop();
        break;
      case 'footer':
        setCurrentScreen('body');
        scrollToTop();
        break;
      case 'preview':
        setCurrentScreen('footer');
        scrollToTop();
        break;
      default:
        onBack();
    }
  };

  // Determine if Next button should be disabled (non-auth flow)
  const isScreenNextDisabled = () => {
    switch (currentScreen) {
      case 'category':
        return !templateData.category;
      case 'variableType':
        return !selectedVariableType;
      default:
        return false;
    }
  };

  // Render current screen based on currentScreen state
  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 'category':
        return renderCategoryScreen();
      case 'variableType':
        return renderVariableTypeSelection();
      case 'header':
        return renderHeaderScreen();
      case 'body':
        return renderBodyScreen();
      case 'footer':
        return renderFooterScreen();
      case 'preview':
        return renderPreviewScreen();
      default:
        return renderCategoryScreen();
    }
  };

  // Render current step for templates
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderTemplateInfo();
      case 1:
        return renderTemplateBuilder();
      case 2:
        return renderReviewSubmitStep();
      default:
        return renderTemplateInfo();
    }
  };



  // Step navigation for authentication templates
  const handleStepNavigation = (direction) => {
    if (direction === 'next') {
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        // Submit template on final step
        submitTemplate();
      }
    } else {
      if (currentStep > 1) {
        setCurrentStep(currentStep - 1);
      } else if (currentStep === 1) {
        // Go back to category selection from authentication builder
        setUseStepFlow(false);
        setCurrentScreen('category');
        setCurrentStep(0);
      } else {
        onBack(); // Go back to templates page
      }
    }
  };

  // Validate current step for authentication templates
  const validateCurrentStepAuth = () => {
    switch (currentStep) {
      case 0:
        return templateData.templateName && templateData.language;
      case 1:
        return templateData.authentication.codeDelivery && 
               (templateData.authentication.codeDelivery === 'copy-code' || 
                templateData.authentication.apps.length > 0);
      case 2:
        return true; // Review step is always valid
      default:
        return false;
    }
  };

  const renderAiFlowModal = () => (
    <Modal
      title={
        <Space>
          <ThunderboltOutlined style={{ color: '#25D366' }} />
          <span>Generate Flow with AI</span>
        </Space>
      }
      open={aiFlowModalOpen}
      onCancel={() => { setAiFlowModalOpen(false); setAiFlowError(''); }}
      className="ai-flow-modal"
      width={600}
      footer={[
        <Button key="cancel" onClick={() => { setAiFlowModalOpen(false); setAiFlowError(''); }}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={aiFlowLoading}
          disabled={!aiFlowPrompt.trim()}
          onClick={generateAiFlow}
        >
          Generate Flow
        </Button>,
      ]}
      destroyOnClose
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <p className="ai-flow-modal-desc">
          Describe what you want to collect. AI will configure the fields, screen text, and button labels.
          You can edit everything after it loads.
        </p>

        <TextArea
          value={aiFlowPrompt}
          onChange={(e) => { setAiFlowPrompt(e.target.value); setAiFlowError(''); }}
          onPressEnter={(e) => {
            if ((e.ctrlKey || e.metaKey) && !aiFlowLoading) generateAiFlow();
          }}
          placeholder="Example: Event registration for our May product launch. Collect name, email, company, attendee count, meal preference, and consent."
          autoSize={{ minRows: 5, maxRows: 9 }}
          maxLength={1200}
          disabled={aiFlowLoading}
          className="ai-flow-prompt-textarea"
        />

        <div className="ai-flow-suggestions">
          {[
            'Customer satisfaction survey with rating, feedback, and contact preference.',
            'Sign-up for a loyalty program — name, mobile, date of birth, and consent.',
            'Webinar registration — name, email, company, designation, and sessions interested in.',
          ].map((s) => (
            <button
              key={s}
              type="button"
              className="ai-flow-suggestion-chip"
              onClick={() => { setAiFlowPrompt(s); setAiFlowError(''); }}
              disabled={aiFlowLoading}
            >
              {s}
            </button>
          ))}
        </div>

        {aiFlowLoading && (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        )}

        {!aiFlowLoading && aiFlowError && (
          <Alert
            message="Could not generate Flow"
            description={aiFlowError}
            type="error"
            showIcon
          />
        )}

        <Text type="secondary" style={{ fontSize: 12 }}>
          Ctrl + Enter to generate &nbsp;·&nbsp; All fields are editable after loading.
        </Text>
      </Space>
    </Modal>
  );

  const renderAiCopyGeneratorModal = () => {
    const copyTargetLabel = aiCopyTarget === 'header'
      ? 'header'
      : aiCopyTarget === 'body'
        ? 'body'
        : 'header and body';

    return (
      <Modal
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#25D366' }} />
            <span>AI writing assistant</span>
          </Space>
        }
        open={aiCopyModalOpen}
        onCancel={() => setAiCopyModalOpen(false)}
        className="ai-copy-modal"
        width={760}
        footer={[
          <Button key="cancel" onClick={() => setAiCopyModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="generate"
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={aiCopyLoading}
            disabled={!aiCopyPrompt.trim()}
            onClick={handleGenerateAiCopy}
          >
            Generate
          </Button>
        ]}
        destroyOnClose={false}
      >
        <Space direction="vertical" size={18} style={{ width: '100%' }}>
          <div className="ai-draft-card ai-copy-simple-card">
            <div className="ai-draft-orb" />
            <div className="ai-draft-kicker">
              <ThunderboltOutlined /> One prompt only
            </div>
            <h3 className="ai-draft-title">Rewrite the {copyTargetLabel}</h3>
            <p className="ai-draft-subtitle">
              Tell AI what to improve. Existing placeholders are preserved automatically, and the generated copy is still validated before use.
            </p>

            <div className="ai-draft-composer ai-copy-composer">
              <TextArea
                value={aiCopyPrompt}
                onChange={(e) => {
                  setAiCopyPrompt(e.target.value);
                  setAiCopyResult(null);
                }}
                onPressEnter={(e) => {
                  if ((e.ctrlKey || e.metaKey) && !aiCopyLoading) {
                    handleGenerateAiCopy();
                  }
                }}
                placeholder="Example: Make this message warmer, concise, and clear for WhatsApp approval."
                autoSize={{ minRows: 4, maxRows: 7 }}
                maxLength={1000}
                disabled={aiCopyLoading}
                className="ai-draft-textarea"
              />
              <div className="ai-draft-actions">
                <Text type="secondary" className="ai-draft-hint">
                  Ctrl + Enter to generate · Review before applying to the builder.
                </Text>
              </div>
            </div>
          </div>

          {aiCopyLoading && (
            <div className="ai-draft-loader">
              <div className="ai-draft-loader-header">
                <Skeleton.Avatar active size="small" />
                <Skeleton.Input active size="small" style={{ width: '45%' }} />
              </div>
              <Skeleton active paragraph={{ rows: 4 }} title={false} />
            </div>
          )}

          {aiCopyResult?.metaReviewTips?.length > 0 && (
            <Alert
              message="Meta review tips"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {aiCopyResult.metaReviewTips.map((tip, index) => <li key={index}>{tip}</li>)}
                </ul>
              }
              type="warning"
              showIcon
            />
          )}

          {!aiCopyLoading && aiCopyResult?.variants?.length > 0 && (
            <div>
              <Divider orientation="left">Generated options</Divider>
              <Row gutter={[12, 12]}>
                {aiCopyResult.variants.map((variant, index) => {
                  const headerText = variant.headerText || getComponentTextFromVariant(variant, 'HEADER');
                  const bodyText = variant.bodyText || variant.templatecontent || getComponentTextFromVariant(variant, 'BODY');
                  const footerText = variant.footerText || getComponentTextFromVariant(variant, 'FOOTER');

                  return (
                    <Col xs={24} md={12} key={index}>
                      <Card
                        size="small"
                        title={`Option ${index + 1}`}
                        extra={
                          <Button type="primary" size="small" onClick={() => applyAiVariant(variant)}>
                            Use this
                          </Button>
                        }
                        style={{ height: '100%', borderRadius: 14 }}
                      >
                        {headerText && (
                          <div style={{ marginBottom: 10 }}>
                            <Text strong>Header</Text>
                            <div style={{ marginTop: 4 }}>{headerText}</div>
                          </div>
                        )}
                        {bodyText && (
                          <div style={{ marginBottom: 10 }}>
                            <Text strong>Body</Text>
                            <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{bodyText}</div>
                          </div>
                        )}
                        {footerText && (
                          <div style={{ marginBottom: 10 }}>
                            <Text strong>Footer</Text>
                            <div style={{ marginTop: 4 }}>{footerText}</div>
                          </div>
                        )}
                        {variant.characterCounts && (
                          <Space wrap size="small">
                            {Object.entries(variant.characterCounts).map(([key, value]) => (
                              <Tag key={key}>{key}: {value}</Tag>
                            ))}
                          </Space>
                        )}
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}
        </Space>
      </Modal>
    );
  };

  return (
    <div className="advanced-template-builder advanced-broadcast-builder template-builder-wizard">
      {/* Top Header */}
      <div className="broadcast-top-header">
        <button className="broadcast-back-btn" onClick={onBack}>
          <ArrowLeftOutlined />
        </button>
        <Title level={4} className="broadcast-top-header-title">
          Set up your template
        </Title>
      </div>

      {/* Two-Column Layout */}
      <div className="broadcast-layout">
        {/* Right Main Content */}
        <div className="broadcast-main">
          {/* Mobile Tabs (hidden on desktop) */}
          <div className="broadcast-mobile-tabs">
            {activeSteps.map((step, index) => (
              <button
                key={index}
                className={`broadcast-mobile-tab${index === currentStepIndex ? ' active' : ''}${index < currentStepIndex ? ' completed clickable' : ''}`}
                onClick={() => {
                  if (index < currentStepIndex) {
                    if (useStepFlow) {
                      setCurrentStep(index);
                    } else {
                      setCurrentScreen(step.key);
                    }
                    scrollToTop();
                  }
                }}
                type="button"
              >
                <span className="mob-step-circle">
                  <span className="mob-step-num">{index + 1}</span>
                  <CheckCircleOutlined className="mob-step-check" />
                </span>
                <span className="mob-step-label">{step.title}</span>
              </button>
            ))}
          </div>

          {/* Step Content */}
          <div className="broadcast-step-content" key={useStepFlow ? currentStep : currentScreen}>
            {!useStepFlow && currentScreen !== 'preview' ? (
              <Row gutter={[24, 16]}>
                <Col xs={24} lg={15}>
                  <div className="broadcast-horizontal-stepper">
                    <TemplateWizardStepIndicator
                      steps={stepIndicatorSteps}
                      currentStep={currentStepIndex + 1}
                      onStepClick={handleStepIndicatorClick}
                      isStepCompleted={(stepNumber) => currentStepIndex + 1 > stepNumber}
                      maxWidth="100%"
                    />
                  </div>
                  <div className="form-container">
                    <Form form={form} layout="vertical">
                      {renderCurrentScreen()}
                    </Form>
                  </div>
                  {/* Navigation — bottom right of center form */}
                  <div className="broadcast-step-nav" style={{ justifyContent: 'flex-end', padding: '4px 0 0 0' }}>
                    <Button
                      size="small"
                      onClick={handleScreenPrev}
                      disabled={currentScreen === 'category'}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      onClick={handleScreenNext}
                      disabled={isScreenNextDisabled()}
                    >
                      Next
                    </Button>
                  </div>
                </Col>
                <Col xs={24} lg={9}>
                  <div className="mobile-preview-container">
                    <Card title="Message Preview" size="small" className="preview-card">
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {renderWhatsAppPreview(getLivePreviewTemplateData())}
                      </div>
                    </Card>
                  </div>
                </Col>
              </Row>
            ) : (
              <>
                <div className="broadcast-horizontal-stepper">
                  <TemplateWizardStepIndicator
                    steps={stepIndicatorSteps}
                    currentStep={currentStepIndex + 1}
                    onStepClick={handleStepIndicatorClick}
                    isStepCompleted={(stepNumber) => currentStepIndex + 1 > stepNumber}
                    maxWidth="100%"
                  />
                </div>
                <div className="form-container">
                  <Form form={form} layout="vertical">
                    {useStepFlow ? renderCurrentStep() : renderCurrentScreen()}
                  </Form>
                </div>
              </>
            )}
          </div>

          {/* Navigation — for step flow (auth) only; review screen has its own inline footer */}
          {useStepFlow && (
          <div className="broadcast-step-nav">
            {useStepFlow ? (
              <>
                <Button
                  onClick={() => handleStepNavigation('prev')}
                  disabled={currentStep === 0}
                >
                  Previous
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleStepNavigation('next')}
                  disabled={!validateCurrentStepAuth()}
                >
                  {currentStep === 2 ? 'Submit for Approval' : 'Next'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleScreenPrev}
                  disabled={currentScreen === 'category'}
                >
                  Previous
                </Button>
                {currentScreen !== 'preview' ? (
                  <Button
                    type="primary"
                    onClick={handleScreenNext}
                    disabled={isScreenNextDisabled()}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={submitTemplate}
                    disabled={hasFlowButton && !getFlowButtonComponentForSubmit()}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Submit for Approval
                  </Button>
                )}
              </>
            )}
          </div>
          )}
        </div>
      </div>

      {renderAiCopyGeneratorModal()}
      {renderAiFlowModal()}

      {/* Advanced Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="validation-errors">
          <Alert
            message={`Please review the following ${validationErrors.length === 1 ? 'issue' : 'issues'} before proceeding`}
            description={
              <div className="validation-error-list">
                {validationErrors.map((error, index) => (
                  <div key={index} className="validation-error-item">
                    <span className="error-icon">⚠</span>
                    <span className="error-text">{error}</span>
                  </div>
                ))}
              </div>
            }
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
            closable
          />
        </div>
      )}
    </div>
  );
};

export default AdvancedTemplateBuilder;
